/**
 * ============================================================================
 * Leadbox DataLayer (LBT-1395)
 * ============================================================================
 *
 * Pushes standardized lbx_* events to window.dataLayer per the Leadbox
 * dataLayer specification. Reads server-side context from window.LBX_CONTEXT
 * which is injected in resources/views/partials/head.blade.php.
 *
 * Only loaded when "Leadbox DataLayer" is Enabled in Leadbox Settings.
 *
 * PHASE A:   lbx_page_view
 * PHASE B:   lbx_form_submit, lbx_phone_click, lbx_directions_click,
 *            lbx_ui_interaction (SRP filters + nav/tabs/CTAs)
 * PHASE C.1: lbx_tool_interaction (iBoost 360 + window sticker + compare + opt-in),
 *            lbx_trade_in_complete, lbx_credit_app_complete, lbx_chat_submit (stub)
 * PHASE C.2: lbx_tool_interaction deep hooks for payment_calculator (open +
 *            calculate), gallery (open/scroll/close), configurator / build &
 *            price (open + select_option), service_scheduler (open + select_option)
 */

(function () {
  'use strict';

  window.dataLayer = window.dataLayer || [];

  var ctx = window.LBX_CONTEXT || null;
  if (!ctx) {
    return;
  }

  // ---------------------------------------------------------------------------
  // Normalization helpers — enforce the two golden rules of the spec:
  //   1. Strings are lowercased.
  //   2. Missing values are null (never undefined, '', or the string '(not set)').
  // ---------------------------------------------------------------------------

  function lc(v) {
    if (v === null || v === undefined || v === '') return null;
    return String(v).trim().toLowerCase();
  }

  function num(v) {
    if (v === null || v === undefined || v === '') return null;
    var n = Number(v);
    return isFinite(n) ? n : null;
  }

  // Map one raw value onto a spec enum using the ordered keyword rules the
  // server exports in LBX_CONTEXT.filter_enum_rules. The rules deliberately
  // live in PHP (lbx_bodystyle_rules()) so the server-side vehicle fields and
  // the browser-side filter fields cannot disagree.
  //
  // Order is load-bearing and comes from the array itself: 'station wagon'
  // contains 'wagon', 'mini-van' contains 'van'.
  //
  // A value no rule matches returns null, matching the server: null is
  // spec-legal and visible, a raw feed string is neither. It is warned about
  // rather than swallowed, so a body style the feed invented stays findable —
  // the same contract lbx_report_unmapped_enum() honours on the PHP side.
  //
  // If the rules did not arrive at all, the lowercased value is returned
  // unchanged. That is the one case where a raw string is the better answer: a
  // missing export is our bug, and blanking the field would report it as "no
  // filter applied", which is a wrong measurement rather than a coarse one.
  function enumFromRules(field, raw) {
    var v = lc(raw);
    if (v === null) return null;

    var rules = ctx.filter_enum_rules && ctx.filter_enum_rules[field];
    if (!rules || !rules.length) {
      lbxWarn('no ' + field + ' enum rules in LBX_CONTEXT; reporting the raw value.');

      return v;
    }

    for (var i = 0; i < rules.length; i++) {
      var needles = rules[i].needles || [];
      for (var j = 0; j < needles.length; j++) {
        if (v.indexOf(needles[j]) !== -1) return rules[i].canonical;
      }
    }

    lbxWarn('unmapped ' + field + ' value from the inventory filter: "' + v + '".');

    return null;
  }

  // Turn a promotional destination into a stable element_name (LBT-1602, QA 9c).
  //
  // The last path segment is the campaign: '/employee-pricing-event/' becomes
  // 'employee_pricing_event', and an outbound
  // 'https://capitalautomotivegroup.ca/best-managed/' becomes 'best_managed'.
  // Keeping only the segment loses nothing — element_url carries the whole
  // destination, including whether the click left the site.
  //
  // A link to a bare domain has no segment to be named after, so the host
  // answers for it; a same-site root has neither and reports 'home'.
  function bannerNameFromUrl(raw) {
    var v = String(raw || '').trim();
    if (!v || v === '#') return null;

    var host = (v.match(/^[a-z][a-z0-9+.-]*:\/\/([^/?#]+)/i) || [])[1] || '';
    var path = v.replace(/^[a-z][a-z0-9+.-]*:\/\/[^/?#]+/i, '').split(/[?#]/)[0];
    var slug = path.replace(/^\/+|\/+$/g, '').split('/').pop() || host || 'home';

    return lc(slug.replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '')) || null;
  }

  // Ask the browser what it is, rather than inferring it from how wide the
  // window happens to be.
  //
  // This used to read window.innerWidth and call anything under 768px a phone,
  // so a desktop with a narrowed window reported 'mobile' (LBT-1602, M11). The
  // spec asks for the device, not the viewport, and the two stop agreeing the
  // moment someone resizes — which also made the figure drift for anyone with
  // devtools open.
  //
  // Order matters: tablets are checked before phones because an Android tablet
  // still says "android", and iPadOS 13+ reports itself as a Mac, so the touch
  // points are what give it away.
  function detectDeviceType() {
    var uaData = navigator.userAgentData;
    // Client Hints: a boolean the browser asserts about itself. It only knows
    // "mobile or not", so a false answer is left to the tests below rather than
    // being treated as proof of a desktop.
    if (uaData && uaData.mobile === true) return 'mobile';

    var ua = navigator.userAgent || '';

    if (/\b(ipad|tablet|playbook|silk|kindle)\b/i.test(ua)) return 'tablet';
    if (/android/i.test(ua) && !/mobile/i.test(ua)) return 'tablet';
    if (/macintosh/i.test(ua) && navigator.maxTouchPoints > 1) return 'tablet';

    if (/\b(iphone|ipod|android|blackberry|bb10|windows phone|iemobile|opera mini|mobile)\b/i.test(ua)) {
      return 'mobile';
    }

    return 'desktop';
  }

  // Empty vehicle object used on non-VDP pages so all keys are always present.
  var EMPTY_VEHICLE = {
    vehicle_vin: null,
    vehicle_stock_number: null,
    vehicle_condition: null,
    vehicle_make: null,
    vehicle_model: null,
    vehicle_year: null,
    vehicle_trim: null
  };

  // Shallow merge — later args win.
  function merge() {
    var out = {};
    for (var i = 0; i < arguments.length; i++) {
      var src = arguments[i];
      if (!src) continue;
      for (var k in src) {
        if (Object.prototype.hasOwnProperty.call(src, k)) out[k] = src[k];
      }
    }
    return out;
  }

  // Current vehicle context (VDP only). Returns EMPTY_VEHICLE on non-VDP.
  // Only the 7 fields required by conversion events (form/phone/directions/chat/etc).
  function vehicleShort() {
    if (!ctx.vehicle) return merge({}, EMPTY_VEHICLE);
    return {
      vehicle_vin: ctx.vehicle.vehicle_vin,
      vehicle_stock_number: ctx.vehicle.vehicle_stock_number,
      vehicle_condition: ctx.vehicle.vehicle_condition,
      vehicle_make: ctx.vehicle.vehicle_make,
      vehicle_model: ctx.vehicle.vehicle_model,
      vehicle_year: ctx.vehicle.vehicle_year,
      vehicle_trim: ctx.vehicle.vehicle_trim
    };
  }

  // ---------------------------------------------------------------------------
  // thank_you department inheritance (LBT-1602, M9)
  //
  // The spec has thank_you inherit the department of the previous page. That is
  // knowable only from the referrer, which is the one input a full-page cache
  // destroys — so this cannot be decided on the server. Measured on
  // renfrewchrysler.com (2026-08-15): /thank-you/ served from cache
  // (x-kinsta-cache: HIT, s-maxage=3600) reported 'general' no matter where the
  // visitor came from, while the same URL with the cache bypassed reported
  // 'sales' and 'service' correctly.
  //
  // The rules are compiled server-side from the same pattern map the rest of
  // the classifier uses (lbx_referrer_department_rules), so this is the same
  // single source of truth with the matching moved — not a second list. A
  // second list is what M9 was.
  // ---------------------------------------------------------------------------
  var referrerRules = null;

  function compiledReferrerRules() {
    if (referrerRules) return referrerRules;
    referrerRules = [];
    var raw = (ctx && ctx.department_referrer_rules) || [];
    for (var i = 0; i < raw.length; i++) {
      var r = raw[i];
      if (!r || !r.p || !r.d) continue;
      if (r.m === 'sub') {
        referrerRules.push({ sub: String(r.p).toLowerCase(), d: r.d });
        continue;
      }
      try {
        referrerRules.push({ re: new RegExp(r.p), d: r.d });
      } catch (e) {
        // Skip one malformed rule rather than take the whole module down.
      }
    }
    return referrerRules;
  }

  function departmentFromReferrer() {
    if (!document.referrer) return null;

    var a = document.createElement('a');
    a.href = document.referrer;

    // Same-host only, so an inbound external link cannot set the department.
    if (a.hostname && String(a.hostname).toLowerCase() !== String(location.hostname).toLowerCase()) {
      return null;
    }

    var path = String(a.pathname || '').toLowerCase();
    if (!path) return null;

    // VDPs are the one page type with no slug to match on — their permalink is
    // /view/<slug>-<id>/. Kept as a special case rather than a pattern because
    // 'view' is far too generic to sit in the shared map.
    if (path.indexOf('/view/') !== -1) return 'sales';

    var rules = compiledReferrerRules();
    for (var i = 0; i < rules.length; i++) {
      var r = rules[i];
      if (r.sub ? path.indexOf(r.sub) !== -1 : r.re.test(path)) return r.d;
    }

    return null;
  }

  // Resolve once, before anything reads ctx.department, so the page_view and
  // every conversion event fired from this page agree.
  if (ctx.page_type === 'thank_you') {
    var inheritedDepartment = departmentFromReferrer();
    if (inheritedDepartment) {
      ctx.department = inheritedDepartment;
    }
  }

  // Base parameters present on every page_view.
  function baseContext() {
    return {
      department: lc(ctx.department),
      language: lc(ctx.language),
      device_type: detectDeviceType(),
      leadbox_id: ctx.leadbox_id !== null && ctx.leadbox_id !== undefined
        ? String(ctx.leadbox_id)
        : null
    };
  }

  // ---------------------------------------------------------------------------
  // SRP filter helpers — full filter state per spec, all keys always present.
  // ---------------------------------------------------------------------------

  // Maps the SRP Vue component's `selected` shape into spec's filter_* keys.
  // The component uses: { condition, year, make, model, trim, bodystyle,
  // drivetrain, exteriorcolor, location, status } as arrays.
  // The last filter snapshot this page produced. A vendor event that changes the
  // listing has to carry the full filter state per Appendix A, and the vendor
  // does not know ours — so the answer is the state we last reported. Null until
  // the inventory component has spoken, which is also the honest value: before
  // that, there is no filter state to describe.
  var lastSrpFilterState = null;

  // The result count that went out with that snapshot. Remembered for the same
  // reason as the state: a control that reconfigures the listing without
  // changing the filter set — the sort — must still carry "the full state
  // after the change" (spec v2.3 §11), and the sort component does not hold
  // it. The count it reports is the one the listing showed when it was sorted,
  // which is the count the shopper was looking at.
  var lastSrpTotalResults = null;

  function rememberSrpFilterState(state, totalResults) {
    lastSrpFilterState = state;
    if (totalResults !== undefined) lastSrpTotalResults = num(totalResults);

    return state;
  }

  function normalizeSrpFilterState(selected) {
    function first(arr) {
      if (!arr || !arr.length) return null;
      // If multiple values are selected, join them comma-separated (lowercased).
      return arr.map(function (v) { return String(v).toLowerCase(); }).join(',');
    }

    // Same as first(), but every value is mapped onto the spec enum before it
    // is joined (LBT-1602, QA 5g).
    //
    // The sidebar offers the feed's own prose — "CREW CAB PICKUP", "4DR CAR" —
    // and reporting it verbatim put a raw string in a field whose VDP
    // counterpart already said 'truck' for the same vehicle, so the two sides
    // could not be joined. The rules come from the server
    // (lbx_bodystyle_rules() in helpers.php, exported through LBX_CONTEXT) so
    // there is one copy of an ordered keyword map, not two.
    //
    // Duplicates collapse: 'crew cab pickup' and 'regular cab' are both trucks,
    // and 'truck,truck' would describe the sidebar rather than the filter.
    function enumList(arr, field) {
      if (!arr || !arr.length) return null;

      var out = [];
      for (var i = 0; i < arr.length; i++) {
        var mapped = enumFromRules(field, arr[i]);
        if (mapped !== null && out.indexOf(mapped) === -1) out.push(mapped);
      }

      return out.length ? out.join(',') : null;
    }
    // LBT-1453 introduced ?filter_vin=ABC,DEF as a real SRP filter (handled by
    // the leadbox-plugin and the spa-inventory Vue stack). The Vue sidebar
    // doesn't carry the VIN in `selected`, so we read it live from the URL
    // and pass it through as a Leadbox-local extension to the spec.
    function readFilterVinFromUrl() {
      try {
        var raw = new URLSearchParams(window.location.search).get('filter_vin');
        if (!raw) return null;
        var clean = String(raw).split(',').map(function (v) {
          return v.trim().toLowerCase();
        }).filter(Boolean).join(',');
        return clean || null;
      } catch (e) { return null; }
    }
    // filter_keyword — the words typed into the SRP sidebar search (LBT-1603,
    // Q3). Added to the spec's filter block on 2026-08-17.
    //
    // Until this existed a visit that cut the inventory from 1,567 vehicles to
    // 38 reported the resulting count correctly and every filter as null, with
    // the word searched for appearing in no field at all — a 97% narrowing with
    // nothing to explain it.
    //
    // WHY IT CANNOT BE A CLICK EVENT: submitting the search is a full
    // navigation (the component sets window.location to /filter/<term>), so a
    // click event dies with the page that fired it. The value has to travel on
    // the destination page's filter block, which is what this is.
    //
    // The component hands us its raw input; the URL form of the same term has
    // spaces replaced by underscores, and reporting 'grand_cherokee' would be
    // reporting our own routing rather than what the shopper typed. So the
    // underscores are turned back into spaces when the value comes from the URL
    // segment.
    function readFilterKeyword(raw) {
      var value = raw;

      // Fall back to the URL when the caller has no value — a direct hit on
      // /new-vehicles/filter/jeep/ from a bookmark or an ad still has to report
      // the term, and on that load the component may not have booted yet.
      if (value === null || value === undefined || value === '') {
        try {
          var m = String(window.location.pathname).match(/\/filter\/([^/?#]+)/);
          value = m ? decodeURIComponent(m[1]).replace(/_/g, ' ') : null;
        } catch (e) { value = null; }
      }

      if (value === null || value === undefined) return null;

      value = String(value).trim().toLowerCase();
      if (value === '') return null;

      // Free-text boxes collect personal data eventually, and GA4 rejects PII
      // at the property level — so an address or a phone number is dropped
      // rather than reported. Better to lose one search than to poison the
      // property. Digits themselves are legitimate ('2024', 'f-150'), so only
      // shapes that read as contact details are refused.
      if (/[^\s@]+@[^\s@]+\.[a-z]{2,}/.test(value)) return null;
      if (/(?:\d[\s().-]*){10,}/.test(value)) return null;

      // Spec cap. Truncating beats dropping: the first 100 characters of a long
      // search still say what was looked for.
      if (value.length > 100) value = value.slice(0, 100);

      return value;
    }

    selected = selected || {};
    return {
      filter_keyword: readFilterKeyword(selected.filterValue),
      filter_make: first(selected.make),
      filter_model: first(selected.model),
      filter_trim: first(selected.trim),
      // Always a string, one year or several (spec v2.2, "The shape of a filter
      // value"). LBT-1602 QA 11e made a lone year a number; v2.2 overruled it,
      // because a parameter that alternates between number and string lands in
      // two BigQuery columns (int_value / string_value) and half the data is
      // lost to any query that forgets to COALESCE them.
      filter_year: first(selected.year),
      filter_condition: first(selected.condition),
      filter_bodystyle: enumList(selected.bodystyle, 'bodystyle'),
      filter_drivetrain: first(selected.drivetrain),
      filter_colour: first(selected.exteriorcolor || selected.color || selected.colour),
      filter_location: first(selected.location),
      filter_status: first(selected.status),
      // Spec v2.2: same enum and same feed mapping as vehicle_fuel_type, so the
      // rules come from the server (lbx_fuel_type_rules()) like bodystyle's.
      filter_fuel_type: enumList(selected.fueltype, 'fuel_type'),
      // Leadbox-local extension (not part of the universal spec — see DATALAYER.md).
      filter_vin: readFilterVinFromUrl()
    };
  }

  // ---------------------------------------------------------------------------
  // Heuristics: infer department / form_type from names when not explicit.
  // ---------------------------------------------------------------------------

  function inferDepartmentFromText(text) {
    if (!text) return null;
    var t = String(text).toLowerCase();
    if (/\bservice\b/.test(t)) return 'service';
    if (/\bparts?\b|\bpi[èe]ces?\b/.test(t)) return 'parts';
    if (/\bfinanc|credit|cr[ée]dit/.test(t)) return 'finance';
    if (/\btrade|[ée]valuation/.test(t)) return 'sales';
    return null;
  }

  function inferFormType(formName, buttonText) {
    var t = String(formName || '').toLowerCase() + ' ' + String(buttonText || '').toLowerCase();
    // "SERVICE SCHEDULER" is a booking form and came out consumer_contact
    // (Leonardo, 2026-10-29). Spec §4: service_appointment = "Book a service
    // appointment". Scheduling words only next to "service", so a plain
    // "Service" contact form keeps its consumer_contact type.
    if (/service\s*(appointment|rendez-vous|schedul|booking)|book(ing)?\s*(a\s*)?service|schedul\w*\s*(a\s*)?service|rendez-vous\s*(de\s*)?service/.test(t)) return 'service_appointment';
    if (/trade|[ée]valuation/.test(t)) return 'trade';
    if (/credit|finance|financ/.test(t)) return 'finance_credit';
    // Word-bounded: 'part' is inside 'department', so "Contact Sales
    // Department" came out as a parts inquiry (QA 2026-10-22, #13).
    if (/\bparts?\b|\bpi[èe]ces?\b/.test(t)) return 'parts';
    if (/quote|devis/.test(t)) return 'quote';
    if (/test\s*drive|essai/.test(t)) return 'test_drive';
    // Forms that take a person's details without being a lead: NEWSLETTER sits
    // on 30 Renfrew pages and 9 on each Capital site and reported as
    // consumer_contact, the type for a contact or inquiry lead (QA 2026-10-22,
    // #2). The spec's 'other' is "any form not matching the listed types", so
    // these are named explicitly; the default below stays consumer_contact for
    // the lead forms nothing names. Checked after the lead types so a title
    // like "Trade-in review" still reads as the lead it is.
    if (/newsletter|infolettre|subscri|abonn|career|carri[èe]re|employ|emploi|\bjobs?\b|r[ée]sum[ée]|\bcv\b|survey|sondage|feedback|contest|concours|giveaway|unsubscribe|opt-?out/.test(t)) return 'other';
    return 'consumer_contact';
  }

  // ---------------------------------------------------------------------------
  // Public API: window.LBX.track.*
  // ---------------------------------------------------------------------------

  var srpReadyFired = false;
  var vdpReadyFired = false;

  // Whether anything ever CALLED srpReady(), regardless of whether it fired.
  // Distinguishes "the component reported and the state never settled" from
  // "no component reported at all" — see srpWatchForMissingInventoryView().
  var srpReadyCalled = false;

  // Whether an lbx_page_view has already gone out on this load.
  //
  // The spec's "one push per action" rule has to hold for page_view even when
  // the page is classified wrongly. Before LBT-1602 it did not: pageView()
  // suppressed the generic push only when it already knew page_type was 'srp',
  // so a misclassified SRP emitted the generic view AND a deferred SRP one (C1).
  //
  // That whole class of bug is gone with spec v2.1: the page view no longer
  // waits for anything, so there is no second push to collide with it. The flag
  // stays because it still guarantees the invariant by construction rather than
  // by classifier accuracy.
  var pageViewFired = false;

  function pushPageView(payload) {
    // One page view per document load, enforced here rather than trusted to
    // callers. C1 was a duplicate page view that took a conformance audit to
    // find, so the invariant is worth a guard even now that the deferral which
    // caused it is gone.
    if (pageViewFired) {
      lbxWarn('a second lbx_page_view was suppressed for this page load.');

      return;
    }
    pageViewFired = true;
    window.dataLayer.push(payload);
  }

  function lbxWarn(msg) {
    if (window.console && console.warn) console.warn('[LBX] ' + msg);
  }

  // ---------------------------------------------------------------------------
  // THE READY CONTRACT (LBT-1603, Q4 — spec v2.1)
  //
  // Three events replace one, and the split is what fixes both halves of the
  // problem: a page view must never be lost to a slow load, and a vehicle or
  // inventory payload must never be reported half-formed. Trying to make one
  // event do both is what produced the old timeout, the nameless reconciliation
  // push, and the audit's C1.
  //
  //   lbx_page_view   immediately, every page, base context only
  //   lbx_vdp_ready   once per VDP, base context + the vehicle block
  //   lbx_srp_ready   once per SRP, base context + filter block + result count
  //
  // WHAT WAS REMOVED, so nobody puts it back: the 5-second budget the page view
  // used to wait out, the pagehide flush that covered it, and the push with no
  // event key that carried late filter values into GTM's model. The spec owner's
  // reason for the last one is the one to remember — for a container listening
  // on named events, a nameless push is indistinguishable from nothing arriving.
  //
  // Delaying the page view was also unsafe in a way we could not see from here:
  // their GA4 configuration sets send_page_view false, so lbx_page_view is the
  // ONLY page view those properties ever receive. A visitor who left an SRP
  // before the inventory settled did not produce a late page view — they
  // produced no visit at all.
  //
  // If a payload never resolves, no ready event fires. That is the correct
  // failure: the page view is still counted, and we have no VDP or SRP view for
  // that visit rather than a fabricated one.
  // ---------------------------------------------------------------------------

  // The filter dimensions that can appear as /<dimension>/<value>/ pairs in an
  // SRP URL, mapped to the key each one takes in the component's state.
  var URL_FILTER_DIMENSIONS = {
    make: 'make',
    model: 'model',
    trim: 'trim',
    year: 'year',
    condition: 'condition',
    bodystyle: 'bodystyle',
    body: 'bodystyle',
    drivetrain: 'drivetrain',
    colour: 'colour',
    color: 'colour',
    location: 'location',
    status: 'status'
  };

  // Which filters the URL is asking for. Read from the path pairs and from any
  // ?filter_* parameters, since both forms are in use.
  //
  // Only the DIMENSION NAMES are collected, not the values: the component
  // resolves a slug to whatever the platform actually has, so comparing values
  // would fail on every legitimate normalisation. What has to converge is which
  // dimensions are populated.
  function urlFilterDimensions() {
    var wanted = {};

    try {
      var parts = String(window.location.pathname).split('/');
      for (var i = 0; i < parts.length - 1; i++) {
        var dim = URL_FILTER_DIMENSIONS[lc(parts[i])];
        if (dim && parts[i + 1]) wanted[dim] = true;
      }

      var params = new URLSearchParams(window.location.search);
      params.forEach(function (value, key) {
        var k = lc(key);
        if (k.indexOf('filter_') !== 0 || !value) return;
        var mapped = URL_FILTER_DIMENSIONS[k.slice('filter_'.length)];
        if (mapped) wanted[mapped] = true;
      });
    } catch (e) { /* a URL we cannot parse constrains nothing */ }

    return wanted;
  }

  // Has the applied filter state caught up with what the URL asked for?
  //
  // The spec's definition is "the applied filter set equals the filter set in
  // the URL, and the result count corresponds to that applied set". Two of its
  // three edge rules live here:
  //
  //   - A filter that resolves to ZERO results is still reflected. Applied with
  //     a count of zero is settled, so this never waits for a non-empty result.
  //   - A filter the platform REJECTS counts as resolved. Otherwise the sets
  //     never converge and the event never fires — a filter for a make this
  //     dealer does not stock would hang the SRP view forever.
  //
  // Which is why a dimension the URL asked for and the component reports as
  // empty is only unsettled while the component still has options to resolve.
  function srpFilterStateSettled(state) {
    var wanted = urlFilterDimensions();

    for (var dim in wanted) {
      if (!ownProp(wanted, dim)) continue;
      if (state['filter_' + dim] === null || state['filter_' + dim] === undefined) {
        return false;
      }
    }

    return true;
  }

  // The rejection safety net, and it is what stops a rejected filter from
  // hanging the event forever.
  //
  // A URL can ask for a make this dealer does not stock. The platform drops it,
  // so that dimension NEVER populates and the sets never converge by the test
  // above — which under a naive implementation means no inventory view is ever
  // reported for that visit.
  //
  // The signal that separates "not applied yet" from "will never be applied" is
  // movement: while the platform is still resolving, each report differs from the
  // last. Once two consecutive reports are identical, it has finished and
  // whatever the URL asked for that is still missing was refused.
  var lastSrpReportSignature = null;

  function srpStateStoppedMoving(state, totalResults) {
    var signature = JSON.stringify(state) + '|' + String(totalResults);
    var repeated = lastSrpReportSignature === signature;
    lastSrpReportSignature = signature;

    return repeated;
  }

  // How long to wait for the sets to converge before saying so. Not a deadline
  // for the event — nothing fires late — only for the diagnosis. The third edge
  // rule: if they never converge, fire nothing and log it. A missing SRP view is
  // findable in a console; a wrong one is not findable in GA4.
  var SRP_CONVERGENCE_LOG_MS = 10000;

  var srpConvergenceTimer = null;

  function srpAwaitConvergence() {
    if (srpConvergenceTimer || srpReadyFired) return;
    srpConvergenceTimer = setTimeout(function () {
      srpConvergenceTimer = null;
      if (srpReadyFired) return;
      lbxWarn(
        'lbx_srp_ready never fired: after ' + SRP_CONVERGENCE_LOG_MS + 'ms the ' +
        'applied filters still do not match the ones in the URL, so no inventory ' +
        'view was reported for this load. The page view was counted. Filters the ' +
        'URL asked for: ' + Object.keys(urlFilterDimensions()).join(', ') + '.'
      );
    }, SRP_CONVERGENCE_LOG_MS);
  }

  // An inventory page that reports NO inventory view at all.
  //
  // This is a different failure from the one above, and the one above cannot
  // see it: srpAwaitConvergence() only runs once srpReady() has been called at
  // least once, and srpReady() is called by the FILTER component. The listing
  // and the filter are separate components, and the reporting lives in the
  // filter — so a page that renders a listing WITHOUT a filter classifies as
  // 'srp', fires its page view, and then reports nothing: no inventory view, no
  // filter state, no result count, and no warning either.
  //
  // Found on a page built with the inventory block's filter switched off. The
  // point of this guard is that it does not depend on knowing which block or
  // which site: any page that calls itself an inventory page and never reports
  // one says so here, including cases nobody has met yet.
  var SRP_UNREPORTED_LOG_MS = 12000;
  function srpWatchForMissingInventoryView() {
    if (!ctx || ctx.page_type !== 'srp') return;

    setTimeout(function () {
      // Called and still resolving, or called and fired: not this failure.
      // srpAwaitConvergence() owns the "called but never settled" case and
      // reports it in its own words.
      if (srpReadyCalled) return;

      lbxWarn(
        'No inventory view was reported on a page classified as page_type ' +
        '"srp": after ' + SRP_UNREPORTED_LOG_MS + 'ms nothing had called ' +
        'srpReady(), so lbx_srp_ready, the filter state and the result count ' +
        'are all missing for this load. The page view was counted. The usual ' +
        'cause is an inventory listing rendered without its filter component, ' +
        'which is what carries the reporting.'
      );
    }, SRP_UNREPORTED_LOG_MS);
  }

  // Track which tool-open events we've already fired this page load so the
  // same iBoost 360 / gallery / etc. doesn't emit 'open' a hundred times.
  var toolOpenFired = {};

  // The last window state reported for a tool that is a WINDOW the shopper
  // opens and closes -- chat and video calls. "Once per page load" is the
  // wrong dedupe for those: spec v2.3 §10 has chat fire 'open' "when chat
  // window opens", and a reopen after a close is a second action (Rule 5).
  // With the per-page key a relaunch from "Chat With Sales" after closing the
  // window emitted nothing (Robinson, 2026-10-29, renfrewchrysler.com and
  // capitalcadillac.ca). What Rule 5 forbids is the same state reported twice
  // -- the DOM observer and the vendor's own ASC event both seeing one open --
  // so that is what is suppressed instead.
  var WINDOW_TOOLS = ['chat', 'video_call'];
  var toolWindowState = {};

  // Best-effort metadata about HOW the gallery was opened, captured from the
  // click that triggers it and consumed by observeGallery(), which is what
  // actually fires the 'open' lbx_tool_interaction. See observeGallery below.
  var galleryOpenHint = null;

  var LBX = {
    version: 'phase-c2',
    context: ctx,

    track: {
      /**
       * lbx_page_view — fires immediately on every page load, no exceptions.
       *
       * Base context only: page_type, department, language, device_type,
       * leadbox_id. The vehicle block travels on lbx_vdp_ready and the filter
       * block on lbx_srp_ready, which is what lets this one never wait.
       */
      pageView: function () {
        pushPageView(merge(
          { event: 'lbx_page_view', page_type: lc(ctx.page_type) },
          baseContext()
        ));

        // A VDP's vehicle block is server-rendered, so it is already resolved
        // here and its ready event follows in the same tick. Nothing about that
        // is guaranteed by the contract — vdpReady() decides for itself.
        if (ctx.page_type === 'vdp') LBX.track.vdpReady();

        // An SRP's inventory comes from a component that may not be on the page
        // at all. Start watching from here, where the page type is known and the
        // page view is already counted.
        if (ctx.page_type === 'srp') srpWatchForMissingInventoryView();
      },

      /**
       * lbx_vdp_ready — once per VDP load, when the vehicle payload is complete.
       *
       * Ready means: identity resolved (a VIN or a stock number) and the money
       * fields resolved or determined unavailable. Both come from the server, so
       * in practice this fires immediately after the page view.
       *
       * PAYMENT IS NOT PART OF THIS, deliberately and per the spec owner: it is
       * read out of a calculator that may take seconds to compute, or may be a
       * third party's widget that never publishes anything. Waiting for it would
       * make this event hostage to a vendor. It travels on the calculator's own
       * interaction instead — see reportVdpPayment().
       */
      vdpReady: function () {
        if (vdpReadyFired) return;

        if (ctx.page_type !== 'vdp' || !ctx.vehicle) return;

        // No identity, no vehicle view. Firing one would report a vehicle
        // nobody can name; staying silent leaves the page view counted and the
        // problem visible in the console.
        if (!ctx.vehicle.vehicle_vin && !ctx.vehicle.vehicle_stock_number) {
          lbxWarn(
            'lbx_vdp_ready suppressed: the vehicle payload has neither a VIN nor ' +
            'a stock number, so it cannot identify what was viewed. The page ' +
            'view was still counted.'
          );

          return;
        }

        vdpReadyFired = true;
        window.dataLayer.push(merge(
          { event: 'lbx_vdp_ready', page_type: 'vdp' },
          baseContext(),
          ctx.vehicle
        ));
      },

      /**
       * lbx_srp_ready — once per SRP load, when the inventory result is settled.
       *
       * Called by the inventory component with its filter state and count. Fires
       * only once the applied filters match what the URL asked for, so a
       * multi-filter URL cannot report the count of a partially applied set —
       * which is H9 (223 shown, 676 reported) and H8 (a location filter applied
       * by the URL and reported as null) at their root.
       *
       * Filter CHANGES stay on lbx_ui_interaction. This must not re-fire on one,
       * or SRP views inflate with every click.
       */
      srpReady: function (selected, totalResults) {
        // Recorded before every early return: the question this answers is
        // whether a component reported at all, not whether the event fired.
        srpReadyCalled = true;

        if (srpReadyFired) return;

        // A page that merely EMBEDS a listing does not get an inventory view.
        //
        // The component mounts on any filterable hub, including the one on a
        // homepage or on a service page — and under spec v2.1 those keep their own
        // page_type, because the listing is not what the page is about (Q6). An
        // inventory view there would count a homepage visit as an inventory
        // search, so this returns instead.
        //
        // Filter interactions inside that widget still report, carrying the
        // page's own page_type. See srpFilterChange.
        //
        // Note what this replaced: a warning about misclassification, which used
        // to be the guard that caught C1's duplicate page view. It is gone
        // because it can no longer tell the two cases apart — "an SRP the server
        // called something else" and "a page that legitimately embeds a listing"
        // now look identical from here, and the second is the common one.
        if (ctx.page_type !== 'srp') return;

        var state = rememberSrpFilterState(normalizeSrpFilterState(selected), totalResults);
        var stoppedMoving = srpStateStoppedMoving(state, totalResults);

        if (!srpFilterStateSettled(state) && !stoppedMoving) {
          // Not settled, and still moving. Nothing is emitted and nothing is
          // delayed: the component reports again on every filter resolution, and
          // one of those either converges or stops changing. If neither happens,
          // the timer leaves a console line saying so.
          srpAwaitConvergence();

          return;
        }

        srpReadyFired = true;
        if (srpConvergenceTimer) { clearTimeout(srpConvergenceTimer); srpConvergenceTimer = null; }

        window.dataLayer.push(merge(
          { event: 'lbx_srp_ready', page_type: 'srp' },
          baseContext(),
          state,
          { srp_total_results: num(totalResults) }
        ));
      },

      /**
       * Fires lbx_ui_interaction with element_type: 'filter' on SRP filter change.
       * `changedValue` is the value the user just selected/toggled (for element_text).
       */
      srpFilterChange: function (selected, totalResults, changedValue, opts) {
        // `opts` exists for the inventory keyword search (LBT-1602, M2). The
        // search IS a filter change — same 12-field snapshot, same result count
        // — but the spec names it differently: element_type 'search' with
        // element_action 'search', which was the one element_action the theme
        // never emitted. Everything else about the payload is identical, so it
        // reuses this instead of growing a near-copy that can drift.
        opts = opts || {};

        var elementType = lc(opts.element_type) || 'filter';
        // `selected === null` means "the state did not change, carry the last
        // one": the sort path (srpSortChange) reconfigures the listing without
        // touching a filter, so there is nothing new to normalise and nothing to
        // remember — remembering an empty snapshot here would erase the real
        // state for the next event. Before any component has reported, the
        // honest state is all-null (Rule 1), not an invented one.
        var state = selected === null
          ? (lastSrpFilterState || normalizeSrpFilterState({}))
          : rememberSrpFilterState(normalizeSrpFilterState(selected), totalResults);

        // For the keyword search, element_text is the same term filter_keyword
        // carries: lowercased, trimmed, capped, and NULL when it reads as an
        // e-mail address or a phone number. The raw text used to go out in
        // element_text while filter_keyword was correctly dropped, so the PII
        // the spec's guard exists for reached GA4 through the other field
        // (QA 2026-10-22, #5).
        var elementText = elementType === 'search' ? state.filter_keyword : lc(changedValue);

        var payload = merge(
          {
            event: 'lbx_ui_interaction',
            element_type: elementType,
            element_name: lc(opts.element_name) || 'srp_filter',
            element_action: lc(opts.element_action) || 'select',
            element_text: elementText,
            element_position: lc(opts.element_position) || 'sidebar',
            element_url: null,
            // The page's own type, not 'srp': the same filter sidebar can sit on
            // a homepage or a service page, and an interaction there belongs to
            // that page (spec v2.1, Q6).
            page_type: lc(ctx.page_type)
          },
          state,
          { srp_total_results: num(totalResults) }
        );
        window.dataLayer.push(payload);
      },

      /**
       * Sort change on the SRP — element_type 'filter', element_name 'sort'.
       *
       * The spec files sort under 'filter' ("SRP filter / sort controls", §11)
       * and defines the 'filter' payload as the same 13-field snapshot
       * lbx_srp_ready carries, plus srp_total_results: "a field that appears in
       * one place and not the other produces a state snapshot that doesn't
       * describe the state". The sort used to go out through uiInteraction with
       * the 7 base keys only (manual conformance test 2026-10-28, F-2, on all
       * three dealers).
       *
       * The sort component does not hold the filter state or the count, so this
       * reuses the last snapshot the inventory component reported through
       * srpReady / srpFilterChange — the listing the shopper sorted is exactly
       * that one. `text` is "field order" ('price desc'); element_position is
       * 'top' because the control sits above the results, not in the sidebar.
       */
      srpSortChange: function (text, opts) {
        opts = opts || {};
        LBX.track.srpFilterChange(null, lastSrpTotalResults, text, {
          element_name: lc(opts.element_name) || 'sort',
          element_action: lc(opts.element_action) || 'select',
          element_position: lc(opts.element_position) || 'top'
        });
      },

      /**
       * lbx_form_submit — any form submitted successfully.
       * Caller supplies form_name, form_button_text, submission_id.
       * department and form_type are inferred when not explicitly provided.
       * Returns the resolved form_type so callers can branch on it
       * (e.g. to also fire lbx_trade_in_complete / lbx_credit_app_complete).
       */
      formSubmit: function (p) {
        p = p || {};
        var dept = p.department || inferDepartmentFromText(p.form_name) || ctx.department || 'sales';
        var type = p.form_type || inferFormType(p.form_name, p.form_button_text);

        var payload = merge(
          { event: 'lbx_form_submit' },
          {
            department: lc(dept),
            form_name: lc(p.form_name),
            form_type: lc(type),
            form_button_text: lc(p.form_button_text),
            submission_id: p.submission_id !== undefined && p.submission_id !== null && p.submission_id !== ''
              ? String(p.submission_id)
              : null
          },
          vehicleShort()
        );
        window.dataLayer.push(payload);
        return lc(type);
      },

      /**
       * lbx_phone_click — user initiated a phone call (tel: link or JS-driven).
       * Accepts an optional { department, phone_number, affiliation } override;
       * otherwise infers from the element attributes.
       */
      phoneClick: function (p) {
        p = p || {};
        var payload = merge(
          { event: 'lbx_phone_click' },
          {
            department: lc(p.department || ctx.department || 'sales'),
            phone_number: p.phone_number || null,
            affiliation: lc(p.affiliation) || null
          },
          vehicleShort()
        );
        window.dataLayer.push(payload);
      },

      /**
       * lbx_directions_click — user initiated a get-directions action.
       *
       * Tester finding 9 (LBT-1395 round 2): fall back to ctx.location_name
       * and ctx.location_address (single dealer location) so anchors that
       * don't carry per-click data-lbx-location-* still emit usable values
       * instead of null or leaked template placeholders. Department also
       * falls back to ctx.department (page-global) when not provided.
       */
      directionsClick: function (p) {
        p = p || {};
        var payload = merge(
          { event: 'lbx_directions_click' },
          {
            department: lc(p.department || ctx.department || 'general'),
            click_url: p.click_url || null,
            location_name: lc(p.location_name || ctx.location_name || null),
            location_address: lc(p.location_address || ctx.location_address || null)
          },
          vehicleShort()
        );
        window.dataLayer.push(payload);
      },

      /**
       * lbx_ui_interaction — generic nav/tab/CTA/menu interactions.
       * Caller supplies { element_type, element_name, element_action,
       * element_text, element_position, element_url }. Missing fields → null.
       */
      uiInteraction: function (p) {
        p = p || {};
        var payload = {
          event: 'lbx_ui_interaction',
          element_type: lc(p.element_type),
          element_name: lc(p.element_name),
          element_action: lc(p.element_action) || 'click',
          element_text: lc(p.element_text),
          element_position: lc(p.element_position),
          element_url: p.element_url || null,
          page_type: lc(ctx.page_type)
        };
        // Fields that ride on the event itself where the spec asks for them:
        // the SRP filter block on a vendor's listing-configuration event.
        if (p.extra) payload = merge(payload, p.extra);
        window.dataLayer.push(payload);
      },

      /**
       * lbx_tool_interaction — user engaged with a tool/widget.
       * Caller supplies { tool_name, tool_action, tool_result?, tool_stage? }.
       * Vehicle context (7 fields) is attached automatically on VDP.
       *
       * Per spec, every tool MUST fire with tool_action: 'open' at least once
       * when the tool first launches. Use `dedupeOpen: true` in the payload
       * to guarantee the 'open' action fires only once per page load.
       */
      toolInteraction: function (p) {
        p = p || {};
        var toolName = lc(p.tool_name);
        // An explicit null is kept: it is how an outcome that carries no action
        // of its own reports (Appendix A, "tool_result only"). Only a caller that
        // says nothing about the action gets the 'open' default.
        var toolAction = p.tool_action === null ? null : (lc(p.tool_action) || 'open');

        if (WINDOW_TOOLS.indexOf(toolName) !== -1 && (toolAction === 'open' || toolAction === 'close')) {
          if (toolWindowState[toolName] === toolAction) return;
          toolWindowState[toolName] = toolAction;
        } else if (p.dedupeOpen && toolAction === 'open') {
          var key = toolName + ':open';
          if (toolOpenFired[key]) return;
          toolOpenFired[key] = true;
        }

        // Every key in the spec's schema goes out on every push, null when there
        // is no value (Rule 1). tool_input and the two payment fields used to be
        // added only when set, so most tool events arrived without them — absent
        // rather than null, which is what QA flagged on the ASC recalc path
        // (LBT-1903, spec v2.3 l.635). Key order follows the spec's example.
        var payment = p.payment || {};
        var payload = merge(
          { event: 'lbx_tool_interaction' },
          {
            tool_name: toolName,
            tool_action: toolAction,
            tool_result: p.tool_result !== undefined && p.tool_result !== null && p.tool_result !== ''
              ? String(p.tool_result).toLowerCase()
              : null,
            // Which control the shopper changed to produce tool_result — today
            // only the payment calculator's 'calculate' sets it.
            tool_input: p.tool_input !== undefined && p.tool_input !== null && p.tool_input !== ''
              ? String(p.tool_input).toLowerCase()
              : null,
            tool_stage: num(p.tool_stage),
            // The typed payment figures ride the calculator's own 'calculate'
            // event (LBT-1603, Q4) — see reportVdpPayment for why they are an
            // interaction rather than a property of the page. Null on every
            // other tool.
            vehicle_payment_monthly: num(payment.vehicle_payment_monthly),
            vehicle_payment_type: lc(payment.vehicle_payment_type)
          },
          vehicleShort()
        );

        window.dataLayer.push(payload);
      },

      /**
       * lbx_chat_submit — chat/SMS/video lead delivered to the dealer.
       * Stub: no chat widget is currently installed on this theme. API is
       * ready so when a provider is added (ActivEngage, LivePerson, Podium,
       * Gubagoo, etc.) you only need to wire the provider's "lead delivered"
       * callback to this method.
       */
      chatSubmit: function (p) {
        p = p || {};
        var payload = merge(
          { event: 'lbx_chat_submit' },
          {
            department: lc(p.department || ctx.department || 'sales'),
            comm_type: lc(p.comm_type) || 'chat',   // chat|sms|video
            affiliation: lc(p.affiliation) || null,
            submission_id: p.submission_id !== undefined && p.submission_id !== null && p.submission_id !== ''
              ? String(p.submission_id)
              : null
          },
          vehicleShort()
        );
        window.dataLayer.push(payload);
      },

      /**
       * lbx_trade_in_complete — user completed and submitted a trade-in
       * appraisal. Caller supplies { affiliation?, submission_id?,
       * trade_vehicle_make?, trade_vehicle_model?, trade_vehicle_year?,
       * trade_vehicle_condition?, trade_vehicle_trim?, trade_vehicle_mileage? }.
       * The VEHICLE OF INTEREST block is filled from VDP context automatically.
       */
      tradeInComplete: function (p) {
        p = p || {};
        var payload = merge(
          { event: 'lbx_trade_in_complete' },
          {
            affiliation: lc(p.affiliation) || null,
            submission_id: p.submission_id !== undefined && p.submission_id !== null && p.submission_id !== ''
              ? String(p.submission_id)
              : null
          },
          vehicleShort(),
          {
            trade_vehicle_make: lc(p.trade_vehicle_make),
            trade_vehicle_model: lc(p.trade_vehicle_model),
            trade_vehicle_year: num(p.trade_vehicle_year),
            trade_vehicle_condition: lc(p.trade_vehicle_condition),
            trade_vehicle_trim: lc(p.trade_vehicle_trim),
            trade_vehicle_mileage: num(p.trade_vehicle_mileage)
          }
        );
        window.dataLayer.push(payload);
      },

      /**
       * lbx_credit_app_complete — user submitted a credit/finance application.
       */
      creditAppComplete: function (p) {
        p = p || {};
        var payload = merge(
          { event: 'lbx_credit_app_complete' },
          {
            affiliation: lc(p.affiliation) || null,
            submission_id: p.submission_id !== undefined && p.submission_id !== null && p.submission_id !== ''
              ? String(p.submission_id)
              : null
          },
          vehicleShort()
        );
        window.dataLayer.push(payload);
      }
    },

    _helpers: {
      lc: lc, num: num, merge: merge,
      vehicleShort: vehicleShort, baseContext: baseContext,
      normalizeSrpFilterState: normalizeSrpFilterState,
      inferDepartmentFromText: inferDepartmentFromText,
      inferFormType: inferFormType
    }
  };

  window.LBX = LBX;

  // ===========================================================================
  // VENDOR EVENT ADAPTERS
  // ===========================================================================
  // Dealers embed third-party tools — trade-in estimators, chat, finance — and
  // the theme has no integration with any of them, so everything a visitor does
  // inside them is invisible to reporting. That is the whole of LBT-1603's
  // "blocked on a vendor" group: H1, H2, H4, H5, H6, M7.
  //
  // Wiring each tool on each site would be the same anti-pattern as per-site
  // page slugs: it does not survive 200+ sites. What makes a fleet-wide fix
  // possible is that several vendors already push their own events onto the
  // shared window.dataLayer. We wrap push(), recognise those events, and
  // re-emit them as spec-compliant lbx_* events. The vendor's code is never
  // touched, nothing is configured per site, and the vendor's own event keeps
  // flowing to whatever else consumes it.
  //
  // Surfaces verified by reading each vendor's live JS on renfrewchrysler.com
  // (2026-08-14) rather than from documentation:
  //
  //   AutoVerify      autoverify.event.pushAscEvent writes to dataLayer, and
  //                   sendASCCallToActionEvent names 'asc_cta_interaction'.
  //                   ASC is the Automotive Standards Council schema, NOT an
  //                   AutoVerify invention — so the adapter below is written
  //                   against ASC, and every vendor that adopts it is covered
  //                   without new code. That is the leverage here.
  //   Dealer Inspire  the Conversations bundle calls dataLayer.push and carries
  //                   chat_open / chat_close / chat_message_sent / chatLead.
  //                   Its JS globals (DIConv) are commands only — start(),
  //                   isOpen(), setVisitorData() — with no subscription API,
  //                   so dataLayer is the only outbound channel it offers.
  //   AutoFi          sends its analytics to Amplitude through its own endpoint
  //                   (autofi.data.trackerConfigs = amplitudeId/analyticsUrl/
  //                   endpointUrl) and does NOT write to dataLayer. There is
  //                   nothing here to intercept. Left unhandled deliberately —
  //                   a guessed adapter that silently never fires is worse than
  //                   an absent one, because it looks like coverage.
  //
  // `affiliation` is supplied BY the adapter, because the adapter is the thing
  // that knows which vendor it is. That is what lets lbx_trade_in_complete and
  // lbx_credit_app_complete fire without anyone hand-tagging a form wrapper
  // with data-lbx-affiliation per site — the last per-site dependency the
  // dataLayer had. The gate itself is unchanged: these still fire only for a
  // real appraisal/credit tool, never for a generic contact form.

  // Vendor events we recognised but have no mapping for. Recorded rather than
  // guessed at, so QA can read LBX._unmappedVendorEvents and we learn the real
  // vocabulary from production instead of inventing enum values.
  var unmappedVendorEvents = [];

  // Chat leads already converted by the generic chat adapter, keyed by
  // affiliation and lead id. One conversion per lead id (Rule 5; spec v2.3 §7
  // "fire twice with a unique submission_id for each"), the same guard the
  // Gubagoo mapping keeps in gubagooLeadsSeen. Without it a vendor that
  // re-pushes the same lead — on every page while the conversation lasts, or
  // on a retry — emitted the submit + lbx_chat_submit pair each time
  // (observed on the Boilerplate, 2026-10-23).
  var chatLeadsSeen = {};

  // Set by the chat adapter the first time a chat vendor pushes anything.
  // observeChat() reads it to decide whether it is needed at all — one
  // interaction must not be reported by both the event path and the DOM path.
  var chatVendorEventsSeen = false;

  // --- AutoVerify: which tool (frame) an event came from (LBT-1743) --------
  //
  // lead-created does not say whether the trade or the credit tool created the
  // lead, and AutoVerify runs both on the same site — a VDP carries both CTAs.
  // So the flow is taken from the last asc_retail_process sent by the SAME
  // frame (spec owner's answer, 2026-10-05), never from the last one anywhere
  // on the page: that is the error that would count a credit lead as a trade
  // conversion and reach bidding.
  //
  // How the frame is known: AutoVerify's SDK listens for postMessage from
  // app.autoverify.com and, inside that same listener, pushes both its ASC
  // events and lead-created onto dataLayer synchronously (verified in
  // sdk.min.js, 2026-10-05). This listener runs in the CAPTURE phase, so at
  // the window target it runs before the SDK's own (non-capture) listener and
  // the push wrapper can read which frame is speaking. It is cleared on the
  // next task, because each postMessage is a task of its own.
  var AUTOVERIFY_ORIGINS = ['https://app.autoverify.com', 'https://staging-app.autoverify.com'];
  var autoverifyMessageSource = null;

  // Per frame: the flow its last asc_retail_process named, and whether that
  // flow already produced a conversion through ASC. One map covers trade and
  // credit because one frame is one tool — that is the premise of telling
  // AutoVerify's two tools apart by frame (spec v2.3 l.812).
  var ascFlowByFrame = new WeakMap();
  var ascCompletedByFrame = new WeakMap();

  window.addEventListener('message', function (e) {
    if (!e.source || AUTOVERIFY_ORIGINS.indexOf(e.origin) === -1) return;

    var source = e.source;
    autoverifyMessageSource = source;
    setTimeout(function () {
      if (autoverifyMessageSource === source) autoverifyMessageSource = null;
    }, 0);
  }, true);

  // lead_ids already converted, so a re-pushed lead-created counts once.
  var autoverifyLeadsSeen = {};

  // Retired chat vendor names → the current brand, applied to lbx_chat_submit's
  // affiliation (LBT-1743).
  var CHAT_BRAND_ALIASES = {
    'dealer inspire': 'cars commerce',
    'dealerinspire': 'cars commerce'
  };

  // Read the first key that exists — vendor payload shapes are not contractual
  // and vary between versions, so never assume one key name.
  function pick(obj, keys) {
    for (var i = 0; i < keys.length; i++) {
      var v = obj[keys[i]];
      if (v !== undefined && v !== null && v !== '') return v;
    }
    return null;
  }

  // The spec's closed set for element_type. A vendor's value is adopted only if
  // it is a member; anything else is a raw string leaking into an enum field,
  // which is the same defect the audit raised against the feed in C4/C5. Seen
  // in production: AutoVerify sends element_type 'digital_retailing_tool'.
  var UI_ELEMENT_TYPES = [
    'menu', 'search', 'filter', 'tab', 'accordion', 'banner', 'cta_button',
    'modal', 'carousel', 'share', 'language_toggle', 'location_selector'
  ];

  // Never widen the enum to fit a vendor. Record the rejected value instead, so
  // an unmapped vocabulary is visible in QA rather than silently reshaped.
  function specElementType(adapterName, evName, value, fallback) {
    var v = lc(value);
    if (!v) return fallback;
    for (var i = 0; i < UI_ELEMENT_TYPES.length; i++) {
      if (UI_ELEMENT_TYPES[i] === v) return v;
    }
    unmappedVendorEvents.push({ adapter: adapterName, event: evName, unmapped_element_type: v });
    return fallback;
  }

  // Which product a vendor event refers to, from whatever text it carries.
  // Deliberately narrow: only trade and credit map to a tool, because those are
  // the two that unlock a conversion event. Anything else stays a plain CTA.
  function inferVendorProduct(text) {
    var t = String(text || '').toLowerCase();
    if (/trade|apprais|valuation|reprise|[ée]valuation/.test(t)) return 'trade';
    if (/credit|financ|loan|pre-?approv|cr[ée]dit/.test(t)) return 'credit';
    return null;
  }

  // ---------------------------------------------------------------------------
  // ASC retail-process crosswalks (LBT-1603, Q5)
  //
  // asc_retail_process fires when a shopper completes a step in a digital
  // retailing, finance or trade tool. It used to land in _unmappedVendorEvents:
  // the adapter recognised the family and had nowhere to put this member, so it
  // was recorded rather than guessed at. The spec owner supplied the mapping on
  // 2026-08-17 (Appendix A of the LBT-1603 answers), so it now has a home.
  //
  // Both tables are the spec's, verbatim. They are data rather than a chain of
  // ifs because the next ASC family (Q10) adds more of them, and a table can be
  // diffed against the document it came from.
  // ---------------------------------------------------------------------------

  var ASC_FLOW_TO_TOOL = {
    financing: 'credit_tool',
    credit_app: 'credit_tool',
    soft_pull: 'credit_tool',
    trade: 'trade_tool',
    payment_calculator: 'payment_calculator',
    lease: 'payment_calculator',
    compare: 'compare',
    // 'appointment' is the one context-dependent row — see ascToolName().
    ecommerce: 'digital_retailing',
    delivery: 'digital_retailing',
    upload_data: 'digital_retailing',
    retention: 'digital_retailing',
    test_drive: 'digital_retailing'
  };

  // flow_outcome → tool_action. The values absent here are outcomes that carry
  // no action of their own: they refine the RESULT of the step the shopper is
  // already in (approved, declined, error, skipped, back, added, removed,
  // unlock). Mapping those onto an action would invent a second interaction out
  // of one, so they travel as tool_result and leave the action alone.
  var ASC_OUTCOME_TO_ACTION = {
    // 'open' is not in the spec's own table, and adding it is not a liberty:
    // AutoVerify — the only vendor on the fleet that publishes ASC — sends
    // exactly flow_outcome: 'open' when its trade and credit tools launch.
    // Verified in production on renfrewchrysler.com (2026-08-20) on both flows.
    //
    // Without this row the value fell through to the 'select_option' default and
    // the tool never reported an open at all, so the entry point of the funnel
    // was missing and dedupeOpen could never apply. The table lists 'close' but
    // not 'open', which reads like an oversight in the document rather than a
    // deliberate omission — flagged to the spec owner.
    open: 'open',
    opened: 'open',
    start: 'open',
    viewed: 'open',
    stop: 'close',
    close: 'close',
    closed: 'close',
    recalc: 'calculate',
    download: 'download',
    submit: 'submit',
    lead: 'submit',
    proceed: 'select_option',
    next_step: 'select_option',
    finished: 'complete'
  };

  // The outcomes that mean the shopper actually delivered a trade or credit
  // application. ASC has no dedicated conversion event for either, so this is
  // the only signal — and it is why the completion must NOT fire on every
  // asc_retail_process. One trade submission reported three times would be
  // counted three times on eight ad platforms.
  var ASC_CONVERSION_OUTCOMES = ['lead', 'submit', 'approved'];

  // The outcomes Appendix A lists as "tool_result only, no action change". They
  // report with tool_action null, like an unknown value, but they are known, so
  // they are not logged as unmapped.
  var ASC_RESULT_ONLY_OUTCOMES = ['approved', 'declined', 'error', 'skipped', 'back', 'added', 'removed', 'unlock'];

  // The spec's own tool_action enum. Appendix A's first rule for a value the
  // crosswalk does not list is "if the Leadbox enum has a value of the same
  // name, use it" — a vendor's flow_outcome 'complete' used to report with
  // tool_action null and land in the unmapped log (QA 2026-10-22, #8).
  var TOOL_ACTIONS = ['open', 'close', 'calculate', 'play', 'pause', 'complete', 'scroll', 'zoom', 'spin', 'select_option', 'download', 'submit', 'filter'];

  // `product` is the fallback for a vendor that fires the event without naming
  // the flow — inferred from its own product field, the same way the CTA branch
  // does it. A retail process with no recognisable flow is still a retailing
  // step, so it reports digital_retailing rather than a null tool_name: the
  // event exists, and an event naming no tool is worse than a broad one.
  function ascToolName(flowName, department, product) {
    var flow = lc(flowName);

    // Spec: 'appointment' is service_scheduler when the department is service,
    // otherwise it is a retailing step (booking a delivery, say).
    if (flow === 'appointment') {
      return lc(department) === 'service' ? 'service_scheduler' : 'digital_retailing';
    }

    if (flow && ASC_FLOW_TO_TOOL[flow]) {
      return ASC_FLOW_TO_TOOL[flow];
    }

    // Not a value from the table. Two things arrive here: a vendor that named
    // its flow in prose ('Trade Appraisal') rather than with the standard's
    // vocabulary, and a genuinely new ASC flow. Reading the words catches the
    // first without mislabelling the second, since a flow that mentions neither
    // trade nor credit keeps the generic value.
    var hint = product || inferVendorProduct(flow);
    if (hint === 'trade') return 'trade_tool';
    if (hint === 'credit') return 'credit_tool';

    return 'digital_retailing';
  }

  // tool_stage is numeric in the spec, and ASC's flow_stage is free-form: it
  // arrives as 2, as '2', or as prose like 'step 2 of 5'. Pull the first
  // integer out of whatever shows up; a stage with no number in it has nowhere
  // to go in a numeric field and reports null rather than a made-up ordinal.
  function ascToolStage(flowStage) {
    if (flowStage === null || flowStage === undefined || flowStage === '') return null;
    if (typeof flowStage === 'number') return isFinite(flowStage) ? flowStage : null;
    var m = String(flowStage).match(/-?\d+/);
    return m ? Number(m[0]) : null;
  }

  // ---------------------------------------------------------------------------
  // ONE CONSUMER ACTION PRODUCES EXACTLY ONE LEADBOX EVENT (LBT-1603, Q10)
  //
  // This is the rule the whole ASC translation hangs on, and it has to exist
  // before any of the families are written rather than after.
  //
  // ASC is deliberately redundant. Its own wording for the departmental
  // derivatives is "this always happens, regardless if ONE of the following
  // events are fired": a service-appointment form fires BOTH
  // asc_form_submission and asc_form_submission_service_appt for one submitted
  // form. The same pattern repeats across the comm and video-call families.
  //
  // Translating one-for-one would emit two Leadbox conversions for a single
  // lead — and because every ad-platform tag fires on the conversion trigger,
  // that is one lead counted twice on eight platforms at once, with bidding
  // optimising against the inflated number.
  //
  // So a derivative is ENRICHMENT: it sets fields on the one event its base
  // produces, and emits nothing of its own.
  //
  // HOW THE TWO ARE JOINED WITHOUT WAITING. The vendor pushes base and
  // derivative in the same synchronous block, so a MICROTASK — not a timer —
  // runs after both are in hand with no wall-clock delay at all. That matters:
  // the spec's Rule 5 forbids delaying a user-action event, because a form
  // submission is usually followed by a navigation that would take the event
  // with it. A microtask cannot be interrupted by navigation; a setTimeout can.
  //
  // If a vendor turns out to fire its derivative in a later task, the base has
  // already been emitted by then: the enrichment is lost, the event is not
  // duplicated, and the late derivative is recorded rather than emitted. That is
  // the correct failure — an unenriched conversion still counts, a doubled one
  // corrupts the number.
  // ---------------------------------------------------------------------------

  // ASC department → Leadbox department (Appendix A).
  var ASC_DEPARTMENT = {
    sales: 'sales',
    trade: 'sales',
    service: 'service',
    quick_lube: 'service',
    body_shop: 'service',
    parts: 'parts',
    accessories: 'parts',
    credit: 'finance',
    admin: 'general',
    hr: 'general',
    rentals: 'general',
    custom_shop: 'general',
    unknown: 'general'
  };

  // ASC form_type → Leadbox form_type. The ones that keep their name are listed
  // explicitly rather than passed through, so a value the spec does not define
  // cannot reach the payload by accident.
  var ASC_FORM_TYPE = {
    consumer_contact: 'consumer_contact',
    trade: 'trade',
    parts: 'parts',
    service_appointment: 'service_appointment',
    quote: 'quote',
    finance: 'finance_credit',
    credit: 'finance_credit',
    'finance/credit': 'finance_credit',
    accessories: 'parts',
    // Not in the spec's crosswalk, added for the same reason as 'open' in
    // ASC_OUTCOME_TO_ACTION: TradePending — the trade tool on capitalcadillac.ca
    // — sends its completed appraisal as asc_form_submission with
    // form_type 'trade_open' (flow_name 'trade', department 'trade', a
    // submission_id). Verified with a test lead on 2026-10-05 (LBT-1743).
    // Without this row the one trade conversion it publishes became a generic
    // lbx_form_submit with form_type 'other'. Flagged to the spec owner.
    trade_open: 'trade'
  };

  // Where an ASC payload names the vendor, in order of preference.
  var ASC_AFFILIATION_KEYS = ['affiliation', 'vendor', 'partner', 'event_owner'];

  // The chat provider for an ASC chat lead. Cars Commerce's embed names
  // itself event_owner 'conversations' (its product) and puts affiliation
  // 'native' on the submission -- neither is a provider name, and the spec's
  // vendor table is explicit: "Write affiliation: 'cars commerce', never the
  // retired 'Dealer Inspire' brand". Any other vendor keeps what it sends,
  // through the same brand aliases as the generic chat adapter.
  function ascChatAffiliation(p) {
    if (lc(pick(p, ['event_owner'])) === 'conversations') return 'cars commerce';
    var raw = lc(pick(p, ASC_AFFILIATION_KEYS));

    return (raw && CHAT_BRAND_ALIASES[raw]) || raw || null;
  }

  // asc_cta_interaction element_text values a chat embed uses for its own
  // window controls (Cars Commerce: prod.embed.conversations.dealerinspire.com,
  // read 2026-10-30). See the asc_cta_interaction branch.
  var CHAT_WINDOW_CTAS = ['minimize_chat', 'minimize_messenger', 'launcher', 'reply', 'text'];

  // The departmental derivative suffixes, shared by the form, comm and
  // video-call families. sales_appt is the one that carries more than a
  // department: booking a test drive is a distinct form_type in our spec.
  var ASC_DERIVATIVE_DEPARTMENT = {
    parts: { department: 'parts' },
    sales: { department: 'sales' },
    sales_appt: { department: 'sales', form_type: 'test_drive' },
    service: { department: 'service' },
    service_appt: { department: 'service', form_type: 'service_appointment' },
    other: { department: 'general' }
  };

  // Own-property test that does not go through the object under inspection:
  // a vendor payload can legitimately carry a key called "hasOwnProperty".
  function ownProp(obj, key) {
    return Object.prototype.hasOwnProperty.call(obj, key);
  }

  // ASC comm_status → tool_action, for chat and video-call engagement.
  //
  // A status the standard carries but our tool_action enum has no room for is
  // NOT resolved to 'open': that would report a conversation starting every
  // time the vendor reported something else about it. It returns null and the
  // caller records the event instead.
  function ascCommAction(rawStatus) {
    var s = lc(rawStatus);
    if (!s) return null;
    if (/^(open|start|started|initiat|active|accepted|engaged)/.test(s)) return 'open';
    if (/^(close|closed|end|ended|dismiss|minimi|abandon|timeout)/.test(s)) return 'close';
    if (/^(complete|completed|finish|resolved)/.test(s)) return 'complete';

    return null;
  }

  // ASC element_subtype → our element_type. Three of the standard's subtypes are
  // real UI kinds in our enum under different names; without this crosswalk the
  // enum guard rejects them and every one lands on the 'cta_button' fallback,
  // which is how a hero banner and a share icon both used to read as buttons.
  var ASC_ELEMENT_SUBTYPE = {
    cta_button: 'cta_button',
    hyperlink: 'cta_button',
    hero_image: 'banner',
    share_icon: 'share'
  };

  // ASC item_condition → our vehicle_condition, applied to the condition a
  // vendor reports for the trade-in (see ascTradeVehicle()).
  //
  // 'unknown' maps to null rather than to a guess, and ASC has no 'demo': an
  // inbound demo unit arrives as 'new' and there is nothing we can do about that
  // from the vendor's side. On our OWN vehicles the feed's demo flag is
  // authoritative — see get_lbx_vehicle_context().
  var ASC_ITEM_CONDITION = {
    'new': 'new',
    used: 'used',
    salvage: 'used',
    cpo: 'certified',
    dealer_cpo: 'certified',
    oem_cpo: 'certified',
    unknown: null
  };

  function ascItemCondition(raw, adapterName, evName) {
    var c = lc(raw);
    if (!c) return null;
    if (ownProp(ASC_ITEM_CONDITION, c)) return ASC_ITEM_CONDITION[c];

    unmappedVendorEvents.push({ adapter: adapterName, event: evName, unmapped_item_condition: c });

    return null;
  }

  // Adds the six trade_vehicle_* fields of a trade conversion that arrived via
  // ASC to `fields`. Only the vendor's own trade_vehicle_* keys count: ASC's
  // item_* fields (and a bare make / model / year) describe the dealership
  // vehicle the shopper is viewing, not the one being traded, so on a VDP they
  // would report the inventory unit as the trade-in (flagged by AutoVerify,
  // 2026-10-06). A vendor that does not publish the vehicle leaves them null,
  // as the spec says.
  function ascTradeVehicle(fields, p, evName) {
    fields.trade_vehicle_make = pick(p, ['trade_vehicle_make']);
    fields.trade_vehicle_model = pick(p, ['trade_vehicle_model']);
    fields.trade_vehicle_year = pick(p, ['trade_vehicle_year']);
    fields.trade_vehicle_condition = ascItemCondition(pick(p, ['trade_vehicle_condition']), 'asc', evName);
    fields.trade_vehicle_trim = pick(p, ['trade_vehicle_trim']);
    fields.trade_vehicle_mileage = pick(p, ['trade_vehicle_mileage']);

    return fields;
  }

  // ASC media_type → what the interaction actually is. Video, gallery and 360
  // are tools in our spec; a slider is a carousel, which is a UI element. That
  // split is why one ASC event maps onto two different Leadbox events.
  var ASC_MEDIA_TOOL = {
    video: 'video_player',
    gallery: 'gallery',
    image: 'gallery',
    thumbnail: 'gallery',
    '360_platform': '360_spin',
    vr: '360_spin'
  };

  // The LBX Slider block's CTA classes, one per layout, and the element_name
  // each reports as (see the click rule "LBX Slider block CTAs").
  var LBX_SLIDER_CTAS = [
    ['lbx-slider__hero-cta', 'lbx_slider_hero_cta'],
    ['lbx-slider__floating-cta', 'lbx_slider_floating_cta'],
    ['lbx-slider__overlay-cta', 'lbx_slider_overlay_cta'],
    ['lbx-slider__split-cta', 'lbx_slider_split_cta'],
    ['lbx-slider__media-col-cta', 'lbx_slider_media_col_cta'],
    ['lbx-countdown__cta', 'lbx_slider_countdown_cta'],
    ['lbx-offer-hero__shop', 'lbx_slider_offer_hero_cta'],
    ['lbx-offer-block__shop', 'lbx_slider_offer_block_cta'],
    ['lbx-bt__cta', 'lbx_slider_brand_tabs_cta'],
    ['lbx-motion__cta', 'lbx_slider_motion_cta']
  ];

  // tool_action values a media event can carry under its own name.
  var MEDIA_TOOL_ACTIONS = ['open', 'close', 'play', 'pause', 'complete', 'scroll', 'zoom', 'spin', 'download'];

  // Media actions, which have a wider vocabulary than a chat status.
  //
  // Same-name first (spec v2.3 Appendix A, l.942: "if the Leadbox enum has a
  // value of the same name, use it"). The patterns below used to run first and
  // folded 'play' into 'open', 'pause' into 'close', 'zoom' into 'open' and
  // 'scroll' into 'select_option' — so a video starting reported as the player
  // opening (LBT-1903, QA CO 3/8/12), and the spec's own video minimum ('play'
  // when the video starts) could never be met.
  //
  // `tool` decides the loose synonyms: 'start' / 'stop' are playback on a video
  // and opening / closing on a gallery or a 360 viewer, which is what they
  // reported before for every medium.
  function ascMediaAction(rawAction, tool) {
    var a = lc(rawAction);
    if (!a) return null;
    if (MEDIA_TOOL_ACTIONS.indexOf(a) !== -1) return a;
    var isVideo = tool === 'video_player';
    if (/^(play|start)/.test(a)) return isVideo || a.indexOf('play') === 0 ? 'play' : 'open';
    if (/^(open|launch|view|expand)/.test(a)) return 'open';
    if (/^(pause|stop)/.test(a)) return isVideo || a.indexOf('pause') === 0 ? 'pause' : 'close';
    if (/^(close|exit|collapse|dismiss)/.test(a)) return 'close';
    if (/^(complete|completed|ended|finish)/.test(a)) return 'complete';
    if (/^(download|save)/.test(a)) return 'download';
    // zoom_in / zoom_out / zoomed: the enum has a single 'zoom'.
    if (/^zoom/.test(a)) return 'zoom';
    // Moving through content is 'scroll' in the enum ("gallery, carousel") —
    // the theme's own gallery already reports prev / next that way. The prefix
    // also takes the vendors' own forms of it (scrolling, scroll_left).
    if (/^(next|prev|previous|swipe|scroll)/.test(a)) return 'scroll';
    if (/^(select|choose|click)/.test(a)) return 'select_option';

    return null;
  }

  // What an asc_element_configuration actually configured. The standard does not
  // name it in a single field, so this reads the vendor's own enum first and
  // falls back to the words in the control's name.
  //
  // 'filter' is the default rather than null because that is what the event is
  // for — changing how a listing is configured — and our spec already files sort
  // under filter. A wrong-but-in-enum value is visible in reporting; a null
  // element_type is a broken event.
  function ascConfigurationType(payload, evName) {
    var declared = specElementType('asc', evName, pick(payload, ['element_type', 'element_subtype']), null);
    if (declared === 'filter' || declared === 'search' || declared === 'language_toggle' || declared === 'location_selector') {
      return declared;
    }

    var text = String(
      pick(payload, ['element_name', 'element_title', 'element_text', 'configuration_type', 'link_text']) || ''
    ).toLowerCase();

    if (/lang|français|francais|english|espa/.test(text)) return 'language_toggle';
    if (/location|dealer|store|branch|succursale/.test(text)) return 'location_selector';
    if (/search|keyword|query/.test(text)) return 'search';

    return 'filter';
  }

  // ASC events we understand and deliberately do not emit, with the reason.
  //
  // Kept apart from _unmappedVendorEvents on purpose: "we have not mapped this"
  // is a gap and "we decided not to" is an answer, and a list that mixes them
  // makes a settled decision look like an oversight at the next audit.
  var ascDeclinedEvents = [];

  // When each ASC publisher last opened a tool, by event_owner. TradePending
  // publishes three events for one click on "Value Your Trade":
  // asc_cta_interaction, asc_element_configuration (element_text "Value Your
  // Trade") and asc_retail_process trade/start. Translating all three gave two
  // Leadbox events for one consumer action (conformance test 2026-10-23, F4),
  // which Rule 5 forbids: the redundant member of a vendor's burst "emits
  // nothing of its own". The tool open is what the shopper did; a configuration
  // event that lands within a beat of it from the same owner is that same
  // action and is declined (recorded in _ascDeclinedEvents, not dropped
  // silently). A real asc_element_configuration — a vendor filter changed on
  // an SRP with no CTA before it — still translates.
  var lastAscToolOpenByOwner = {};
  var ASC_SAME_ACTION_WINDOW_MS = 2000;

  function noteAscToolOpen(owner) {
    lastAscToolOpenByOwner[owner || 'unknown'] = Date.now();
  }

  function ascFollowsToolOpen(owner) {
    var at = lastAscToolOpenByOwner[owner || 'unknown'];
    return !!at && (Date.now() - at) < ASC_SAME_ACTION_WINDOW_MS;
  }

  function ascDeclinedReason(evName) {
    // Per-field form-fill milestones: high volume, no decision value. Declined
    // explicitly by the spec owner (LBT-1603, Q10).
    if (evName === 'asc_form_engagement') {
      return 'out of scope by decision — per-field fill telemetry, no decision value';
    }

    // A monetary transaction completed over the phone. Nothing downstream
    // consumes it, so it is out of scope rather than unmapped.
    if (evName === 'asc_voice_sale') {
      return 'out of scope by decision — nothing downstream consumes it';
    }

    // The voice family is the one genuinely open question in the standard's
    // surface: a connected, qualified, department-attributed call has no
    // equivalent in our spec. Either lbx_call_complete is added or the family is
    // declared out of scope, and until that is decided, guessing would invent a
    // conversion. Recorded so it stays visible.
    if (evName.indexOf('asc_voice_') === 0) {
      return 'decision pending — the voice family needs lbx_call_complete or an explicit out-of-scope';
    }

    // A page view of our own already fired from LBX_CONTEXT before any vendor
    // spoke, and it is the authoritative one: it carries the server's
    // classification. Translating asc_pageview on top of it would report two
    // page views for one page — the exact defect the audit opened with (C1).
    //
    // Appendix A maps asc_pageview onto lbx_page_view, and that is right for a
    // platform where the container is the only translator. On a Leadbox theme
    // the theme is, so this row must not be applied here. Worth a line in the
    // spec, since nothing else in Appendix A depends on which side translates.
    if (evName === 'asc_pageview') {
      return 'not applicable on a Leadbox theme — our own lbx_page_view is authoritative and a second one would double-count';
    }

    // The item and item-list page views map onto our two ready events — and on
    // a Leadbox theme those have already fired from our own data by the time a
    // vendor speaks. Same reason as asc_pageview above: translating them would
    // report a second vehicle or inventory view for one page.
    //
    // They stay listed because on a platform where the container translates, the
    // Appendix A mapping is right and this is where it happens.
    if (evName === 'asc_item_pageview' || evName === 'asc_itemlist_pageview') {
      return 'not applicable on a Leadbox theme — lbx_vdp_ready / lbx_srp_ready already fire from our own data';
    }

    return null;
  }

  function ascDepartment(raw, adapterName, evName) {
    var d = lc(raw);
    if (!d) return null;
    if (ownProp(ASC_DEPARTMENT, d)) return ASC_DEPARTMENT[d];

    // A department the standard does not define is recorded, not reshaped —
    // same rule the element_type guard follows.
    unmappedVendorEvents.push({ adapter: adapterName, event: evName, unmapped_department: d });

    return 'general';
  }

  function ascFormType(raw, adapterName, evName) {
    var t = lc(raw);
    if (!t) return null;
    if (ownProp(ASC_FORM_TYPE, t)) return ASC_FORM_TYPE[t];

    // The spec's own instruction for everything else (recall, pre-order,
    // bodyshop, surveys, …) is 'other', so this is a mapping rather than a
    // rejection — but it is still worth seeing in QA.
    unmappedVendorEvents.push({ adapter: adapterName, event: evName, unmapped_form_type: t });

    return 'other';
  }

  // Runs fn after the current synchronous block, without a timer. Promise is
  // used when available and setTimeout is the fallback for the browsers this
  // ES5 module still supports.
  function ascMicrotask(fn) {
    if (typeof Promise === 'function') {
      Promise.resolve().then(fn)['catch'](function (e) {
        if (window.console && console.warn) console.warn('[LBX] ASC group flush failed', e);
      });

      return;
    }
    setTimeout(fn, 0);
  }

  // Groups awaiting their microtask flush, and the ones already flushed. Keyed
  // by group name plus submission id, so two submissions in one page load stay
  // separate while a late derivative of an already-emitted group is refused.
  var ascPendingGroups = {};
  var ascFlushedGroups = {};

  /**
   * Collect one member of an ASC event group and emit the single Leadbox event
   * once the whole group is in hand.
   *
   * @param {string}   groupName Stable name for this family, e.g. 'form_submission'.
   * @param {object}   payload   The vendor's payload.
   * @param {object}   fields    What this member contributes (department, form_type…).
   * @param {boolean}  isBase    True for the base event, false for a derivative.
   * @param {function} emit      Receives the merged payload and fires the Leadbox event.
   * @return {boolean} Always true: the member was understood, whether or not it emitted.
   */
  function ascCollectGroup(groupName, payload, fields, isBase, emit) {
    var id = pick(payload, ['submission_id', 'form_submission_id', 'lead_id', 'conversation_id']);
    var key = groupName + ':' + (id === null ? '_' : String(id));

    // A group with no id exists only for the current synchronous block: the
    // base and its derivatives arrive together, and that is all the key has to
    // hold. It used to be remembered in ascFlushedGroups like a real id, so
    // every later id-less submission on the page landed on the same key and
    // was refused as a late derivative — a shopper's second trade lead, or a
    // second vendor's form, counted zero (QA 2026-10-22, #3). Only an id can
    // identify a straggler, so only an id is remembered.
    if (id !== null && ascFlushedGroups[key]) {
      // The base already went out. Record the straggler instead of emitting a
      // second conversion for the same action.
      unmappedVendorEvents.push({
        adapter: 'asc',
        event: payload.event,
        note: 'derivative arrived after its base event had been emitted; enrichment lost, not re-emitted'
      });

      return true;
    }

    var pendingGroup = ascPendingGroups[key];
    if (!pendingGroup) {
      pendingGroup = ascPendingGroups[key] = { payload: {}, fields: {}, sawBase: false, emit: null };
      ascMicrotask(function () {
        delete ascPendingGroups[key];
        if (id !== null) ascFlushedGroups[key] = true;
        // A derivative with no base is still one consumer action, so it emits
        // on its own rather than being dropped — ASC says the base always
        // fires, but a vendor getting that wrong should not cost us the lead.
        if (pendingGroup.emit) {
          pendingGroup.emit(pendingGroup.payload, pendingGroup.fields);
        }
      });
    }

    // The base's payload wins on shared keys: it is the event the standard
    // guarantees, and the derivative is only meant to add a department.
    var merged = pendingGroup.payload;
    for (var k in payload) {
      if (!ownProp(payload, k)) continue;
      if (isBase || merged[k] === undefined) merged[k] = payload[k];
    }
    for (var f in fields) {
      if (!ownProp(fields, f)) continue;
      if (fields[f] !== null && fields[f] !== undefined) pendingGroup.fields[f] = fields[f];
    }
    if (isBase) pendingGroup.sawBase = true;
    if (emit) pendingGroup.emit = emit;

    return true;
  }

  // Vendor widgets that render into an OPEN shadow root — the DOM counterpart
  // of VENDOR_ADAPTERS below, which covers vendors that speak through events.
  //
  // A click inside a shadow root does reach this module: it is composed and it
  // bubbles. But the browser RETARGETS ev.target to the shadow host, so every
  // rule in the delegated handler — all of which read ev.target and walk up
  // with closest() — sees a bare <div> carrying none of the classes they look
  // for, and drops the interaction before any selector gets a chance.
  //
  // That is the actual reason the audit found payment_calculator "wired but
  // matching nothing" (H6 / M7). The read at the time was that the theme's
  // native calculator selectors no longer matched anything, which is true but
  // incomplete: no selector could have matched, because the click was already
  // invisible by the time the rules ran. Adding selectors would not have fixed
  // it.
  //
  // Verified on renfrewchrysler.com (2026-08-15): clicking AutoFi's "Calculate
  // Your Payment" delivers a click to document with composed = true and
  // ev.target = div.autofi-css-isolator, while ev.composedPath() carries the
  // real <button> 19 nodes deep. The accessibility tree does not see it either.
  //
  // Matched on the vendor's own host element, never on button text: no guessing
  // and nothing that breaks in French. Vendors are listed explicitly here for
  // the same reason they are in VENDOR_ADAPTERS — an adapter that fires on a
  // guess is worse than one that does not fire.
  var VENDOR_WIDGETS = [
    // AutoFi wraps its embed in this class to isolate its CSS. The payment CTA
    // is the only control it renders on the VDP before its own modal opens.
    { name: 'autofi', host: 'autofi-css-isolator', tool: 'payment_calculator' }
  ];

  // The vendor widget a click happened inside, looking THROUGH shadow roots.
  // Falls back to null where composedPath is unavailable, which just leaves the
  // pre-existing behaviour in place rather than throwing.
  function vendorWidgetFromEvent(ev) {
    if (!ev || typeof ev.composedPath !== 'function') return null;
    var path = ev.composedPath() || [];
    for (var i = 0; i < path.length; i++) {
      var node = path[i];
      if (!node || node.nodeType !== 1 || !node.classList) continue;
      for (var w = 0; w < VENDOR_WIDGETS.length; w++) {
        if (node.classList.contains(VENDOR_WIDGETS[w].host)) return VENDOR_WIDGETS[w];
      }
    }
    return null;
  }

  var VENDOR_ADAPTERS = [
    {
      // --- ASC (Automotive Standards Council) --------------------------------
      // Standard schema, so this is not vendor-specific. AutoVerify emits it
      // today; anything else that adopts ASC is covered by the same branch.
      name: 'asc',
      match: function (evName) { return evName.indexOf('asc_') === 0; },
      emit: function (evName, p) {
        // Order matters, and the original order was wrong. `pick` returns the
        // first non-empty key, and AutoVerify sets element_subtype to the
        // generic 'cta_button' on every CTA — so reading it first resolved
        // EVERY event to "no product". Verified on renfrewchrysler.com
        // (2026-08-15): the payload carries product_name 'Trade' / 'Credit'
        // while element_subtype says 'cta_button'. Consequence: the
        // toolInteraction below never ran, so trade_tool / credit_tool were
        // still never emitted (H5) even though this adapter was written to
        // emit them. The vendor's own product field has to win.
        var product = inferVendorProduct(
          pick(p, ['product_name', 'product', 'element_title', 'element_text', 'link_text'])
        );
        // event_owner is how ASC publishers name themselves — TradePending sends
        // event_owner 'tradepending', and AutoVerify's SDK 'autoverify'. Without
        // it a TradePending conversion was attributed to AutoVerify (LBT-1743).
        // Null when the payload names nobody (Rule 1). This defaulted to
        // 'autoverify', which credited any other ASC vendor's conversion to
        // AutoVerify (QA 2026-10-22, #9); AutoVerify's own SDK sends
        // event_owner 'autoverify' on every ASC event (verified live on
        // renfrewchrysler.com, 2026-10-22), so it loses nothing here.
        var affiliation = pick(p, ASC_AFFILIATION_KEYS) || null;

        // Events the standard defines and we deliberately do not emit. Recorded
        // in their own list rather than in _unmappedVendorEvents, because those
        // two mean different things: "we have not mapped this" is a gap,
        // "we decided not to" is an answer. Reading the arrays should tell them
        // apart without reading the code.
        var declined = ascDeclinedReason(evName);
        if (declined) {
          ascDeclinedEvents.push({ event: evName, reason: declined });

          return true;
        }

        // --- communications: chat, SMS and video calls -----------------------
        //
        // ASC here does NOT stand the DOM observer down. It used to, on the
        // reasoning that one conversation would otherwise be counted twice --
        // once from the vendor's event and once from watching its panel
        // appear. That double count is now prevented where it belongs, in
        // toolInteraction(): a window tool never reports the same state twice.
        // And standing the observer down lost real actions, because an ASC
        // vendor does not necessarily report every state change: Cars Commerce
        // publishes 'start' when a conversation begins, but nothing when it
        // restores the window on the next page, and only asc_cta_interaction
        // when the shopper minimizes it (measured on renfrewchrysler.com,
        // 2026-10-30). With the observer off, that close and the reopen after
        // it were never reported.
        if (evName === 'asc_comm_engagement' || evName === 'asc_video_call_engagement') {
          var commAction = ascCommAction(pick(p, ['comm_status', 'status', 'engagement_status']));
          if (!commAction) {
            // A status the mapping does not cover. Recorded rather than
            // resolved to 'open', which would report a conversation starting
            // every time the vendor said something else.
            return false;
          }

          LBX.track.toolInteraction({
            tool_name: evName === 'asc_video_call_engagement' ? 'video_call' : 'chat',
            tool_action: commAction,
            dedupeOpen: commAction === 'open'
          });

          return true;
        }

        // The submission families: base plus departmental derivatives, one
        // Leadbox event for the one conversation the shopper had.
        var commFamily = null;
        if (evName === 'asc_comm_submission' || evName.indexOf('asc_comm_submission_') === 0) {
          commFamily = { group: 'comm_submission', prefix: 'asc_comm_submission_', type: null };
        } else if (evName === 'asc_video_call_submission' || evName.indexOf('asc_video_call_submission_') === 0) {
          commFamily = { group: 'video_call_submission', prefix: 'asc_video_call_submission_', type: 'video' };
        }

        if (commFamily) {
          // A submission event is a lead only when it says so. Cars Commerce
          // publishes asc_comm_submission with comm_status 'send_deal' when the
          // conversation hands consumer details to the dealer
          // (conversationLeadGathered), but its embed also fires the same event
          // name with comm_status 'engage' from a CTA, which is a shopper
          // engaging, not information being sent (spec v2.3 §7: "results in
          // consumer information being sent to the dealer"). Counting it would
          // put a conversion into bidding for a click.
          if (lc(pick(p, ['comm_status', 'status'])) === 'engage') {
            unmappedVendorEvents.push({ adapter: 'asc', event: evName, note: "comm_status 'engage' is not a lead" });

            return true;
          }

          var commDerivative = evName.length > commFamily.prefix.length
            ? evName.slice(commFamily.prefix.length)
            : null;
          var commContributed = {};

          if (commDerivative !== null) {
            var commFields = ASC_DERIVATIVE_DEPARTMENT[commDerivative];
            if (!commFields) return false;
            // Only the department carries over here: a video or chat submission
            // has no form_type, so the sales_appt extra is dropped on purpose.
            commContributed.department = commFields.department;
          }

          return ascCollectGroup(commFamily.group, p, commContributed, commDerivative === null, function (merged, fields) {
            // The tool's own 'submit' rides with the conversion, as on the ASC
            // trade and credit paths (spec v2.3 l.810: "the same rule as the ASC
            // path"). asc_comm_engagement never maps to 'submit', so this cannot
            // duplicate one (Rule 5). Inside the group callback, so the base and
            // its departmental derivatives still produce a single pair
            // (LBT-1903).
            LBX.track.toolInteraction({
              tool_name: commFamily.type === 'video' ? 'video_call' : 'chat',
              tool_action: 'submit',
              tool_result: null
            });
            // comm_type is an enum (chat|sms|video). Cars Commerce sends
            // comm_type 'form' for the lead its chat gathers through an inline
            // form -- still a chat session, so it reports as 'chat'.
            var commType = commFamily.type || lc(pick(merged, ['comm_type', 'channel']));
            if (commType !== 'sms' && commType !== 'video') commType = 'chat';

            LBX.track.chatSubmit({
              // 'chat' unless the vendor says otherwise; the video family is
              // always 'video' regardless of what it puts in comm_type.
              comm_type: commType,
              department: fields.department
                || ascDepartment(pick(merged, ['department', 'comm_department']), 'asc', merged.event),
              affiliation: ascChatAffiliation(merged),
              submission_id: pick(merged, ['submission_id', 'conversation_id', 'lead_id'])
            });
          });
        }

        // --- menus, listing configuration, media and offers ------------------

        if (evName === 'asc_menu_interaction') {
          LBX.track.uiInteraction({
            element_type: 'menu',
            element_name: lc(pick(p, ['element_name', 'element_title', 'link_text'])) || 'vendor_menu',
            element_action: lc(pick(p, ['event_action', 'element_action'])) || 'click',
            element_text: lc(pick(p, ['element_text', 'link_text'])),
            element_position: lc(pick(p, ['element_position'])),
            element_url: pick(p, ['link_url', 'element_url'])
          });

          return true;
        }

        if (evName === 'asc_element_configuration') {
          // Same click as the vendor's own CTA/open a moment ago: one consumer
          // action, one Leadbox event (Rule 5). See lastAscToolOpenByOwner.
          if (ascFollowsToolOpen(lc(affiliation))) {
            ascDeclinedEvents.push({
              event: evName,
              event_owner: lc(affiliation) || null,
              element_text: lc(pick(p, ['element_value', 'element_text', 'link_text'])),
              reason: 'same consumer action as the vendor tool open ' + ASC_SAME_ACTION_WINDOW_MS + ' ms earlier (Rule 5)'
            });

            return true;
          }

          var configType = ascConfigurationType(p, evName);
          var configPayload = {
            element_type: configType,
            element_name: lc(pick(p, ['element_name', 'element_title'])) || ('vendor_' + configType),
            element_action: configType === 'search' ? 'search' : 'select',
            element_text: lc(pick(p, ['element_value', 'element_text', 'link_text'])),
            element_position: lc(pick(p, ['element_position'])),
            element_url: pick(p, ['link_url', 'element_url'])
          };

          // Appendix A: on an SRP the event carries the full filter state. The
          // vendor does not know ours, so the state we last reported is the
          // answer, and it rides on the event itself (Rule 1: every key, on
          // the push a container can see). This used to go out as a second
          // push with no event key — invisible to any trigger listening on
          // named events (QA 2026-10-22, #12).
          //
          // srp_total_results is null, deliberately: the vendor just changed
          // how the listing is configured, so the count we last saw no longer
          // describes it, and a stale number reads as a real result. Our own
          // component supplies the count when it recomputes.
          if (ctx.page_type === 'srp') {
            configPayload.extra = merge(
              lastSrpFilterState || normalizeSrpFilterState({}),
              { srp_total_results: null }
            );
          }

          LBX.track.uiInteraction(configPayload);

          return true;
        }

        if (evName === 'asc_media_interaction') {
          var mediaType = lc(pick(p, ['media_type', 'medium', 'asset_type']));
          var mediaTool = ASC_MEDIA_TOOL[mediaType];
          var mediaAction = ascMediaAction(pick(p, ['event_action', 'media_action', 'element_action', 'action']), mediaTool);

          // A slider is a carousel, which is a UI element rather than a tool.
          if (mediaType === 'slider' || mediaType === 'carousel') {
            LBX.track.uiInteraction({
              element_type: 'carousel',
              element_name: lc(pick(p, ['element_name', 'element_title'])) || 'carousel',
              // element_action has its own, smaller enum: open and close carry
              // over, everything else on a slider is moving through it. A tool
              // action ('play', 'complete', 'download') used to land here as is.
              element_action: mediaAction === 'open' || mediaAction === 'close' ? mediaAction : 'scroll',
              element_text: lc(pick(p, ['element_text'])),
              element_position: lc(pick(p, ['element_position'])),
              element_url: pick(p, ['link_url', 'element_url'])
            });

            return true;
          }

          if (!mediaTool || !mediaAction) {
            // Either a medium the crosswalk does not cover or an action with no
            // spec equivalent. Recorded rather than filed under a guess — the
            // gallery and the video player are different funnels.
            return false;
          }

          LBX.track.toolInteraction({
            tool_name: mediaTool,
            tool_action: mediaAction,
            dedupeOpen: mediaAction === 'open'
          });

          return true;
        }

        if (evName === 'asc_special_offer') {
          // A promotion rendered in place is a banner; one that interrupts the
          // page is a modal. element_position is what the standard gives us to
          // tell them apart.
          var offerPosition = lc(pick(p, ['element_position', 'position', 'placement']));
          var offerType = /modal|popup|overlay|interstitial|lightbox/.test(offerPosition || '') ? 'modal' : 'banner';

          LBX.track.uiInteraction({
            element_type: offerType,
            element_name: lc(pick(p, ['promotion_name', 'offer_name', 'element_name', 'element_title'])) || 'vendor_offer',
            element_action: lc(pick(p, ['event_action', 'element_action'])) || 'click',
            element_text: lc(pick(p, ['element_text', 'promotion_name'])),
            element_position: offerPosition,
            element_url: pick(p, ['link_url', 'element_url'])
          });

          return true;
        }

        if (evName === 'asc_click_to_call') {
          LBX.track.phoneClick({
            phone_number: pick(p, ['comm_phone_number', 'phone_number', 'phone']),
            department: ascDepartment(pick(p, ['department', 'comm_department']), 'asc', evName),
            affiliation: pick(p, ASC_AFFILIATION_KEYS)
          });

          return true;
        }

        if (evName === 'asc_cta_interaction') {
          // A chat widget's own window controls are the chat opening or
          // closing, and that is already one event: the window state reported
          // by asc_comm_engagement and the panel observer. Cars Commerce also
          // publishes these clicks as CTAs -- minimize_chat /
          // minimize_messenger on close, launcher (comm_status 'restart') and
          // the proactive teaser's reply / text on open -- so translating them
          // too would report one click as a chat close AND a CTA (Rule 5).
          // Recorded as declined. Its other chat_tool CTAs (consent, SMS
          // consent, directions, links) change no window and report below.
          if (lc(pick(p, ['element_type'])) === 'chat_tool'
              && CHAT_WINDOW_CTAS.indexOf(lc(pick(p, ['element_text']))) !== -1) {
            ascDeclinedEvents.push({
              event: evName,
              reason: 'chat window control — reported as the chat open/close',
              element_text: lc(pick(p, ['element_text']))
            });

            return true;
          }

          // A CTA that launches a tool IS the tool opening, so it reports as
          // the tool and as nothing else. One click used to produce both
          // lbx_ui_interaction and lbx_tool_interaction (QA 2026-10-22, #6),
          // and the spec defines lbx_ui_interaction as the event for what is
          // "not a tool interaction" — Rule 5 applies. A CTA naming no tool is
          // a plain CTA and reports as one below.
          if (product) {
            noteAscToolOpen(lc(affiliation));
            LBX.track.toolInteraction({
              tool_name: product + '_tool',
              tool_action: 'open',
              dedupeOpen: true
            });

            return true;
          }

          LBX.track.uiInteraction({
            // element_subtype is a valid secondary source, but only through the
            // enum guard — it is where 'cta_button' legitimately arrives.
            // The crosswalk first, then the enum guard. ASC names three UI
            // kinds differently from the spec (hero_image is a banner,
            // share_icon is a share, hyperlink is a cta_button), and without
            // the translation the guard rejects all three and they land on the
            // fallback — a hero banner reported as a button.
            element_type: specElementType(
              'asc',
              evName,
              ASC_ELEMENT_SUBTYPE[lc(pick(p, ['element_subtype', 'element_type']))]
                || pick(p, ['element_type', 'element_subtype']),
              'cta_button'
            ),
            // element_name identifies WHICH control, so it must stay stable
            // when the button's wording changes. The product is the stable
            // identity; free text is only a fallback. element_subtype is gone
            // from here: it is the same generic literal for every CTA, which is
            // why every vendor click used to report element_name 'cta_button'.
            element_name: (product ? product + '_tool' : null)
              || lc(pick(p, ['element_title', 'element_text', 'link_text']))
              || 'vendor_cta',
            element_action: lc(pick(p, ['event_action', 'element_action'])) || 'click',
            element_text: lc(pick(p, ['element_text', 'link_text'])),
            // Was falling back to element_subtype, which is not a position.
            element_position: lc(pick(p, ['element_position'])),
            element_url: pick(p, ['link_url', 'element_url'])
          });

          return true;
        }

        // asc_retail_process — a milestone inside a retailing / finance / trade
        // flow (LBT-1603, Q5).
        //
        // Two things happen at most, and the split is the whole point of the
        // mapping: every milestone is a tool_interaction, and ONLY the outcomes
        // in ASC_CONVERSION_OUTCOMES additionally produce a completion. The
        // completion is the conversion; the tool_interaction is engagement, so
        // the pair is one conversion plus one engagement rather than the same
        // lead counted twice.
        if (evName === 'asc_retail_process') {
          var flowName = pick(p, ['flow_name', 'flow', 'product_name', 'product']);
          var outcome = lc(pick(p, ['flow_outcome', 'outcome', 'status']));
          var toolName = ascToolName(flowName, pick(p, ['department', 'comm_department']), product);
          var action = ASC_OUTCOME_TO_ACTION[outcome]
            || (outcome && TOOL_ACTIONS.indexOf(outcome) !== -1 ? outcome : null);

          var frame = autoverifyMessageSource;
          if (frame) {
            if (lc(flowName)) ascFlowByFrame.set(frame, lc(flowName));
            if (action === 'open') ascCompletedByFrame['delete'](frame);
          }
          if (action === 'open') noteAscToolOpen(lc(affiliation));

          // An outcome outside the crosswalk is recorded, not resolved. Appendix A
          // (v2.3): "record the raw value in tool_result and leave tool_action
          // unchanged" — and "never infer a stage from an unrecognised outcome".
          if (outcome && !action && ASC_RESULT_ONLY_OUTCOMES.indexOf(outcome) === -1) {
            unmappedVendorEvents.push({ adapter: 'asc', event: evName, note: 'flow_outcome ' + outcome });
          }

          LBX.track.toolInteraction({
            tool_name: toolName,
            // A milestone with no recognised action is still engagement with the
            // tool, so it reports — with tool_action null. This used to default
            // to 'select_option', reporting a declined or unknown outcome as the
            // shopper advancing a step (LBT-1903, QA CO 4/5). The previous
            // action is not reused either: 'submit' followed by 'approved' would
            // then read as two submissions (Rule 5).
            tool_action: action,
            // The outcomes that carry no action of their own arrive here, which
            // is how 'approved' and 'declined' survive without pretending to be
            // interactions.
            tool_result: outcome,
            tool_stage: ascToolStage(pick(p, ['flow_stage', 'stage', 'step'])),
            dedupeOpen: action === 'open'
          });

          // The trade / credit fork. `flow_name` decides which one, not the
          // button's wording: ASC names the flow, and inferVendorProduct() is
          // only a fallback for vendors that leave flow_name empty.
          if (outcome && ASC_CONVERSION_OUTCOMES.indexOf(outcome) !== -1) {
            var flow = lc(flowName);
            var isTrade = flow === 'trade' || (!flow && product === 'trade');
            var isCredit = flow === 'financing' || flow === 'credit_app' || flow === 'soft_pull'
              || (!flow && product === 'credit');

            if (isTrade) {
              if (frame) ascCompletedByFrame.set(frame, true);
              LBX.track.tradeInComplete(ascTradeVehicle({
                affiliation: affiliation,
                submission_id: pick(p, ['submission_id', 'form_submission_id', 'lead_id'])
              }, p, evName));
            } else if (isCredit) {
              if (frame) ascCompletedByFrame.set(frame, true);
              LBX.track.creditAppComplete({
                affiliation: affiliation,
                submission_id: pick(p, ['submission_id', 'form_submission_id', 'lead_id'])
              });
            }
          }

          return true;
        }

        // --- the form family (base + its six departmental derivatives) -------
        //
        // One submitted form produces one Leadbox event. Which one depends on
        // what was submitted: a trade or credit application is a conversion of
        // its own, anything else is a form submission.
        var formDerivative = evName.indexOf('asc_form_submission_') === 0
          ? evName.slice('asc_form_submission_'.length)
          : null;

        if (evName === 'asc_form_submission' || formDerivative !== null) {
          // A click on submit is not a submission. Spec v2.3 §4: lbx_form_submit
          // fires when the POST "returns success" and "Do NOT fire on validation
          // errors or failed submissions" -- the same rule the NinjaForms path
          // applies (§15.1 of DATALAYER.md). A GTM tag on capitalcadillac.ca
          // (GTM-KMQZ7S3K, tag 106) pushes asc_form_submission from the click
          // listener of a "Get your Monthly Report" button, after only the
          // browser's checkValidity(), and says so in the payload:
          // comm_status 'submit_click'. Translated, every click with valid
          // fields was a conversion whether or not the server took the lead.
          // Recorded, not converted; a publisher that reports the outcome
          // (no comm_status, or one that is not a click) is unaffected.
          if (/click/.test(lc(pick(p, ['comm_status', 'status'])) || '')) {
            ascDeclinedEvents.push({
              event: evName,
              reason: 'a click on submit, not a completed submission (comm_status ' + lc(pick(p, ['comm_status', 'status'])) + ')',
              form_name: lc(pick(p, ['form_name']))
            });

            return true;
          }

          // Captured now: the group flushes on a microtask.
          var formFrame = autoverifyMessageSource;

          // Only what this member genuinely contributes goes in here. Anything
          // with a default — affiliation, say — must be resolved from the merged
          // payload at flush time instead: a derivative carries no affiliation,
          // so its default would otherwise overwrite the real value the base
          // supplied. Found by the test, not by reading it.
          var contributed = {};

          if (formDerivative !== null) {
            var derivativeFields = ASC_DERIVATIVE_DEPARTMENT[formDerivative];
            if (!derivativeFields) {
              // A suffix the standard does not define. Recorded rather than
              // guessed at, and it still counts as its base's enrichment so it
              // cannot emit an event of its own.
              return false;
            }
            contributed.department = derivativeFields.department;
            if (derivativeFields.form_type) contributed.form_type = derivativeFields.form_type;
          }

          return ascCollectGroup('form_submission', p, contributed, formDerivative === null, function (merged, fields) {
            var submissionId = pick(merged, ['submission_id', 'form_submission_id', 'lead_id']);
            var vendorAffiliation = pick(merged, ASC_AFFILIATION_KEYS) || null;

            // The trade / credit fork. The vendor's own form_type decides when
            // it says so; the product inferred from its text is the fallback
            // for vendors that leave it blank.
            var formType = ascFormType(pick(merged, ['form_type', 'formtype']), 'asc', merged.event)
              || fields.form_type
              || null;
            var isTrade = formType === 'trade' || (!formType && product === 'trade');
            var isCredit = formType === 'finance_credit' || (!formType && product === 'credit');

            // The tool's own 'submit' rides with the conversion, as on the
            // asc_retail_process path, so the trade / credit funnel closes the
            // same way whichever ASC event carried the lead (spec owner's
            // answer 1, 2026-10-05 — two events, two jobs, not a duplicate).
            if (isTrade) {
              if (formFrame) ascCompletedByFrame.set(formFrame, true);
              LBX.track.toolInteraction({ tool_name: 'trade_tool', tool_action: 'submit', tool_result: null });
              LBX.track.tradeInComplete(ascTradeVehicle({
                affiliation: vendorAffiliation,
                submission_id: submissionId
              }, merged, merged.event));

              return;
            }

            if (isCredit) {
              if (formFrame) ascCompletedByFrame.set(formFrame, true);
              LBX.track.toolInteraction({ tool_name: 'credit_tool', tool_action: 'submit', tool_result: null });
              LBX.track.creditAppComplete({
                affiliation: vendorAffiliation,
                submission_id: submissionId
              });

              return;
            }

            // Everything else is a form submission, with the department and
            // form type the derivative supplied.
            //
            // WHY THE FORK RETURNS EARLY INSTEAD OF ALSO EMITTING THIS:
            // Appendix A maps asc_form_submission onto lbx_form_submit as well,
            // but whether a trade or credit submission ALSO counts as a generic
            // form conversion is an open decision on the container side (their
            // ledger item 8) — both events fire every conversion tag on eight
            // ad platforms today, so emitting both would double-count on
            // purpose. When that lands, the two returns above come out.
            LBX.track.formSubmit({
              form_name: pick(merged, ['form_name', 'form_id', 'form']),
              form_button_text: pick(merged, ['element_text', 'button_text']),
              form_type: fields.form_type || formType,
              department: fields.department
                || ascDepartment(pick(merged, ['department', 'comm_department']), 'asc', merged.event),
              submission_id: submissionId
            });
          });
        }

        return false;   // recognised the family, not this member
      }
    },

    {
      // --- AutoVerify lead-created (spec v2.3, Case 2) -----------------------
      // AutoVerify publishes its tools' open outcomes in ASC but the
      // conversions only as its own event, outside the standard:
      //   { event: 'lead-created', event_category: 'autoverify',
      //     user_data: { lead_id, email_address_sha256, phone_number_sha256 } }
      // Verified by submitting a real appraisal on renfrewchrysler.com
      // (2026-08-21).
      //
      // AutoVerify's SDK pushes it onto this page's dataLayer, so the push
      // wrapper reads it like any ASC event.
      //
      // TRADE AND CREDIT. The event does not say which tool created the lead, so
      // the flow comes from the last asc_retail_process sent by the same frame
      // (see autoverifyMessageSource). Trade → lbx_trade_in_complete; credit →
      // lbx_credit_app_complete. Credit was parked under v2.2 until AutoVerify
      // confirmed its credit tool is a soft-check pre-qualification whose
      // lead-created fires at step 1, before the check — so for this vendor the
      // conversion means "credit lead captured", not "pre-qualification
      // completed" (v2.3 Case 2 table, LBT-1903). No frame, or a frame that
      // never named a flow: nothing is guessed, it is recorded as unmapped.
      //
      // The payload carries no vehicle data, so the six trade_vehicle_* fields
      // are null; the vehicle of interest comes from the page. The hashed email
      // and phone are not in the schema and are not passed through.
      name: 'autoverify',
      match: function (evName) { return evName === 'lead-created'; },
      emit: function (evName, p) {
        if (lc(p.event_category) !== 'autoverify') return false;

        // The frame that sent this lead, and the flow that same frame last
        // named. No frame, or a frame that never named a flow: unmapped.
        var frame = autoverifyMessageSource;
        var flow = frame ? ascFlowByFrame.get(frame) : null;
        var tool = ASC_FLOW_TO_TOOL[flow] || null;
        if (tool !== 'trade_tool' && tool !== 'credit_tool') return false;

        var leadId = p.user_data ? pick(p.user_data, ['lead_id']) : null;

        // One application, one conversion (Rule 5): a re-pushed lead, or a
        // flow that already converted through ASC, emits nothing more.
        if (frame && ascCompletedByFrame.get(frame)) return true;
        if (leadId && autoverifyLeadsSeen[leadId]) return true;
        if (leadId) autoverifyLeadsSeen[leadId] = true;

        // Same pair the ASC path emits for outcome 'lead' (spec v2.3 l.810), so
        // each funnel closes the same way whichever way the vendor reports it.
        LBX.track.toolInteraction({ tool_name: tool, tool_action: 'submit', tool_result: null });
        if (tool === 'credit_tool') {
          LBX.track.creditAppComplete({ affiliation: 'autoverify', submission_id: leadId });
        } else {
          LBX.track.tradeInComplete({ affiliation: 'autoverify', submission_id: leadId });
        }

        return true;
      }
    },

    {
      // --- Chat vendors ------------------------------------------------------
      // Dealer Inspire Conversations is the one verified; the vocabulary is
      // generic enough that other chat vendors pushing chat_* land here too.
      name: 'chat',
      match: function (evName) {
        return /^chat[_a-z]*$|^chat_|_chat$|^return_to_chat$|^minimize_chat$/.test(evName);
      },
      emit: function (evName, p) {
        // Tells observeChat() to stand down: this vendor does speak, so the
        // DOM observer would be a second source for the same interaction.
        chatVendorEventsSeen = true;

        // Dealer Inspire is retired as a brand; the product is Cars Commerce. The
        // embed is still DI-namespaced (DIConv, dealerinspire.com), so detecting
        // on DIConv stays right, but the old name must not reach a reporting
        // dimension — whether it comes from the fallback or the vendor payload.
        var affiliation = lc(pick(p, ['affiliation', 'vendor'])) || (window.DIConv ? 'cars commerce' : null);
        if (CHAT_BRAND_ALIASES[affiliation]) affiliation = CHAT_BRAND_ALIASES[affiliation];

        // The lead itself. chatSubmit() has existed since phase C.1 with zero
        // callers, which is exactly why H2 said the event could never fire.
        if (/lead|conversion|submit/.test(evName)) {
          // Case 2 needs a stable name AND an id; a lead push that carries no
          // id is recorded rather than converted, as the Gubagoo mapping already
          // does. The conversion emits the pair every Case 2 mapping emits: the
          // tool's own 'submit' plus lbx_chat_submit (QA 2026-10-22, #11).
          var chatLeadId = pick(p, ['submission_id', 'conversation_id', 'lead_id', 'chatLeadId']);
          if (chatLeadId === null) return false;

          // Same lead pushed again: handled, and nothing emitted (see chatLeadsSeen).
          var chatLeadKey = (affiliation || 'chat') + ':' + chatLeadId;
          if (chatLeadsSeen[chatLeadKey]) return true;
          chatLeadsSeen[chatLeadKey] = true;

          LBX.track.toolInteraction({ tool_name: 'chat', tool_action: 'submit', tool_result: null });
          LBX.track.chatSubmit({
            comm_type: 'chat',
            affiliation: affiliation,
            submission_id: chatLeadId
          });
          return true;
        }

        if (/open|start/.test(evName)) {
          LBX.track.toolInteraction({ tool_name: 'chat', tool_action: 'open', dedupeOpen: true });
          return true;
        }

        if (/close|minimize/.test(evName)) {
          LBX.track.toolInteraction({ tool_name: 'chat', tool_action: 'close' });
          return true;
        }

        // A plain message is not a lead and has no spec-legal tool_action, so
        // it is recorded rather than mapped to an invented value.
        return false;
      }
    },

    {
      // --- iBoost 360 --------------------------------------------------------
      // The original interceptor, unchanged in behaviour — it is simply the
      // first adapter rather than a special case.
      name: 'iboost',
      match: function (evName) { return /iboost|360[_\s-]?spin|spin[_\s-]?360|360spin/.test(evName); },
      emit: function (evName) {
        LBX.track.toolInteraction({
          tool_name: '360_spin',
          tool_action: inferIBoostAction(evName),
          tool_result: null
        });
        return true;
      }
    }
  ];

  // A gtag() call as an event object. gtag is `dataLayer.push(arguments)`, so
  // gtag('event', 'asc_comm_engagement', {...}) lands here as the Arguments
  // object {0: 'event', 1: 'asc_comm_engagement', 2: {...}} -- no `event` key,
  // so every vendor that publishes ASC through gtag was invisible to the
  // adapters. Cars Commerce does (window.gtag("event", name, payload) for its
  // whole ASC vocabulary, including the chat lead): reported by Robinson on
  // 2026-10-29, confirmed in the embed's source on 2026-10-30. Only the 'event'
  // command is translated; 'js', 'config', 'set' are gtag's own bookkeeping.
  // The payload is copied, never mutated: GTM reads the same object.
  function gtagEventObject(arg) {
    if (!arg || typeof arg !== 'object' || arg.event) return null;
    var isArgs = Object.prototype.toString.call(arg) === '[object Arguments]' || Array.isArray(arg);
    if (!isArgs || arg[0] !== 'event' || typeof arg[1] !== 'string' || !arg[1]) return null;

    var out = {};
    var params = arg[2];
    if (params && typeof params === 'object') {
      for (var k in params) {
        if (ownProp(params, k)) out[k] = params[k];
      }
    }
    out.event = arg[1];

    return out;
  }

  (function wrapDataLayerPush() {
    var origPush = window.dataLayer.push;
    window.dataLayer.push = function () {
      var result = origPush.apply(this, arguments);
      for (var i = 0; i < arguments.length; i++) {
        var arg = arguments[i];
        arg = gtagEventObject(arg) || arg;
        if (!arg || typeof arg !== 'object' || !arg.event) continue;
        var evName = String(arg.event).toLowerCase();

        // Skip our own lbx_* pushes to avoid recursion / double-emission.
        if (evName.indexOf('lbx_') === 0) continue;

        for (var a = 0; a < VENDOR_ADAPTERS.length; a++) {
          var adapter = VENDOR_ADAPTERS[a];
          try {
            if (!adapter.match(evName)) continue;
            if (!adapter.emit(evName, arg)) {
              unmappedVendorEvents.push({ adapter: adapter.name, event: evName });
            }
            break;   // one adapter owns an event; no double translation
          } catch (e) {
            // A vendor changing its payload must never break the page or the
            // rest of the module. postMessage and dataLayer shapes are not
            // contractual, so every adapter runs inside this guard.
            if (window.console && console.warn) {
              console.warn('[LBX] vendor adapter "' + adapter.name + '" failed on ' + evName, e);
            }
            break;
          }
        }
      }
      return result;
    };
  })();

  // --- Gubagoo through Shift Digital (spec v2.2, Case 2 — LBT-1743) ---------
  //
  // Gubagoo publishes nothing on window.dataLayer. On OEM-programme sites it
  // reports to Shift Digital instead, with one discrete call per chat event
  // (cdn.gubagoo.io/v4/shiftDigital.*.js, read 2026-10-05):
  //   sd('dataLayer', { chatProvider: 'Gubagoo', events: 'chatLead',
  //                     chatLeadId, chatSessionId, chatProfitCenter, chatType });
  //   sd('send');
  // The lead is a named event carrying its id at the moment it is created, which
  // the spec owner ruled is Case 2 (answer 7, 2026-10-05): lbx_chat_submit,
  // affiliation 'gubagoo', submission_id from chatLeadId. A chatLead without an
  // id is recorded as unmapped, not converted.
  //
  // window.sd is wrapped in a Proxy (so its own properties survive) and
  // re-wrapped whenever Shift Digital replaces its loader stub with the real
  // function, which is why the property becomes an accessor.
  var gubagooLeadsSeen = {};
  var shiftDigitalProxies = typeof WeakSet === 'function' ? new WeakSet() : null;

  function onShiftDigitalCall(args) {
    var data = args[0] === 'dataLayer' ? args[1] : null;
    if (!data || typeof data !== 'object' || lc(data.chatProvider) !== 'gubagoo') return;

    var ev = data.events;
    if (ev === 'chatLead') {
      var leadId = data.chatLeadId;
      if (leadId === undefined || leadId === null || leadId === '') {
        unmappedVendorEvents.push({ adapter: 'gubagoo', event: 'chatLead', note: 'no chatLeadId' });

        return;
      }
      if (gubagooLeadsSeen[leadId]) return;
      gubagooLeadsSeen[leadId] = true;

      // The pair every Case 2 mapping emits (spec v2.3, l.810): the tool's own
      // 'submit' plus the conversion — two events with two jobs, not a
      // duplicate under Rule 5. Same shape as the AutoVerify trade lead. Only
      // the conversion used to fire (LBT-1903, QA C+UI 8, CO 1/6). Inside the
      // chatLeadId dedupe, so a re-pushed lead emits neither.
      LBX.track.toolInteraction({ tool_name: 'chat', tool_action: 'submit', tool_result: null });
      LBX.track.chatSubmit({
        comm_type: lc(data.chatType) === 'text' ? 'sms' : 'chat',
        department: GUBAGOO_DEPARTMENTS[lc(data.chatProfitCenter)] || null,
        affiliation: 'gubagoo',
        submission_id: leadId
      });
    } else if (ev === 'chatClick') {
      // Published engagement, so the DOM observer stands down for this vendor.
      chatVendorEventsSeen = true;
      LBX.track.toolInteraction({ tool_name: 'chat', tool_action: 'open', dedupeOpen: true });
    } else if (ev === 'chatClosed') {
      chatVendorEventsSeen = true;
      LBX.track.toolInteraction({ tool_name: 'chat', tool_action: 'close' });
    }
  }

  // chatProfitCenter → the spec's department enum ('other' has no equivalent).
  var GUBAGOO_DEPARTMENTS = { sales: 'sales', service: 'service', parts: 'parts' };

  function wrapShiftDigital(fn) {
    if (typeof fn !== 'function' || typeof Proxy !== 'function' || !shiftDigitalProxies) return fn;
    if (shiftDigitalProxies.has(fn)) return fn;

    var proxy = new Proxy(fn, {
      apply: function (target, thisArg, args) {
        try {
          onShiftDigitalCall(args);
        } catch (e) {
          if (window.console && console.warn) console.warn('[LBX] Shift Digital adapter failed', e);
        }

        return Reflect.apply(target, thisArg, args);
      }
    });
    shiftDigitalProxies.add(proxy);

    return proxy;
  }

  (function hookShiftDigital() {
    var current = wrapShiftDigital(window.sd);
    try {
      Object.defineProperty(window, 'sd', {
        configurable: true,
        enumerable: true,
        get: function () { return current; },
        set: function (v) { current = wrapShiftDigital(v); }
      });
    } catch (e) {
      // A non-configurable sd: wrap what is there and accept missing a later
      // replacement rather than break the page.
      if (current !== window.sd) window.sd = current;
    }
  })();

  LBX._unmappedVendorEvents = unmappedVendorEvents;
  // Events understood and deliberately not emitted, with the reason. Read this
  // alongside the array above: a name here is a decision, a name there is a gap.
  LBX._ascDeclinedEvents = ascDeclinedEvents;
  LBX._vendorAdapters = VENDOR_ADAPTERS;

  function inferIBoostAction(eventName) {
    var e = eventName || '';
    if (/open|launch|load|start/.test(e)) return 'open';
    if (/close|exit|dismiss/.test(e))     return 'close';
    if (/spin|rotate|drag/.test(e))       return 'spin';
    if (/zoom/.test(e))                   return 'zoom';
    if (/maximize|fullscreen/.test(e))    return 'open';
    if (/hotspot|interior|exterior/.test(e)) return 'select_option';
    return 'open';   // safe default per spec (every tool must fire 'open' at least once)
  }

  // ===========================================================================
  // AUTO-LISTENERS
  // ===========================================================================

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  ready(function () {
    // Before the page view, not after: this corrects a field the page view
    // carries (LBT-1602 M6).
    syncVehicleIncentiveName();

    LBX.track.pageView();

    // No SRP fallback any more, and its absence is the point (LBT-1603, Q4).
    //
    // There used to be a 5-second budget here: if the inventory component had
    // not reported by then, the page view fired with every filter null, and a
    // pagehide flush covered the visitor who left first. Both existed because
    // the page view was carrying the filter block and could not go out without
    // it.
    //
    // It no longer carries it. The page view has already fired by the time this
    // runs, so there is nothing to rescue: if the inventory never settles, the
    // visit is counted and simply has no inventory view attached. That is the
    // failure the spec asks for, and srpAwaitConvergence() leaves a console line
    // explaining it rather than a row of nulls in GA4.

    // -------------------------------------------------------------------------
    // iBoost 360 — fire 'open' when the container is detected.
    // The user-interaction events (spin/hotspot/etc) are captured by the
    // dataLayer.push wrapper above.
    // -------------------------------------------------------------------------
    function checkIboost360Present() {
      var present = document.querySelector(
        '.lbx-iboost360-container, iframe[src*="spin360"], iframe[src*="iboost"]'
      );
      if (present) {
        LBX.track.toolInteraction({
          tool_name: '360_spin',
          tool_action: 'open',
          dedupeOpen: true
        });
        return true;
      }
      return false;
    }
    // Check twice: immediately for inline renders, then after 1s for lazy.
    if (!checkIboost360Present()) {
      setTimeout(checkIboost360Present, 1000);
    }

    // -------------------------------------------------------------------------
    // Configurator / Build & Price — fire 'open' when we're on the B&P page
    // AND the container is in the DOM. Selection events are handled by the
    // delegated click listener below.
    // -------------------------------------------------------------------------
    function checkConfiguratorPresent() {
      var path = (window.location.pathname || '').toLowerCase();
      var onBpPage = /build-and-price|configure|configurateur/.test(path);
      var present = document.querySelector('.lbx-bap, .lbx-build-and-price-models');
      if (onBpPage || present) {
        if (present) {
          LBX.track.toolInteraction({
            tool_name: 'configurator',
            tool_action: 'open',
            dedupeOpen: true
          });
          return true;
        }
      }
      return false;
    }
    if (!checkConfiguratorPresent()) {
      setTimeout(checkConfiguratorPresent, 1000);
    }

    // -------------------------------------------------------------------------
    // Service Scheduler — fire 'open' when the scheduler widget is detected.
    // -------------------------------------------------------------------------
    function checkServiceSchedulerPresent() {
      var present = document.querySelector(
        '.service-scheduler-datetime, .service-scheduler-date-input'
      );
      if (present) {
        LBX.track.toolInteraction({
          tool_name: 'service_scheduler',
          tool_action: 'open',
          dedupeOpen: true
        });
        return true;
      }
      return false;
    }
    if (!checkServiceSchedulerPresent()) {
      setTimeout(checkServiceSchedulerPresent, 1500);
    }

    // -------------------------------------------------------------------------
    // Gallery — reliably detect open/close by watching the shared lightbox node
    // instead of guessing per-version open-trigger selectors.
    // -------------------------------------------------------------------------
    observeGallery();

    // -------------------------------------------------------------------------
    // Chat — detect open/close from the panel for vendors that publish no
    // events. Stands down automatically if the vendor turns out to speak.
    // -------------------------------------------------------------------------
    observeChat();

    // -------------------------------------------------------------------------
    // VDP payment — sync the displayed monthly payment / type into the dataLayer
    // once the calculator's Vue app renders its default value (page_view fires
    // first, so the server context ships these as null).
    // -------------------------------------------------------------------------
    initVdpPaymentSync();

    // -------------------------------------------------------------------------
    // NinjaForms — lbx_form_submit (+ lbx_trade_in_complete / lbx_credit_app_complete)
    // -------------------------------------------------------------------------
    if (typeof jQuery === 'function') {
      jQuery(document).on('nfFormSubmitResponse', function (event, response) {
        try {
          // A rejected submission is not a submission (spec v2.3 §4: "Do NOT
          // fire on validation errors or failed submissions"). NinjaForms
          // triggers this event from the success callback of its AJAX POST,
          // i.e. on ANY HTTP 200 -- and the server rejects with a 200 too:
          // reCAPTCHA failed, server-side field validation, or a nonce expired
          // on a cached page. The rejection is in `errors`, which is `[]` on
          // success and an object (`{form: ...}`, `{fields: ...}`) otherwise.
          // Client-side validation never gets here (NinjaForms does not POST),
          // which is why only empty forms had been tested.
          var errors = response && response.response && response.response.errors;
          if (errors && (Array.isArray(errors) ? errors.length : Object.keys(errors).length)) return;

          var settings = response && response.response && response.response.data && response.response.data.settings
            ? response.response.data.settings
            : {};
          var formName = settings.form_title || settings.title || null;

          // Find the submit button text from the form that was just submitted.
          var buttonText = null;
          var formId = response && response.id ? response.id : (settings.id || null);
          if (formId) {
            var $btn = jQuery('#nf-form-' + formId + '-cont .nf-field-container.submit-container input[type="submit"], '
                              + '#nf-form-' + formId + '-cont .nf-field-container.submit-container button');
            if ($btn.length) buttonText = $btn.first().val() || $btn.first().text() || null;
          }

          // Submission ID: Ninja returns the action_data.id or data.actions for the save action.
          var submissionId = null;
          var actions = response && response.response && response.response.data && response.response.data.actions;
          if (actions && typeof actions === 'object') {
            Object.keys(actions).forEach(function (k) {
              var a = actions[k];
              if (!submissionId && a && (a.sub_id || a.submission_id || a.id)) {
                submissionId = a.sub_id || a.submission_id || a.id;
              }
            });
          }

          // The affiliation lets Ops mark a specific form as powered by
          // DealerTrack / RouteOne / CBB / etc. via an attribute on the form.
          // LBT-1602 (H3a): resolved from the container OR any ancestor.
          //
          // The container is generated by NinjaForms, not by our templates, so
          // there is no theme markup to put the attribute on. The pattern in
          // DATALAYER.md worked around that by having Ops wrap the shortcode in
          // a div carrying the SAME id as the plugin's container, which puts a
          // duplicate id in the DOM -- invalid, and it only resolved because
          // jQuery returns the first match. Accepting the attribute on an
          // ancestor lets Ops wrap with a plain <div data-lbx-affiliation="...">
          // and leave the ids alone. closest() includes the element itself, so
          // any form already marked the old way keeps working unchanged.
          var affiliation = null;
          if (formId) {
            var $form = jQuery('#nf-form-' + formId + '-cont');
            if ($form.length) {
              affiliation = $form.closest('[data-lbx-affiliation]').attr('data-lbx-affiliation') || null;
            }
          }

          // Primary event: lbx_form_submit. Returns the resolved form_type.
          var resolvedType = LBX.track.formSubmit({
            form_name: formName,
            form_button_text: buttonText,
            submission_id: submissionId
          });

          // Secondary conversion events based on form_type.
          // Tester finding 7 (LBT-1395): lbx_trade_in_complete and
          // lbx_credit_app_complete should ONLY fire when the form is backed
          // by a real third-party estimator/application tool (Black Book,
          // Equifax, AutoVerify, AutoFi, DealerTrack, RouteOne, etc.). A
          // generic NinjaForms "request a trade-in value" is a contact form
          // and is fully captured by lbx_form_submit alone. We gate on
          // `affiliation` (data-lbx-affiliation on the form wrapper): if it
          // is non-null, the dealer/Ops has explicitly opted in to the
          // secondary event and named the provider.
          if (resolvedType === 'trade' && affiliation) {
            // Trade vehicle fields (make/model/year/etc) would need to be
            // mapped from the NF field values — not standardized across sites.
            // For now we fire with nulls; Ops can map specific fields by
            // adding data-lbx-trade-field-* to inputs and we'll enhance later.
            LBX.track.tradeInComplete({
              affiliation: affiliation,
              submission_id: submissionId
            });
          } else if (resolvedType === 'finance_credit' && affiliation) {
            LBX.track.creditAppComplete({
              affiliation: affiliation,
              submission_id: submissionId
            });
          }
        } catch (e) {
          if (window.console && console.warn) console.warn('[LBX] form_submit hook error', e);
        }
      });
    }

    // -------------------------------------------------------------------------
    // Delegated click listeners — phone, directions, tool, UI interactions.
    // -------------------------------------------------------------------------

    document.addEventListener('click', function (ev) {
      var el = ev.target;
      if (!el || el.nodeType !== 1) return;

      // Walk up to find an anchor or an opt-in element.
      var node = el;
      var anchor = null;
      var uiOptIn = null;
      var toolOptIn = null;
      var accordion = null;
      while (node && node !== document) {
        if (!anchor && node.tagName === 'A') anchor = node;
        if (!uiOptIn && node.hasAttribute && node.hasAttribute('data-lbx-ui-name')) uiOptIn = node;
        if (!toolOptIn && node.hasAttribute && node.hasAttribute('data-lbx-tool-name')) toolOptIn = node;
        // LBT-1602 (M3): accordions were matched on ev.target alone, so the
        // event only fired when the click landed exactly on the element
        // carrying aria-expanded. Headers wrap a label and an SVG chevron, and
        // clicking the chevron makes ev.target the <svg> or its <path> — the
        // common case missed. Resolve it by walk, like the other opt-ins.
        if (!accordion && node.hasAttribute && node.hasAttribute('aria-expanded')) accordion = node;
        node = node.parentNode;
      }

      // === TOOL INTERACTION — opt-in via data-lbx-tool-name =====================
      // Highest priority: explicit opt-in takes precedence over auto-detects.
      if (toolOptIn) {
        LBX.track.toolInteraction({
          tool_name: toolOptIn.getAttribute('data-lbx-tool-name'),
          tool_action: toolOptIn.getAttribute('data-lbx-tool-action') || 'open',
          tool_result: toolOptIn.getAttribute('data-lbx-tool-result') || null,
          tool_stage: toolOptIn.getAttribute('data-lbx-tool-stage') || null
        });
        return;
      }

      // === TOOL INTERACTION — vendor widget inside a shadow root ==============
      // Checked before the selector-based auto-detects, because for these the
      // walk above is useless: `el` is the shadow host, so `anchor`, `uiOptIn`
      // and friends are all null no matter what the shopper actually clicked.
      // See VENDOR_WIDGETS for why the event is invisible without this.
      //
      // dedupeOpen matters more here than elsewhere: the VDP renders the AutoFi
      // host twice, and once the vendor's modal is open every further click
      // lands inside the same host.
      var vendorWidget = vendorWidgetFromEvent(ev);
      if (vendorWidget) {
        LBX.track.toolInteraction({
          tool_name: vendorWidget.tool,
          tool_action: 'open',
          dedupeOpen: true
        });
        return;
      }

      // === TOOL INTERACTION — auto-detect: window_sticker =====================
      if (anchor && anchor.href && isWindowStickerLink(anchor)) {
        LBX.track.toolInteraction({
          tool_name: 'window_sticker',
          tool_action: 'open',
          tool_result: null
        });
        return;
      }

      // === TOOL INTERACTION — auto-detect: compare ============================
      var compareEl = closest(el, '.compare-button, .add-to-compare-link, .go-to-compare-button, .add-to-compare-label');
      if (compareEl) {
        var isGoTo = !!closest(el, '.go-to-compare-button');
        LBX.track.toolInteraction({
          tool_name: 'compare',
          tool_action: isGoTo ? 'complete' : 'open',
          tool_result: null
        });
        // Do NOT return — allow other handlers to also track (some compare
        // buttons are inside menus/lists and should still count as UI).
      }

      // === TOOL INTERACTION — auto-detect: gallery ============================
      // Inside the lightbox: prev/next/thumb = scroll, close button = close.
      // The close button / backdrop unmount the .vgs lightbox, which
      // observeGallery() detects and reports as 'close' — no manual push here
      // (doing both would double-count). Just swallow the click so it isn't
      // reclassified as a UI interaction by the branches below.
      if (closest(el, '.vgs__close')) {
        return;
      }
      // Tester finding 10 (LBT-1395 round 2): include tool_stage so reporting
      // can see which photo the user was on for each prev/next/thumb action.
      // Read AFTER pushing the interaction so we record the photo the user was
      // ON before navigating — but readGalleryIndex() runs synchronously here,
      // which fires before the gallery component swaps the active slide.
      if (closest(el, '.vgs__prev')) {
        LBX.track.toolInteraction({
          tool_name: 'gallery',
          tool_action: 'scroll',
          tool_result: 'prev',
          tool_stage: readGalleryIndex()
        });
        return;
      }
      if (closest(el, '.vgs__next')) {
        LBX.track.toolInteraction({
          tool_name: 'gallery',
          tool_action: 'scroll',
          tool_result: 'next',
          tool_stage: readGalleryIndex()
        });
        return;
      }
      // Tester finding 10: thumb click without an index was ambiguous. Capture
      // the clicked thumb's position so reporting knows which photo the user
      // jumped to (tool_result=thumb=N, tool_stage=N).
      // Tester finding 12C (round 3): the Vue component wraps each thumb in its
      // own <span v-for> (vue-gallery-slide-show-new.vue.js line ~77). The
      // .vgs__gallery__container__img element is the ONLY child of that <span>,
      // so indexOfSibling(thumbEl) always returns 1. Index the <span> wrapper
      // instead — those <span>s are siblings under .vgs__gallery__container.
      if (closest(el, '.vgs__gallery__container__img')) {
        var thumbEl = closest(el, '.vgs__gallery__container__img');
        var thumbIndex = indexOfSibling(thumbEl.parentNode);
        LBX.track.toolInteraction({
          tool_name: 'gallery',
          tool_action: 'scroll',
          tool_result: thumbIndex ? ('thumb=' + thumbIndex) : 'thumb',
          tool_stage: thumbIndex
        });
        return;
      }
      // Triggers that OPEN the gallery lightbox: main preview image, the
      // "Expand gallery" CTA, the thumb strip below the main image, or any
      // element opted in via [data-gallery-open]. Dedupe so one VDP only emits
      // one 'open' until the gallery is closed.
      // Tester finding 10 (LBT-1395 round 2): emit opened-from so reporting can
      // distinguish dealer-merchandised CTA opens from incidental main-image
      // clicks. When the open came from the thumb strip, also emit tool_stage
      // with the clicked thumb's position.
      // Do NOT push 'open' from a selector match here: the open-trigger markup
      // differs per gallery version (several have none of these classes), which
      // is exactly why QA saw 'open' never fire on multiple versions. Instead we
      // record best-effort "opened-from" metadata and let observeGallery() fire
      // the actual 'open' when the shared .vgs lightbox mounts.
      var galleryOpenEl = closest(el, '.gallery-1-main-img, img.gallery-1-main-img, .bg-gallery, .gallery-expand-btn, [data-gallery-open]');
      if (galleryOpenEl) {
        var openedFrom;
        var openedStage = null;
        if (galleryOpenEl.matches && galleryOpenEl.matches('.gallery-expand-btn')) {
          openedFrom = 'expand-button';
        } else if (galleryOpenEl.hasAttribute && galleryOpenEl.hasAttribute('data-gallery-open')) {
          openedFrom = galleryOpenEl.getAttribute('data-gallery-open') || 'data-attr';
        } else if (galleryOpenEl.matches && galleryOpenEl.matches('.bg-gallery')) {
          openedFrom = 'thumb-strip';
          var stripThumb = el !== galleryOpenEl ? closest(el, '.bg-gallery > *') : null;
          openedStage = stripThumb ? indexOfSibling(stripThumb) : null;
        } else {
          openedFrom = 'main-image';
        }
        galleryOpenHint = { openedFrom: openedFrom, stage: openedStage };
        // Fall through: the click also needs to reach Vue's own @click handler
        // (a separate listener) to actually open the lightbox.
        return;
      }

      // === TOOL INTERACTION — auto-detect: payment_calculator =================
      // Clicks on period toggles (weekly/bi-weekly/monthly) or finance/lease
      // plan icons. First interaction dedupes as 'open'; the change itself
      // reports as 'calculate'. The change listener fires it for typed inputs.
      // Toggles across calculator versions: finance/lease plan icons (V1–V6),
      // term buttons, weekly/bi-weekly/monthly period sections (V7) and the
      // cash/lease/finance tab buttons (V8). QA finding 12.4: V7~V9 period/tab
      // switches were never tracked because none matched the old selector list
      // and the container branch required an <a> (these controls are div/button).
      var pcToggle = closest(el,
        'button[data-action="payment calculator"], .finance_icon, .lease_icon, ' +
        '.term-selector__content button, ' +
        '.weekly-section, .bi-weekly-section, .monthly-section, ' +
        '.payment-calculator-categories [class*="-section"], ' +
        '.tabs-navigation button, .type-active-tab, .type-inactive-tab, ' +
        '.type-active-payment-plan, .type-inactive-payment-plan, ' +
        // QA "Sequence commands": the "Include Tax" toggle is a Tailwind peer
        // checkbox (#checked-toggle is sr-only; the visible track is a <div>
        // inside label.include-tax-section__label), so it matched no control
        // selector and only re-fired the deduped 'open'. Match the include-tax
        // wrapper/label so a concrete select_option fires on toggle.
        '[class*="include-tax"], #checked-toggle, ' +
        // Weekly / Bi-weekly / Monthly buttons of the page-level calculator
        // block (F-4) — label-only tool_input, same as the VDP's period toggles.
        PAGE_CALCULATOR + ' button');
      var pcContainer = closest(el, '#payment-calculator, .payment-calculator__container, .finance-from, .lease-from, .payment-block, .finance-lease, ' + PAGE_CALCULATOR);
      // Same exclusion as the change listener: the lead form mounted inside the
      // calculator box (vdp-payment-calculator v8) is not part of the tool, and
      // returning here would also hide its clicks from every rule below.
      //
      // The modal openers are excluded for the same reason (conformance test
      // 2026-10-23, F7): the "Confirm Availability" button that v8 renders inside
      // the calculator box is the same a.open-form the VDP renders elsewhere, and
      // claiming it here returned before the modal rule below could report the
      // open, so that button pushed only its close.
      if ((pcToggle || pcContainer) && !closest(el, 'form, .nf-form-cont, .lbx-modal-form, a.open-form, .cta-buttons.open-form')) {
        vdpPaymentTouched = true;
        // Any interaction inside the calculator counts as an 'open' (deduped).
        LBX.track.toolInteraction({
          tool_name: 'payment_calculator',
          tool_action: 'open',
          dedupeOpen: true
        });
        // A click that only focuses a typed field (down payment, trade-in value,
        // rate) produces no figure by itself; the figure comes from what is typed
        // next, and the input/change path reports that with the field's name.
        // Scheduling a sync from here raced that path: the calculator recomputes
        // on every keystroke, this sync read the new figure 80 ms after the click
        // with no control name, and the named report that followed on blur was
        // then suppressed as unchanged — tool_input: null on every typed value
        // (conformance test 2026-10-23, F5).
        //
        // A native <select> has the same race in a worse shape: the click that
        // opens it scheduled a sync whose signature was textOf(select) — the text
        // of EVERY option ("weekly bi-weekly monthly") — and the change report
        // that followed, with the control's label and the chosen value, was
        // then suppressed as unchanged. tool_input is "the SINGLE control the
        // shopper changed — name=value ... otherwise the control's label" (spec
        // v2.3 §10), so the select is left to the change path too, which names
        // it from name / data-key / <label for> ("payment frequency=monthly",
        // "loan-term=72"). Retest of 1.23.31, 2026-10-24, observation 1.
        if (closest(el, 'input:not([type="checkbox"]):not([type="radio"]):not([type="button"]):not([type="submit"]):not([type="range"]), textarea, select')) {
          return;
        }
        // ONE EVENT PER CHANGE — Rule 5, ruled absolute by the spec owner
        // (2026-08-20) rather than carved out for this case.
        //
        // This used to fire 'select_option' naming the control and THEN
        // 'calculate' carrying the new figure: two Leadbox events for one click.
        // The 'calculate' already carries both — the figure in tool_result and
        // the control in tool_input, which is exactly the bare-label click-path
        // shape the spec documents ('bi-weekly', no '=') — so the first event
        // only named something the second one was already saying.
        //
        // The rule was kept absolute deliberately: a carve-out here invites the
        // next implementer to argue their case is special too.
        //
        // ACCEPTED CONSEQUENCE, documented in the spec: a toggle that changes no
        // number now fires nothing at all. reportVdpPayment() returns early on an
        // unchanged figure, and unlike the change listener the click path has no
        // fallback push behind it.
        //
        // Info/tooltip icons still never name the control (QA finding 12.4,
        // V1~V6). The reason survives the event they were guarding: the "i" icon
        // is not a selection, so it must not be reported as the input that
        // produced a calculation either.
        var pcControl = pcToggle || closest(el, 'button, [role="button"], label, a');
        var isInfoIcon = closest(el, '[class*="info"], [class*="tooltip"], [data-tooltip], [aria-label*="info" i]');

        // Toggling frequency or finance/lease re-renders the amount, which is a
        // new calculation: it reports as one, carrying the control that caused
        // it — and stays silent if the figure did not actually move.
        schedulePaymentSync((pcControl && !isInfoIcon) ? textOf(pcControl) : null);
        return;
      }

      // === TOOL INTERACTION — auto-detect: configurator (build & price) =======
      // Year selection, vehicle card click, trim card, accessory toggle.
      var bpEl = closest(el, '.span-year, .lbx-bap-vehicle-card, .lbx-bap-available, .lbx-bap-trim-card, .lbx-bap-accessory, [data-bap-option]');
      if (bpEl) {
        LBX.track.toolInteraction({
          tool_name: 'configurator',
          tool_action: 'select_option',
          tool_result: textOf(bpEl)
        });
        return;
      }

      // === PHONE CLICK ========================================================
      if (anchor && anchor.href && anchor.href.toLowerCase().indexOf('tel:') === 0) {
        var phone = anchor.href.replace(/^tel:/i, '').trim();
        var deptP = anchor.getAttribute('data-lbx-department')
          || inferDepartmentFromClasses(anchor)
          || null;
        LBX.track.phoneClick({
          department: deptP,
          phone_number: phone,
          affiliation: anchor.getAttribute('data-lbx-affiliation') || null
        });
        return;
      }

      // === DIRECTIONS CLICK ===================================================
      // 3 detection strategies so no valid directions link slips through:
      //   (a) URL matches a known maps service
      //   (b) anchor (or ancestor) carries a known address/directions class
      //   (c) data-lbx-directions attribute explicitly marks it
      if (anchor && anchor.href) {
        var isDirections = isDirectionsUrl(anchor.href)
          || isDirectionsAnchor(anchor)
          || anchor.getAttribute('data-lbx-directions') === 'true';

        if (isDirections) {
          // Tester finding 9 (LBT-1395 round 2): also forward data-lbx-department
          // so dealers can differentiate "service department directions" from
          // "sales department directions" on multi-department sites. Dropped the
          // anchor.textContent fallback for location_address — when the dealer
          // settings are empty the textContent renders literal placeholders
          // (e.g. "*address_line_1* *address_line_2*"). The directionsClick
          // helper now falls back to ctx.location_address / ctx.location_name
          // (single dealer location) before emitting null.
          // Tester finding 9 (round 3): align with the phone-click pattern —
          // fall back to inferDepartmentFromClasses(anchor) so blades that mark
          // a department via class (lbx-service, lbx-parts, lbx-finance, …) are
          // detected even when the anchor doesn't carry data-lbx-department
          // explicitly. Also climb ancestors via closest() for the explicit
          // attribute, in case the dealer wraps directions in a container.
          var deptAttrEl = closest(anchor, '[data-lbx-department]');
          LBX.track.directionsClick({
            click_url: anchor.href,
            department: (deptAttrEl && deptAttrEl.getAttribute('data-lbx-department'))
              || inferDepartmentFromClasses(anchor)
              || null,
            location_name: anchor.getAttribute('data-lbx-location-name') || null,
            location_address: anchor.getAttribute('data-lbx-location-address') || null
          });
          return;
        }
      }

      // === UI INTERACTION — opt-in via data-lbx-ui-name =======================
      if (uiOptIn) {
        LBX.track.uiInteraction({
          element_type: uiOptIn.getAttribute('data-lbx-ui-type') || 'cta_button',
          element_name: uiOptIn.getAttribute('data-lbx-ui-name'),
          element_action: uiOptIn.getAttribute('data-lbx-ui-action') || 'click',
          element_text: uiOptIn.getAttribute('data-lbx-ui-text') || textOf(uiOptIn),
          element_position: uiOptIn.getAttribute('data-lbx-ui-position') || null,
          element_url: hrefOf(anchor)
        });
        return;
      }

      // === UI INTERACTION — modal-opening CTAs ================================
      // LBT-1602 (H7): element_type 'modal' and 'cta_button' were never emitted
      // because the module's generic escape hatch (data-lbx-ui-name) was never
      // populated by any template — zero occurrences theme-wide. The audit
      // confirmed "Confirm Availability" and "Book a Test Drive" silent.
      //
      // Detected by selector rather than by tagging the markup, for the same
      // reason the header controls above are: these CTAs are rendered by five
      // model-cta blade versions plus the colorizer layouts, so per-template
      // attributes would mean the same edit in a dozen files and a silent gap
      // the next time someone adds version 6. `.open-form` is the class the
      // theme's own modal opener binds to, so it is as stable as the feature.
      //
      // element_name comes from data-id, which the markup already carries and
      // which is meaningful ('model-test-drive'), instead of inventing a name.
      var ctaEl = closest(el, 'a.open-form, .cta-buttons.open-form');
      if (ctaEl) {
        var ctaName = ctaEl.getAttribute('data-id') || ctaEl.id || 'cta';
        LBX.track.uiInteraction({
          element_type: 'modal',
          element_name: lc(ctaName),
          element_action: 'open',
          element_text: textOf(ctaEl),
          element_position: null,
          element_url: null
        });
        return;
      }

      // === UI INTERACTION — detail modal openers ==============================
      // LBT-1602 (QA 9d): the case reads "reports close when opening", and the
      // inversion is an illusion — the open was simply missing. Three templates
      // open the shared detail modal from an info icon, and the close control
      // inside it is already matched by the rule below, so the only modal event
      // a tester ever saw for these was a 'close'.
      //
      // Nothing about the openers looks like a control: they are Vue @click and
      // inline onclick handlers on an <svg>, a <div> and an
      // <a href="javascript:void(0)">. The rule above wants `.open-form`, and
      // the auto-detect at the end of this chain is gated behind `anchor`, which
      // an <svg> never is — so they fell through everything. Reproduced on
      // /our-promotions/, which does not contain a single `.open-form` element.
      //
      // Matched on the classes the templates already carry — `.offer-image-icon`
      // (specials list) and `.so-showcase-details` (specials showcase), each
      // rendered in exactly one template and doing nothing but open this modal.
      // The "Show Legal" text had only utility classes to go by, so it is the
      // one place a hook was added.
      //
      // `.show_discounts_info_icon` is matched as an ANCHOR only, and the tag is
      // load-bearing: vdp-pricing v6 renders it as
      // <a href="javascript:void(0)" onclick="openModal()"> and does open this
      // modal, but vehicle-card v1/v2/v4 render the same class as a <span> that
      // toggles a tooltip panel beside the total-discounts label. Renfrew's
      // /new-vehicles/ carries 15 of those spans, none of them a modal — a bare
      // class match would have reported fifteen modal opens per SRP on a page
      // where no modal exists, which is the mislabelling the audit already
      // punished us for elsewhere.
      //
      // element_name is 'detail_modal', the same name the close below reports,
      // so open pairs with close instead of being two events that cannot be
      // matched up — which is the whole point of the case.
      //
      // LBT-1602 (QA 9e, collateral): the build-and-price page opens this same
      // shared modal for an option's description, and BOTH of its sides were
      // silent. The openers are a bare <img> and a bare <div> with a Vue @click
      // (cl/build-and-price/detail.blade.php), so no rule saw them: not anchors,
      // no `.cta-buttons`, and no `.lbx-bap-*` class, which is what keeps them
      // out of the configurator rule above and lets them reach this one.
      // `.lbx-bap-option-detail-open` is a hook added to those openers for that
      // reason — a name from the existing `.lbx-bap-*` family would have been
      // swallowed by the configurator rule, which runs first.
      var detailOpenEl = closest(el, '.offer-image-icon, .so-showcase-details, a.show_discounts_info_icon, .lbx-offer-legal-open, .lbx-bap-option-detail-open');
      if (detailOpenEl) {
        LBX.track.uiInteraction({
          element_type: 'modal',
          element_name: 'detail_modal',
          element_action: 'open',
          // Icon-only openers have no text; the "Show Legal" one does.
          element_text: textOf(detailOpenEl),
          element_position: null,
          element_url: null
        });
        return;
      }

      // === UI INTERACTION — modal close =======================================
      // LBT-1602 (H7): the audit listed popup close buttons among the silent
      // controls. Only specific closes existed — the gallery lightbox, the omni
      // search and the hamburger — so dismissing a form modal reported nothing,
      // leaving every modal open with no matching close.
      //
      // Both modal components (modal-form and model-modal-form) render their
      // close control as id="close-form-<modalName>", and <modalName> is the
      // same value the opening CTA carries in data-id. Stripping the prefix
      // therefore produces an element_name that pairs open with close for the
      // same modal, instead of two events that cannot be matched up.
      //
      // The build-and-price and special-offers detail modals use their own
      // class, so they are named separately rather than forced into that shape.
      //
      // LBT-1602 (QA 9d): `.lbx-bap-detail-opacity` — the dark overlay behind
      // the detail modal — closes it too, and reported nothing, so a shopper who
      // dismissed by clicking outside left an open with no close. It is an empty
      // sibling of the modal content rather than its parent, so matching it here
      // cannot swallow a click on the content itself.
      //
      // LBT-1602 (QA 9e): `.lbx-modal-form-overlay` — the dark overlay behind a
      // form modal — is the same story one component over. modal-form.js binds
      // the dismiss to it, so clicking outside a Confirm Availability modal
      // closed it and reported nothing: an open with no close, which is exactly
      // what QA filed. The name cannot come from the overlay itself, which
      // carries no id, so it comes from the `#form-<modalName>` wrapper it sits
      // in — the same <modalName> the close button and the opening CTA use, so
      // all three pair up.
      //
      // The overlay only closes where a handler is bound to it, and model pages
      // are bound by bladeScripts/model.js, which binds the close button and the
      // continue-browsing link but NOT the overlay. Reporting a close there
      // would be a lie about a modal still on screen, so the event is emitted
      // only once the wrapper is actually hidden.
      //
      // That check cannot be made synchronously. Whether the dismiss has already
      // run by the time this listener does depends on where the page bound it —
      // directly on the overlay, or delegated on document alongside this one, in
      // which case registration order decides, and it is not ours to control.
      // Read synchronously it was correct on the audited dealership and wrong on
      // a local VDP, where the modal closes and the class lands after this
      // listener has already run: a real dismiss reported nothing. So the state
      // is read on the next tick, once every handler for this click has had its
      // turn. The push is a few milliseconds late and in the same order.
      //
      // LBT-1602 (QA 9e): "Continue Browsing" is the third way out of a form
      // modal — the link both modal components reveal once the form is sent,
      // bound to the same dismiss in modal-form.js:210 and model.js:413. It was
      // not silent: it carries `.cta-buttons`, so the plain-CTA rule far below
      // reported it as a cta_button named after its raw id. But that left the
      // modal's open with no close, and only one rule can fire per click — two
      // events for one action is exactly what case 11f checks. Reporting the
      // close is the more useful of the two, and nothing is lost: the label
      // travels in element_text, which is why this branch keeps its text where
      // the icon-only controls send null.
      var modalCloseEl = closest(el, '[id^="close-form-"], [id^="lbx-continue-browsing-"], .lbx-bap-detail-modal-close, .lbx-bap-detail-modal-close-svg, .lbx-bap-detail-opacity, .lbx-modal-form-overlay');
      if (modalCloseEl) {
        var closeId = modalCloseEl.id || '';
        var isFormOverlay = !!closest(modalCloseEl, '.lbx-modal-form-overlay');
        var formWrap = isFormOverlay ? closest(modalCloseEl, '.lbx-modal-form') : null;

        // An overlay with no modal around it is not a dismiss of anything.
        if (isFormOverlay && !formWrap) return;

        var isContinueBrowsing = closeId.indexOf('lbx-continue-browsing-') === 0;
        var wrapId = (formWrap && formWrap.id) || '';
        var closedName = closeId.indexOf('close-form-') === 0
          ? closeId.slice('close-form-'.length)
          : isContinueBrowsing
            ? closeId.slice('lbx-continue-browsing-'.length)
            : (wrapId.indexOf('form-') === 0 ? wrapId.slice('form-'.length) : 'detail_modal');

        var emitModalClose = function () {
          LBX.track.uiInteraction({
            element_type: 'modal',
            element_name: lc(closedName) || 'modal',
            element_action: 'close',
            // Icon-only control, same convention as the header buttons above —
            // except Continue Browsing, which is the one close that has a label.
            element_text: isContinueBrowsing ? textOf(modalCloseEl) : null,
            element_position: null,
            element_url: null
          });
        };

        // The X and Continue Browsing always close; only the overlay has to be
        // asked, on the next tick, whether it did.
        if (isFormOverlay) {
          setTimeout(function () {
            if (formWrap.classList.contains('hidden')) emitModalClose();
          }, 0);
          return;
        }

        emitModalClose();
        return;
      }

      // === UI INTERACTION — language toggle ===================================
      // LBT-1602 (H7): element_type 'language_toggle' had no emitter. The
      // switcher markup comes from Polylang, not from our templates, so there
      // is nothing of ours to tag — `.lang-item` is Polylang's own class and is
      // what the theme's own polylangLangSwitcher.js already binds to.
      //
      // element_text is the language label the user clicked, which is the whole
      // point of the event: which language they switched to.
      var langEl = closest(el, '.lang-item');
      if (langEl) {
        LBX.track.uiInteraction({
          element_type: 'language_toggle',
          // The spec defines 'toggle' as "Toggle switched (language, dark
          // mode)" — it names this exact control, so it is not a 'select'.
          element_action: 'toggle',
          element_name: 'language_switcher',
          element_text: textOf(langEl),
          element_position: null,
          element_url: hrefOf(anchor)
        });
        return;
      }

      // === UI INTERACTION — promotional banner ================================
      // LBT-1602 (H7): element_type 'banner' had no emitter. The VDP model
      // banner carries no visible text of its own, so element_text falls back
      // to the link's title attribute — the only human-readable label in the
      // markup ("Explore more about the new 2026 Compass").
      //
      // LBT-1602 (QA 9c): that fallback could never run. The banner is split in
      // two halves — a text block and an image block — and only the image half
      // is wrapped in the <a> (see cl/vdp-model-banner/1.blade.php). The link is
      // therefore a DESCENDANT of the banner and never an ancestor, so
      // closest(), which only walks up, could not reach it. Measured on
      // renfrewchrysler.com: the anchor covers 1001px of a 1669px banner, so on
      // the remaining ~40% — the headline half, the most inviting part to click
      // — the event reported a banner click with no destination and no title.
      //
      // Climbing to the outermost wrapper is what makes the lookup work: the
      // class fragment also matches the nested '-text', '-explore' and '-image'
      // elements, so closest() hands back whichever one was clicked, and the
      // anchor lives in a SIBLING subtree of it.
      var bannerEl = closest(el, '[class*="model-banner"]');
      if (bannerEl) {
        var bannerRoot = bannerEl;
        while (bannerRoot.parentElement
          && /model-banner/.test((bannerRoot.parentElement.className || '').toString())) {
          bannerRoot = bannerRoot.parentElement;
        }

        var bannerLink = anchor || closest(bannerEl, 'a') || bannerRoot.querySelector('a');
        LBX.track.uiInteraction({
          element_type: 'banner',
          element_name: 'model_banner',
          element_action: 'click',
          element_text: (bannerLink && bannerLink.getAttribute('title')) || textOf(bannerEl) || null,
          element_position: positionOf(bannerEl),
          element_url: bannerLink ? bannerLink.href : null
        });
        return;
      }

      // === UI INTERACTION — promotional slider banner =========================
      // LBT-1602 (QA 9c): the home page's promotional banner is a Smart Slider,
      // and nothing about it could reach the rule above.
      //
      // Its slides are not links. Each one is a div carrying role="button",
      // data-href and data-n2click, and there is not a single <a> in the whole
      // slider — so the banner rule (which matches the VDP model banner by
      // class) never saw them, and neither did any anchor-based rule. Measured
      // on renfrewchrysler.com: eight promotional slides, pointing at real
      // campaign pages and one of them outbound to another domain, and every
      // click on them went unmeasured.
      //
      // The arrows were never the problem — they match the carousel rule below
      // and report. It was the banner itself that was silent.
      //
      // The widget guard is insurance rather than a fix for something observed:
      // on this site the arrows sit outside the slides, but Smart Layers can be
      // placed anywhere, and an arrow inside a slide must stay a carousel
      // scroll rather than become a banner click.
      var sliderSlide = closest(el, '.n2-ss-slide[data-href]');
      if (sliderSlide && !closest(el, '.n2-ss-widget')) {
        var slideHref = sliderSlide.getAttribute('data-href');

        LBX.track.uiInteraction({
          element_type: 'banner',
          // Named after the destination, not after the slide's own label: that
          // label is the image filename ('08-RFW-EP-HOME-DESKTOP-2000x700'), so
          // it identifies the creative rather than the campaign and changes
          // every time the artwork is re-cut. The filename still travels, as
          // element_text, which keeps the creative traceable without making it
          // the identity of the promotion.
          element_name: bannerNameFromUrl(slideHref),
          element_action: 'click',
          element_text: lc(sliderSlide.getAttribute('data-title')),
          element_position: positionOf(sliderSlide),
          element_url: slideHref || null
        });
        return;
      }

      // === UI INTERACTION — LBX Slider block banner ===========================
      // LBT-1903 (QA C+UI 1 and 4): the theme's own lbx-slider block replaced
      // Smart Slider on the home pages, and neither banner rule above knows it.
      // Its image link, a.lbx-slider__img-link, is an empty anchor laid over the
      // slide; the only text it carries is its aria-label (the slide's alt text,
      // or "View offer"), so it also matched no anchor rule below and the click
      // pushed nothing.
      //
      // Named after the destination, like the Smart Slider rule and for the
      // same reason. The arrows (lbx-slider__arrow) never reach here: they are
      // buttons, not this link, and stay carousel scrolls.
      //
      // The OEM Offer Hero layout of the same block draws its own image link,
      // a.lbx-offer-hero__img-link, with no aria-label: same defect, same rule,
      // element_text null (the spec's "null if icon-only").
      //
      // So does the Motion layout: a.lbx-motion__link is laid over the whole
      // slide, aria-hidden and with no label at all.
      var lbxSliderLink = closest(el, 'a.lbx-slider__img-link, a.lbx-offer-hero__img-link, a.lbx-motion__link');
      if (lbxSliderLink) {
        LBX.track.uiInteraction({
          element_type: 'banner',
          element_name: bannerNameFromUrl(lbxSliderLink.href),
          element_action: 'click',
          element_text: lc(lbxSliderLink.getAttribute('aria-label')),
          element_position: positionOf(lbxSliderLink),
          element_url: lbxSliderLink.href || null
        });
        return;
      }

      // === UI INTERACTION — LBX Slider block CTAs =============================
      // LBT-1903: the buttons inside the slider's slides — "View Inventory",
      // "Learn More", the per-slide inline and floating CTAs of every layout —
      // pushed nothing either, for the same reason as the image link: links in
      // main content that no rule covers. They are the spec's cta_button
      // ("non-conversion CTA buttons", v2.3 l.699 and the element_type table).
      //
      // Non-conversion is guaranteed by order, not by this selector: a CTA that
      // dials (tel:), opens a map or opens a lead form is claimed by the phone,
      // directions or modal-CTA rule above, which report it as its own event and
      // return. Whatever reaches here is a plain navigation link.
      //
      // element_name says which slider control it was; the destination travels
      // in element_url.
      var lbxSliderCta = null;
      var lbxSliderCtaName = null;
      for (var sci = 0; sci < LBX_SLIDER_CTAS.length && !lbxSliderCta; sci++) {
        lbxSliderCta = closest(el, 'a.' + LBX_SLIDER_CTAS[sci][0]);
        lbxSliderCtaName = LBX_SLIDER_CTAS[sci][1];
      }
      if (lbxSliderCta) {
        LBX.track.uiInteraction({
          element_type: 'cta_button',
          element_name: lbxSliderCtaName,
          element_action: 'click',
          element_text: textOf(lbxSliderCta),
          element_position: positionOf(lbxSliderCta),
          element_url: lbxSliderCta.href || null
        });
        return;
      }

      // === UI INTERACTION — plain CTA buttons =================================
      // LBT-1602 (H7): 'cta_button' is the module's default element_type for
      // the data-lbx-ui-name opt-in — which no template ever used, so it never
      // emitted once.
      //
      // The modal-opening CTAs are handled earlier and return before reaching
      // here, so what arrives is the other kind: .cta-buttons rendered as plain
      // links to a form page (model-cta v4 and the colorizer layouts). They sit
      // in main content, so neither the nav rule nor the header/footer region
      // rule below would have caught them either — they fell through everything.
      var plainCta = closest(el, '.cta-buttons');
      if (plainCta) {
        // Name: data-id, then id, then the cta-<what> modifier class. That last
        // fallback matters — the VDP print button on QA carries neither id nor
        // data-id, so every plain CTA on a page would otherwise report the same
        // generic 'cta' and be indistinguishable in reporting. 'cta-print'
        // becomes 'print'.
        var ctaName = plainCta.getAttribute('data-id') || plainCta.id || '';
        if (!ctaName) {
          var ctaModifier = (plainCta.className || '').toString()
            .split(/\s+/)
            .filter(function (c) { return c.indexOf('cta-') === 0 && c !== 'cta-buttons'; })[0];
          ctaName = ctaModifier ? ctaModifier.replace(/^cta-/, '') : 'cta';
        }

        LBX.track.uiInteraction({
          element_type: 'cta_button',
          element_name: lc(ctaName),
          element_action: 'click',
          element_text: textOf(plainCta),
          element_position: positionOf(plainCta),
          element_url: hrefOf(anchor)
        });
        return;
      }

      // === UI INTERACTION — block editor buttons ==============================
      // Conformance test 2026-10-23 (F2): the CTA-styled links beside the hero
      // on Capital Cadillac ("New Vehicles", "Pre-Owned Vehicles") pushed
      // nothing. They are not a theme component at all: they are Stackable
      // button blocks the editor placed in page content (a.stk-button inside
      // .wp-block-stackable-button), so no rule above knew them. The core
      // Buttons block (.wp-block-button__link) is the same case. The spec lists
      // CTA button clicks under lbx_ui_interaction (v2.3 section 11).
      //
      // Order keeps this non-conversion: tel:, map and form-opening buttons are
      // claimed by the phone, directions and modal rules above and never reach
      // here, same as for the slider CTAs.
      //
      // element_name: block buttons carry no id, so the editor-assigned custom
      // class on the block wrapper (heroSection__ctas on Cadillac) is the only
      // stable identifier; without one every block button reports
      // 'block_button' and element_text / element_url tell them apart.
      var blockButton = closest(el, 'a.stk-button, a.stk-link, a.wp-block-button__link, .wp-block-button__link, .wp-block-stackable-button a');
      if (blockButton) {
        var blockWrapper = closest(blockButton, '.wp-block-stackable-button, .wp-block-button');
        var blockCustomClass = blockWrapper
          ? (blockWrapper.className || '').toString().split(/\s+/).filter(function (c) {
              return c && !/^(wp-block|stk-|stk--|has-|is-|aligncenter|alignleft|alignright|alignwide|alignfull)/.test(c);
            })[0]
          : null;
        LBX.track.uiInteraction({
          element_type: 'cta_button',
          element_name: blockCustomClass ? lc(blockCustomClass) : 'block_button',
          element_action: 'click',
          element_text: textOf(blockButton),
          element_position: positionOf(blockButton),
          element_url: hrefOf(anchor || blockButton)
        });
        return;
      }

      // === UI INTERACTION — carousel controls =================================
      // LBT-1602 (H7): element_type 'carousel' had no emitter. The theme drives
      // carousels with slick (the bulk) and swiper, so the hooks are the
      // libraries' own control classes rather than ours — a template cannot
      // rename them without breaking the carousel itself, which makes them a
      // stronger contract than any class we would add.
      //
      // Arrows are 'scroll': the spec defines that action as "Scrolled within
      // element (carousel)". Dots are 'select', an option being chosen.
      // element_text is null because every one of these controls is icon-only.
      // Matched on a class *fragment*, not the exact slick class: this theme
      // renders suffixed variants (slick-prev-egypt / slick-next-egypt), so an
      // exact match found them only by way of the generic .slick-arrow and lost
      // the direction. Confirmed against the QA site.
      //
      // Two more control families reach this rule, and neither carries a slick
      // or swiper class — which is why 'carousel' stayed silent on a home page
      // full of carousels (measured on Renfrew: 8 slider instances, 0 events):
      //
      //   * Smart Slider 3 (the `nextend-arrow-previous` / `nextend-arrow-next`
      //     divs) drives the promotional banner. It is a plugin in the standard
      //     stack, not a per-dealer add-on, and it publishes nothing to the
      //     dataLayer of its own, so observing its controls is the only channel.
      //     Its arrows are never marked disabled — there is no such class in its
      //     CSS — so the guard below does not apply to them.
      //   * Our own `lbx-slider` block wires swiper through Vue refs
      //     (`navigation: { prevEl: $refs.swiperPrev, ... }`), so swiper never
      //     injects `swiper-button-*` and the buttons only ever carry
      //     `lbx-slider__arrow--prev` / `--next`.
      //
      // Direction is read from the explicit `nextend-arrow-next` fragment, never
      // a bare "next": the string `nextend-arrow` contains "next" itself, so a
      // looser fragment would label the previous arrow as a forward scroll.
      var carouselArrow = closest(el, '[class*="slick-next"], [class*="slick-prev"], .slick-arrow, [class*="swiper-button-next"], [class*="swiper-button-prev"], [class*="lbx-slider__arrow"], [class*="nextend-arrow"]');
      var carouselDot = carouselArrow ? null : closest(el, '.slick-dots li, .swiper-pagination-bullet, [class*="nextend-bullet"]');
      // A disabled arrow scrolls nothing, so reporting a scroll would invent an
      // interaction the user never had — slick marks the first/last arrow
      // .slick-disabled at the ends of a non-looping carousel, and swiper does
      // the same with .swiper-button-disabled (plus .swiper-button-lock when a
      // single slide makes both arrows inert). The lbx-slider block defaults to
      // loop: false, so its first slide really does have a dead prev arrow.
      if (carouselArrow && carouselArrow.className && /\b(slick-disabled|swiper-button-disabled|swiper-button-lock)\b/.test(carouselArrow.className.toString())) {
        return;
      }
      if (carouselArrow || carouselDot) {
        // Galleries are slick-driven too (.gallery-7 on the VDP, .slick-gallery
        // on SRP vehicle cards). Browsing a gallery is a tool_interaction with
        // tool_name 'gallery', already tracked elsewhere in this file — emitting
        // a generic carousel event for it would mislabel the interaction and
        // double-count it against whatever the gallery reports. Left to the
        // gallery handlers on purpose.
        if (!closest(el, '.slick-gallery, .gallery-7, .vgs, .vgs__container, .bg-gallery')) {
          var carouselName = 'carousel';
          if (carouselDot) {
            carouselName = 'carousel_dot';
          } else if (closest(el, '[class*="slick-prev"], [class*="swiper-button-prev"], [class*="lbx-slider__arrow--prev"], [class*="nextend-arrow-previous"]')) {
            carouselName = 'carousel_previous';
          } else if (closest(el, '[class*="slick-next"], [class*="swiper-button-next"], [class*="lbx-slider__arrow--next"], [class*="nextend-arrow-next"]')) {
            carouselName = 'carousel_next';
          }

          LBX.track.uiInteraction({
            element_type: 'carousel',
            element_name: carouselName,
            element_action: carouselDot ? 'select' : 'scroll',
            element_text: null,
            element_position: null,
            element_url: null
          });
          return;
        }
      }

      // === UI INTERACTION — known header controls (all header versions 1-9) ===
      // QA finding 9.3a: the omni search toggle, My Garage and the hamburger
      // menu are icon/<div> controls (not <a> inside <nav>), so the generic
      // anchor auto-detect below never fired lbx_ui_interaction for them. Match
      // them by their stable selectors (shared across every header version) and
      // emit the event with the mandatory fields. element_text is null because
      // all three are icon-only (spec: "null if icon-only"). One push per click.
      // QA 9.3b V2/V4: omni component versions 2 & 4 render an extra close "X"
      // INSIDE the search bar (.close-search-svg) that also closes the omni
      // (Header.js binds it alongside .header-search-btn). It isn't the header
      // toggle, so it matched none of the selectors below — add it as a close.
      var omniClose = closest(el, '#btn-close-omnisearch, .header-search-btn.close-button, .close-search-svg');
      var omniOpen = closest(el, '#btn-open-omnisearch, .header-search-btn.open-button');
      if (omniOpen || omniClose) {
        LBX.track.uiInteraction({
          element_type: 'search',
          element_name: 'omni_search',
          element_action: omniClose ? 'close' : 'open',
          element_text: null,
          element_position: 'header',
          element_url: null
        });
        return;
      }

      var myGarage = closest(el, '.header-my-garage-btn, .my-garage-section-button, a[href*="my-garage"], a[href*="mon-garage"]');
      if (myGarage) {
        LBX.track.uiInteraction({
          element_type: 'menu',
          element_name: 'my_garage',
          element_action: 'open',
          element_text: null,
          element_position: 'header',
          element_url: hrefOf(anchor)
        });
        return;
      }

      if (closest(el, '.hamburger')) {
        // QA 9.3a V8: the hamburger is a CSS checkbox toggle — a
        // label[for=ch-burguer] wrapping the .hamburger icon drives the hidden
        // #ch-burguer checkbox. Previously we always emitted 'open'. Read the
        // checkbox on the next tick (after the click has toggled it) and emit
        // open/close to match the real menu state. Resolve the checkbox via the
        // label's `for` so multi-instance headers stay correct.
        var burgerLabel = closest(el, 'label[for]');
        var burgerId = burgerLabel ? burgerLabel.getAttribute('for') : 'ch-burguer';
        setTimeout(function () {
          var burger = document.getElementById(burgerId);
          LBX.track.uiInteraction({
            element_type: 'menu',
            element_name: 'mobile_menu',
            element_action: (burger && burger.checked) ? 'open' : 'close',
            element_text: null,
            element_position: 'header',
            element_url: null
          });
        }, 0);
        return;
      }

      // === UI INTERACTION — class-based tabs (department hours, detail tabs) ===
      // QA finding 9.3b: the footer "department" hours tabs (Sales/Service/Parts)
      // are <a class="tab-item"> without href driven by Vue @click, so they
      // matched neither the phone/directions checks (no href) nor the role-based
      // tab detect below (no role="tab"). The spec lists "department tabs" under
      // element_type 'tab'. Match the shared .tab-item class (also used by VDP
      // detail/pricing tabs) and emit 'tab'/'select' before the anchor menu or
      // footer-region fallback would otherwise mislabel them 'menu'.
      // === UI INTERACTION — vehicle category buttons ==========================
      // LBT-1602 (H7): the audit found the homepage vehicle-category tabs
      // (All / SUV & Crossover / Electric / Trucks / Hybrid) silent. They are
      // the lineup component's category switcher, and they are <button>
      // elements, so neither the .tab-item rule below nor the anchor-based menu
      // rule further down could ever see them.
      //
      // Five of the seven lineup versions render them, each with its own class
      // prefix (line-up__1__, line-up-four__, plain categorie-btn, and
      // .category-item in v2), which is why this matches on the shared fragment
      // rather than on any single class.
      //
      // The mega menu uses the same markup family for its category buttons and
      // was silent for the same reason. It reports as 'menu' rather than 'tab':
      // it navigates away, it does not switch a view in place. Scoping by nav
      // ancestry keeps the two apart instead of collapsing them into one label.
      var categoryBtn = closest(el, '[class*="categories__button"], [class*="categorie-btn"], .category-item');

      // ...except lineup v6, which was still silent after all that, and it is
      // the version Renfrew runs. Its :class binding (cl/lineup/6.blade.php)
      // applies 'line-up__categorie-btn' ONLY to the tab that is already
      // selected; an unselected tab carries nothing but utility classes. So the
      // selector above keys on a class the clicked tab cannot have yet, and
      // could only ever fire on a click that changed nothing. The tab does gain
      // the class immediately afterwards, which is what makes this read as
      // working when you inspect the DOM after clicking.
      //
      // v1, v3 and v4 put '*categories__button' on every tab and v2 uses
      // .category-item, so they were never affected — v6 is the only gap.
      //
      // The hook every version shares is the data-value attribute on the
      // category buttons, scoped to the categories row so that an unrelated
      // button elsewhere in the lineup cannot be mistaken for a tab.
      if (!categoryBtn) {
        var lineupTab = closest(el, 'button[data-value]');
        if (lineupTab && closest(lineupTab, '[class*="categories__list"], [class*="categories-section"]')) {
          categoryBtn = lineupTab;
        }
      }

      if (categoryBtn) {
        var catInNav = !!closest(categoryBtn, 'nav, [role="navigation"], .header-mega-menu');
        LBX.track.uiInteraction({
          element_type: catInNav ? 'menu' : 'tab',
          element_name: catInNav ? 'megamenu_category' : 'lineup_category',
          element_action: 'select',
          // The label is the whole point here: which category was chosen.
          element_text: textOf(categoryBtn),
          element_position: catInNav ? 'header' : null,
          element_url: null
        });
        return;
      }

      // The VDP key-features tabs (Safety / Accessories / Chassis / Powertrain /
      // Seating) are the other half of the audit's "detail tabs", and they were
      // silent: they carry neither .tab-item nor role="tab" nor aria-expanded, so
      // no rule in this handler could see them. Verified on a Renfrew VDP --
      // clicking one produced zero pushes.
      //
      // The hook is `.vehicle-feature-tab`, the element that carries the Vue
      // @click="selectOne(i)" in all three vdp-key-features versions -- NOT the
      // inner `.vehicle-feature-tab-title` span. Matching the span would report
      // only clicks that land on the text and drop the ones on the tab's padding,
      // because closest() walks up: from the span it reaches the div, never the
      // other way round.
      //
      // Note the near-miss above it: the CONTAINER is `.vehicle-features-tab`,
      // plural. Matching a class *fragment* here would be a trap, so this stays
      // an exact class selector.
      var tabItem = closest(el, '.tab-item, .vehicle-feature-tab');
      if (tabItem) {
        var inHours = !!closest(tabItem, '.hours, .hours-footer, .phone_hours')
          || positionOf(tabItem) === 'footer';
        LBX.track.uiInteraction({
          element_type: 'tab',
          element_name: inHours ? 'department_tab' : 'content_tab',
          element_action: 'select',
          element_text: textOf(tabItem),
          element_position: positionOf(tabItem),
          element_url: null
        });
        return;
      }

      // === UI INTERACTION — auto-detect for safe patterns =====================
      if (anchor) {
        // Showroom lineup "View Model" buttons (cl/lineup/1 and /6). Styled as
        // CTAs, rendered as plain anchors with no data-lbx-ui-name and outside
        // any nav, so no rule claimed them and the click was silent (conformance
        // test 2026-10-23, F2). Matched by their own classes rather than by
        // tagging the blades, so a child theme that copies the lineup keeps the
        // event.
        var lineupCta = closest(anchor, '.lbx-lineup-view-model-button, [class*="view_model_btn"], [class*="view-model-button"]');
        if (lineupCta) {
          LBX.track.uiInteraction({
            element_type: 'cta_button',
            element_name: 'lineup_view_model',
            element_action: 'click',
            element_text: textOf(lineupCta),
            element_position: positionOf(lineupCta),
            element_url: hrefOf(anchor)
          });
          return;
        }

        // Menu items inside <nav>, [role=navigation], or .main-navigation
        var menuHost = closest(anchor, 'nav, [role="navigation"], .main-navigation, .primary-navigation, .lbx-main-menu, .menu-primary-container, header .menu');
        if (menuHost) {
          LBX.track.uiInteraction({
            element_type: 'menu',
            element_name: menuHost.getAttribute('data-lbx-ui-name') || (menuHost.id || 'site_nav'),
            element_action: 'click',
            element_text: textOf(anchor),
            element_position: positionOf(menuHost),
            element_url: hrefOf(anchor)
          });
          return;
        }

        // Tabs
        if (anchor.getAttribute('role') === 'tab' || closest(anchor, '[role="tablist"]')) {
          LBX.track.uiInteraction({
            element_type: 'tab',
            element_name: anchor.getAttribute('aria-controls') || 'tab',
            element_action: 'select',
            element_text: textOf(anchor),
            element_position: 'inline',
            element_url: null
          });
          return;
        }

        // Header / footer link lists not wrapped in a <nav> (footer columns,
        // header utility links and CTAs). QA finding 9.3a/9.3b: these emitted no
        // lbx_ui_interaction because the auto-detect only covered <nav>-hosted
        // anchors. Controls that need richer typing (search box, my-garage,
        // language toggle) can still opt in via data-lbx-ui-*.
        var region = closest(anchor, 'footer, .site-footer, .lbx-footer, header, .site-header, .lbx-header');
        if (region) {
          var regionPos = positionOf(region);
          LBX.track.uiInteraction({
            element_type: 'menu',
            element_name: region.getAttribute('data-lbx-ui-name') || (regionPos + '_links'),
            element_action: 'click',
            element_text: textOf(anchor),
            element_position: regionPos,
            element_url: hrefOf(anchor)
          });
          return;
        }
      }

      // Accordion-style toggles (aria-expanded)
      // The attribute still holds the pre-click state at this point (capture
      // phase runs before the component updates it), so 'true' means the user
      // is collapsing.
      // aria-expanded also marks form controls that open a listbox — a combobox
      // or searchbox input — and TradePending's vehicle search box reported as
      // an accordion expanding on focus (QA 2026-10-22, #10). An input is not a
      // section, so those are left out.
      var accordionIsControl = !!accordion && (
        /^(INPUT|TEXTAREA|SELECT)$/.test(accordion.tagName)
        || /^(combobox|textbox|searchbox|listbox|option)$/i.test(accordion.getAttribute('role') || '')
      );
      if (accordion && !accordionIsControl) {
        var expanded = accordion.getAttribute('aria-expanded') === 'true';
        LBX.track.uiInteraction({
          element_type: 'accordion',
          element_name: accordion.getAttribute('aria-controls') || (accordion.id || 'accordion'),
          element_action: expanded ? 'collapse' : 'expand',
          element_text: textOf(accordion),
          element_position: 'inline',
          element_url: null
        });
        return;
      }
    }, true);

    // -------------------------------------------------------------------------
    // Delegated change listener — payment_calculator inputs + service_scheduler.
    // Throttled for the calculator so dragging a range slider doesn't spam.
    // -------------------------------------------------------------------------
    var pcThrottleTimer = null;
    var pcLastSignature = '';
    var pcPendingSig = ''; // signature of the currently-scheduled 'calculate'

    // Fire the throttled payment_calculator 'calculate' for a signature. Extracted
    // so it can run both from the 500ms timer and when flushing a pending event
    // because a DIFFERENT control changed (see the change handler below).
    function firePcCalculate(s) {
      pcThrottleTimer = null;
      pcPendingSig = '';

      // ONE event for one calculation (LBT-1603, Q4). This used to fire the
      // 'calculate' here and then push the recomputed figure separately, without
      // an event name — two pushes describing the same act. reportVdpPayment now
      // carries both the figure and the input on the single interaction.
      if (reportVdpPayment(s)) return;

      // On a VDP, reportVdpPayment() returning false means the figure did not
      // move, or there is none to read. The spec's guard is explicit (v2.3, tool
      // section): "where a control is clicked but the figure does not change,
      // nothing fires". This used to report the signature anyway, which is how
      // a lead form typed inside the calculator box reached the dataLayer
      // (LBT-1903).
      if (ctx && ctx.page_type === 'vdp' && ctx.vehicle) return;

      // The page-level calculator block prints its figure, so it reports like
      // the VDP: figure in tool_result, control in tool_input, nothing when the
      // figure did not move (F-4).
      if (reportPagePayment(s)) return;

      // Off the VDP (model and showroom calculators) there is no figure reader,
      // so the control is all there is to report. It goes in tool_input, which
      // is the field the spec defines for it; tool_result is the figure, and
      // there is none.
      LBX.track.toolInteraction({
        tool_name: 'payment_calculator',
        tool_action: 'calculate',
        tool_result: null,
        tool_input: s
      });
    }

    // Payment calculator inputs → 'calculate' (throttled to 500ms). Shared by
    // the 'change' and 'input' listeners below; returns true when the element
    // was a calculator field (handled or deliberately ignored).
    function handlePaymentCalculatorField(el) {
      var pcContainer = closest(el,
        '#payment-calculator, .payment-calculator__container, ' +
        '.finance-from, .lease-from, .payment-block, .finance-lease, ' +
        '.trade-down-inputs-section, ' +
        // The page-level "Car Loan Calculator" block (a custom-HTML Gutenberg
        // block, capitalcadillac.ca/payment-calculator/): inputs carry only an
        // id (vehiclePrice, downPayment, tradeInValue, paymentTerm, apr), so
        // the container is the only hook. Spec v2.3 §10 — every tool fires
        // 'open', the calculator 'calculate' on each calculation; this block
        // recomputed silently (conformance test 2026-10-28, F-4).
        PAGE_CALCULATOR
      );
      var pcIsInputByName = el.name && /^(down-payment|trade-in-value|interest-rate|loan-term|loan-term-lease)$/.test(el.name);
      // A lead form can render INSIDE the calculator box: vdp-payment-calculator
      // v8 mounts the Confirm Availability <x-modal-form> within it, so every
      // Ninja Forms field matched the container and its value (name, e-mail,
      // phone) went out as a 'calculate' (LBT-1903, QA C+UI 9). No calculator
      // blade uses a <form>, so excluding form markup cannot drop a real input.
      var pcInLeadForm = closest(el, 'form, .nf-form-cont, .lbx-modal-form');
      if ((pcContainer || pcIsInputByName) && !pcInLeadForm) {
        vdpPaymentTouched = true;
        // First change also fires the 'open' (deduped).
        LBX.track.toolInteraction({
          tool_name: 'payment_calculator',
          tool_action: 'open',
          dedupeOpen: true
        });
        // Throttle calculate. Signature prevents duplicate fires when the same
        // input re-emits change with the same value (common with range sliders).
        // Tester finding 4 (LBT-1395): include the entered value in tool_result
        // so analytics can see what the user picked (down-payment=2500,
        // loan-term=60, interest-rate=4.99, etc.), not just which input changed.
        // Tester finding 11 (LBT-1395 round 2): for checkbox/radio inputs (e.g.
        // include-gst-qst toggle) el.value is "on" or blank regardless of state,
        // making on/off indistinguishable. Use el.checked instead so the signature
        // emits e.g. include-gst-qst=true vs include-gst-qst=false.
        var pcSigValue = (el.type === 'checkbox' || el.type === 'radio')
          ? el.checked
          : (el.value || '');
        // Tester finding 11 (LBT-1395 round 3): every checkbox in the calculator
        // blades is hard-coded as id="checked-toggle", so el.name || el.id always
        // resolved to that literal — analytics could not tell which toggle (tax,
        // trade-in, etc.) was flipped. Prefer data-key (the blade already emits
        // e.g. data-key="includeTax"), then the associated <label> text, then
        // aria-label, before falling back to name/id.
        var pcSigName = el.name
          || (el.dataset && el.dataset.key)
          || (el.labels && el.labels[0] && (el.labels[0].textContent || '').replace(/\s+/g, ' ').trim())
          || el.getAttribute('aria-label')
          || el.id
          || 'input';
        var sig = pcSigName + '=' + pcSigValue;
        if (sig === pcLastSignature) return true;
        // If a 'calculate' is already pending for a DIFFERENT control, flush it now
        // so each distinct control keeps its own event. QA: typing in a text field
        // then immediately toggling Tax used to drop the field's event, because the
        // toggle's change cancelled the shared 500ms timer. Rapid changes of the
        // SAME control (e.g. dragging a range slider) still coalesce into one.
        var pcNewName = sig.split('=')[0];
        var pcPendingName = pcPendingSig ? pcPendingSig.split('=')[0] : '';
        if (pcThrottleTimer && pcPendingSig && pcPendingName !== pcNewName) {
          clearTimeout(pcThrottleTimer);
          firePcCalculate(pcPendingSig);
        }
        pcLastSignature = sig;
        pcPendingSig = sig;
        if (pcThrottleTimer) clearTimeout(pcThrottleTimer);
        pcThrottleTimer = setTimeout(function () {
          firePcCalculate(sig);
        }, 500);
        return true;
      }
      return false;
    }

    document.addEventListener('change', function (ev) {
      var el = ev.target;
      if (!el || el.nodeType !== 1) return;

      if (handlePaymentCalculatorField(el)) return;

      // Service scheduler — date or time selection.
      if (el.matches && (el.matches('.service-scheduler-date-input') || el.matches('.service-scheduler-time-select'))) {
        var action = el.matches('.service-scheduler-date-input') ? 'date' : 'time';
        LBX.track.toolInteraction({
          tool_name: 'service_scheduler',
          tool_action: 'select_option',
          tool_result: action + ':' + (el.value || '')
        });
        return;
      }
    }, true);

    // Typed calculator fields report on 'input', not only on 'change'. The
    // calculator (a Vue app) recomputes the figure on every keystroke, so by the
    // time the native 'change' fires on blur the figure has long moved and the
    // report carrying the field's name was suppressed as unchanged; the shopper
    // got a 'calculate' with tool_input: null instead of 'down-payment=5000'
    // (conformance test 2026-10-23, F5). Same signature and the same 500 ms
    // throttle as the change path: keystrokes on one field coalesce into the
    // settled value, and the blur's 'change' then matches pcLastSignature and
    // emits nothing more. Only typed controls route through here; selects,
    // checkboxes and radios keep reporting on 'change'.
    document.addEventListener('input', function (ev) {
      var el = ev.target;
      if (!el || el.nodeType !== 1) return;
      var typed = el.tagName === 'TEXTAREA'
        || (el.tagName === 'INPUT' && /^(text|number|tel|search|)$/.test(el.type || ''));
      if (!typed) return;
      handlePaymentCalculatorField(el);
    }, true);

    // -------------------------------------------------------------------------
    // Delegated keyboard listener — gallery lightbox keyboard navigation.
    // Tester finding 10 (LBT-1395 round 2): clicks on .vgs__prev / .vgs__next
    // and the close button already emit tool_interaction, but keyboard nav
    // inside an open gallery was silent. Capture ArrowLeft/Right/Escape only
    // when the lightbox is in the DOM, to avoid noise from pages without a
    // gallery.
    // -------------------------------------------------------------------------
    document.addEventListener('keydown', function (ev) {
      if (!ev || !ev.key) return;
      var key = ev.key;
      // Escape/close is captured by observeGallery() when .vgs unmounts, so we
      // only handle arrow navigation (scroll) here.
      if (key !== 'ArrowLeft' && key !== 'ArrowRight') return;
      if (!document.querySelector('.vgs__container, .vgs__gallery, .vgs')) return;
      LBX.track.toolInteraction({
        tool_name: 'gallery',
        tool_action: 'scroll',
        tool_result: key === 'ArrowLeft' ? 'prev' : 'next',
        tool_stage: readGalleryIndex()
      });
    });
  });

  // ---------------------------------------------------------------------------
  // DOM helpers (kept tiny; IE11-safe where possible).
  // ---------------------------------------------------------------------------

  function closest(el, selector) {
    if (!el) return null;
    if (el.closest) return el.closest(selector);
    var node = el;
    while (node && node.nodeType === 1) {
      if ((node.matches || node.msMatchesSelector || node.webkitMatchesSelector).call(node, selector)) return node;
      node = node.parentNode;
    }
    return null;
  }

  // The destination of a link, or null where the anchor is a toggle rather
  // than a link: a bare '#' or a javascript: href is not a destination, and
  // reporting it put 'https://site/#' in element_url on every main-menu
  // dropdown toggle (QA 2026-10-22, minor).
  function hrefOf(anchor) {
    if (!anchor || !anchor.getAttribute) return null;
    var raw = String(anchor.getAttribute('href') || '').trim();
    if (!raw || raw === '#' || /^javascript:/i.test(raw)) return null;
    return anchor.href || null;
  }

  function textOf(el) {
    if (!el) return null;
    var t = (el.textContent || el.innerText || '').replace(/\s+/g, ' ').trim();
    return t || null;
  }

  function positionOf(el) {
    if (!el) return null;
    if (closest(el, 'header, .site-header, .lbx-header')) return 'header';
    if (closest(el, 'footer, .site-footer, .lbx-footer')) return 'footer';
    if (closest(el, 'aside, .sidebar, [role="complementary"]')) return 'sidebar';
    if (closest(el, '.modal, [role="dialog"]')) return 'modal';
    return 'inline';
  }

  // VDP payment — read the on-screen payment so vehicle_payment_monthly /
  // vehicle_payment_type stop being null. The amount is computed client-side by
  // the payment calculator's Vue app and rendered into the .finance-from /
  // .lease-from "starting from" blocks AFTER lbx_page_view fires, which is why
  // the server context ships these as null. We READ the rendered value (never
  // recompute) so it always matches what the shopper sees. When both finance and
  // lease are shown we prefer finance (lease is conditional on new + hasLease).
  // The amount reflects the selected frequency (weekly/bi-weekly/monthly); that
  // frequency is separately captured in the payment_calculator tool_interaction
  // tool_result. Reading is best-effort and stays null when nothing is shown.
  function elVisible(el) {
    return !!(el && (el.offsetWidth || el.offsetHeight || (el.getClientRects && el.getClientRects().length)));
  }

  // Subtrees whose numbers are NOT the payment. The details/disclaimer popup
  // carries the rate and the term, and parsing those as the payment is the
  // failure this exclusion exists to prevent. Kept to distinctive words on
  // purpose: 'rate' and 'term' as bare substrings would also match ordinary
  // utility classes like 'separate' or 'intermediate'.
  var PAYMENT_TEXT_EXCLUDE = /detail|popup|tooltip|modal|disclaim|breakdown/i;

  // Text belonging to the payment figure, skipping popups and anything not
  // rendered. Direct text nodes are used when they carry a number, so an open
  // popup nested inside cannot leak into the parse; otherwise we descend.
  function paymentAmountText(el) {
    var kids = el.childNodes;
    var direct = '';
    for (var i = 0; i < kids.length; i++) {
      if (kids[i].nodeType === 3) direct += kids[i].nodeValue;
    }
    if (/\d/.test(direct)) return direct;

    var out = '';
    for (var j = 0; j < kids.length; j++) {
      var child = kids[j];
      if (child.nodeType === 3) { out += child.nodeValue; continue; }
      if (child.nodeType !== 1) continue;
      if (PAYMENT_TEXT_EXCLUDE.test(String(child.className || ''))) continue;
      if (!elVisible(child)) continue;
      out += paymentAmountText(child);
    }

    return out;
  }

  function parsePaymentAmount(txt) {
    var m = String(txt || '').replace(/[,\s]/g, '').match(/\d+(?:\.\d+)?/);
    if (!m) return null;
    var n = parseFloat(m[0]);
    return (isFinite(n) && n > 0) ? n : null;
  }

  // The payment shown in a "starting from" block.
  //
  // This used to target a fixed list of display-size classes
  // ('text-4xl, text-3xl, text-2xl') and read the first match. Treating those
  // as a contract was the bug: the calculator renders the LABEL in text-2xl
  // ("I want to pay") and the AMOUNT in text-5xl, so querySelector returned the
  // label — an element with no digits and no children — and the parse produced
  // null on a calculator that was working perfectly. That is why
  // vehicle_payment_monthly was null across every VDP (LBT-1602, M7), and it
  // would have broken again the next time the theme restyled this block.
  //
  // Size classes are now a hint, not a contract: try each sized element in
  // document order, take the first that actually yields a figure, and fall back
  // to the section itself when none do.
  function readPaymentSectionAmount(section) {
    if (!section) return null;

    var sized = section.querySelectorAll('[class*="xl"]');
    for (var i = 0; i < sized.length; i++) {
      var el = sized[i];
      if (PAYMENT_TEXT_EXCLUDE.test(String(el.className || ''))) continue;
      if (!elVisible(el)) continue;
      var amount = parsePaymentAmount(paymentAmountText(el));
      if (amount !== null) return amount;
    }

    return parsePaymentAmount(paymentAmountText(section));
  }

  function readVdpPayment() {
    var calc = document.querySelector('#payment-calculator, .payment-calculator__container');
    if (!calc) return null;
    var financeSec = calc.querySelector('.finance-from');
    var leaseSec = calc.querySelector('.lease-from');
    var finance = elVisible(financeSec) ? readPaymentSectionAmount(financeSec) : null;
    if (finance !== null) return { amount: finance, type: 'finance', frequency: readVdpPaymentFrequency(calc, financeSec) };
    var lease = elVisible(leaseSec) ? readPaymentSectionAmount(leaseSec) : null;
    if (lease !== null) return { amount: lease, type: 'lease', frequency: readVdpPaymentFrequency(calc, leaseSec) };
    return null;
  }

  // The period of the figure on screen: 'weekly', 'bi-weekly' or 'monthly'.
  //
  // The calculator renders the amount for the frequency the shopper picked, so
  // "$104" under "Weekly" is a weekly payment. The theme used to report it as
  // "104/mo" and put 104 in vehicle_payment_monthly (conformance test
  // 2026-10-23, F6): tool_result is defined as the display string, and the
  // monthly field is named for what it holds (spec v2.3, tool section). Read
  // the frequency control first (v8: select#frequency-finance /
  // select#frequency-lease, v-model "frequency"), then the wording the
  // section prints next to the amount ("Weekly +Tax & Lic"), and assume
  // monthly only when the calculator shows no period at all.
  function readVdpPaymentFrequency(calc, section) {
    var selects = calc.querySelectorAll('select[id^="frequency"], select[name*="frequency"]');
    for (var i = 0; i < selects.length; i++) {
      if (!elVisible(selects[i])) continue;
      var v = normalizePaymentFrequency(selects[i].value);
      if (v) return v;
    }
    var fromText = normalizePaymentFrequency((section && section.textContent) || '');
    return fromText || 'monthly';
  }

  function normalizePaymentFrequency(raw) {
    var s = String(raw || '').toLowerCase();
    if (/bi-?weekly|biweekly|bi-?hebdo|aux deux semaines|quinzaine/.test(s)) return 'bi-weekly';
    if (/weekly|hebdo|semaine/.test(s)) return 'weekly';
    if (/monthly|mensuel|mois|\bmo\b/.test(s)) return 'monthly';
    return null;
  }

  var PAYMENT_FREQUENCY_SUFFIX = { weekly: 'wk', 'bi-weekly': 'biwk', monthly: 'mo' };

  // The incentive's name, read from the pricing block the shopper is looking at.
  //
  // LBT-1602 (M6). The name cannot be resolved server-side, and the reason is
  // worth writing down: the label comes from a per-dealer setting, and WHICH
  // setting depends on which pricing template renders. The parent's templates
  // label the discount row with get_special_discount_label(); Renfrew's child
  // theme replaced that row and labels it with get_special_price_label() -- the
  // same helper the parent uses for a PRICE row. So one helper means two
  // different things depending on the site, PHP cannot tell which is in play,
  // and any fixed choice is wrong on one population. Reporting "adjustment"
  // while the page printed "RENFREW DISCOUNT" was exactly that.
  //
  // The rendered page is the only source that is true regardless of who renders
  // it, including child themes we do not control and templates that do not exist
  // yet. Same approach as the payment amount below, for the same reason.
  //
  // The hook is the MINUS SIGN, not a class: every version that prints a named
  // discount renders the amount as `-{{ currency_format(...) }}`, and a class is
  // exactly what a child theme changes. Rows are read in document order, which
  // puts the discount row ahead of the rebate rows in every version that has
  // both. Pricing versions 1, 3 and 5 print no negative row at all -- those keep
  // whatever PHP resolved, which is the correct degradation rather than a miss.
  //
  // Returns the row, not just its label: { name, amount }. The amount is the
  // other half of the pair the spec defines together — vehicle_discount_price is
  // "a named special where one exists" and vehicle_incentive_name "the name of
  // that special" (v2.3 §2) — and it has to come from the same row, or the two
  // fields describe different things. See syncVehicleIncentiveName.
  function readVdpIncentiveRow() {
    // Rows inside ANY pricing container, not inside the first one that matches:
    // scoping to a single container is a gamble on which element with "pricing"
    // in its class comes first in the document, and the pricing table is not
    // always inside it. Falls back to every row on the page, which is safe here
    // because nothing else on a VDP prints a negative currency amount.
    var rows = document.querySelectorAll('[class*="pricing"] tr');
    if (!rows.length) rows = document.querySelectorAll('tr');
    for (var i = 0; i < rows.length; i++) {
      var cells = rows[i].querySelectorAll('td');
      // A priced row is label + amount. Anything narrower is a spacer row, of
      // which these tables have several.
      if (cells.length < 2) continue;
      var amount = textOf(cells[cells.length - 1]);
      if (!amount || amount.charAt(0) !== '-') continue;
      var label = textOf(cells[0]);
      if (label) return { name: label, amount: parsePaymentAmount(amount) };
    }
    return null;
  }

  function readVdpIncentiveName() {
    var row = readVdpIncentiveRow();
    return row ? row.name : null;
  }

  // Runs before the page view so the corrected name travels in the event itself
  // rather than in a follow-up push: the pricing block is server-rendered, so it
  // is already in the DOM when this fires on DOMContentLoaded. Mutating ctx also
  // means every later event that merges the vehicle carries it.
  //
  // Only overwrites when the page actually printed a discount. Note this beats a
  // feed `promocode` when both exist: the field names what the shopper saw, and
  // what they saw is the row on the page.
  //
  // THE PAIR. vehicle_discount_price comes from PHP as the feed's 'specialprice'
  // (helpers.php), and the name from the row on the page. Where a dealer prints
  // its named special as a REBATE row rather than through 'specialprice' —
  // capitaldodge.ca, pricing template 4: "MSRP $39,410 / Consumer Cash
  // (Applied before taxes) −$2,500 / … / SALE PRICE $34,660" — the feed field
  // is empty, so the event named a special and priced none (manual
  // conformance test 2026-10-28, F-5, on three Dodge VDPs). Both fields are
  // defined on the same special, so when PHP has no price for the row the page
  // names, the price is derived from that row: the list price the page
  // prints minus the row's amount. That is the figure the single-row layouts
  // already report (Renfrew: 38,098 − 8,099 = 29,999; Cadillac: 64,898 −
  // 2,250 = 62,648), so the multi-row layout now agrees with them.
  //
  // Only the FIRST negative row, which is the one the name comes from; the
  // remaining rebates are other programmes, not "that special". And only when
  // vehicle_list_price is a number — it is the pre-discount figure the page
  // prints (its own gate, above) — so a derived price is never built on the
  // asking price or on nothing. A result that is not a positive number stays
  // null (Rule 4). A PHP value is never overwritten: the feed knows the
  // special's price when it sends one.
  function syncVehicleIncentiveName() {
    if (!ctx || ctx.page_type !== 'vdp' || !ctx.vehicle) return;
    var row = readVdpIncentiveRow();
    if (!row) return;
    var v = ctx.vehicle;
    v.vehicle_incentive_name = lc(row.name);
    if (v.vehicle_discount_price === null && typeof v.vehicle_list_price === 'number' && row.amount !== null) {
      var derived = Math.round((v.vehicle_list_price - row.amount) * 100) / 100;
      if (isFinite(derived) && derived > 0) v.vehicle_discount_price = derived;
    }
  }

  // The displayed payment, reported as the calculator interaction it is
  // (LBT-1603, Q4).
  //
  // WHY THIS IS NOT A PAGE-LOAD FIELD, which is the part worth reading. It used
  // to be pushed WITHOUT an event key, updating GTM's model so later events
  // carried it even though the page view had already gone out with null. That
  // works for a persisted variable and is invisible to a container listening on
  // named events — the same defect D4 raised for the SRP.
  //
  // The spec owner's answer (2026-08-18) is better than giving that push a name:
  // a figure read out of a calculator after it computes, which changes again
  // every time the shopper moves an input, is not a property of the page at all.
  // It is a calculator interaction, and the spec already has the event for it —
  // tool_name 'payment_calculator', tool_action 'calculate', with tool_result
  // defined for exactly this (its own example is '425/mo').
  //
  // So the typed fields travel on that event, alongside the display string, and
  // vehicle_payment_monthly / vehicle_payment_type stay null in the VDP payload.
  // That is what keeps every third-party widget outside the ready contract:
  // lbx_vdp_ready never waits for a calculator, ours or anyone's.
  //
  // ctx.vehicle is still updated, so an event fired later in the visit carries
  // the last figure the shopper saw. What is gone is the nameless push.
  // The last figure read from the calculator, as shown: amount, type and
  // frequency. Kept apart from ctx.vehicle because vehicle_payment_monthly is
  // null whenever the shown figure is not monthly (Rule 4: a money field holds
  // the value or null, never a stand-in), and a null there cannot tell "no
  // figure yet" from "a weekly figure" — which is exactly the comparison the
  // unchanged-value guard needs.
  var lastVdpPayment = null;

  function sameVdpPayment(a, b) {
    if (!a && !b) return true;
    if (!a || !b) return false;
    return a.amount === b.amount && a.type === b.type && a.frequency === b.frequency;
  }

  // What the payload carries for a read figure: the monthly field only when
  // the figure IS monthly; the display string names the real period.
  function vdpPaymentFields(p) {
    if (!p) return { vehicle_payment_monthly: null, vehicle_payment_type: null };
    return {
      vehicle_payment_monthly: p.frequency === 'monthly' ? p.amount : null,
      vehicle_payment_type: p.type
    };
  }

  function reportVdpPayment(inputSignature) {
    if (!ctx || ctx.page_type !== 'vdp' || !ctx.vehicle) return false;

    var p = readVdpPayment();

    // "Don't fire on an unchanged value" — re-rendering the same figure is not
    // an interaction. This is also what makes the poll below safe: it can look
    // as often as it likes and only a real change reports.
    if (sameVdpPayment(lastVdpPayment, p)) return false;

    lastVdpPayment = p;
    var fields = vdpPaymentFields(p);
    ctx.vehicle.vehicle_payment_monthly = fields.vehicle_payment_monthly;
    ctx.vehicle.vehicle_payment_type = fields.vehicle_payment_type;

    // Nothing to report yet: the calculator is on the page but has not produced
    // a figure. Recording the null is what suppresses the duplicate later.
    if (!p) return false;

    LBX.track.toolInteraction({
      tool_name: 'payment_calculator',
      tool_action: 'calculate',
      // The spec's own example for tool_result is the payment, so the figure
      // wins the field. The input the shopper changed is still worth having —
      // it is why tester finding 4 put it here in the first place — so it moves
      // to a Leadbox-local field rather than being dropped.
      //
      // The suffix is the period the figure is shown for: '104/wk', '208/biwk',
      // '393/mo' — the display string, as the spec defines tool_result. It used
      // to be '/mo' whatever the calculator showed (F6).
      tool_result: p.amount + '/' + (PAYMENT_FREQUENCY_SUFFIX[p.frequency] || 'mo') + (p.type === 'lease' ? ' lease' : ''),
      tool_input: inputSignature || null,
      payment: fields
    });

    return true;
  }

  var paymentSyncTimer = null;
  function schedulePaymentSync(inputSignature) {
    // Vue updates the DOM on the tick after the click/change; give it a beat.
    if (paymentSyncTimer) clearTimeout(paymentSyncTimer);
    paymentSyncTimer = setTimeout(function () {
      // One of the two readers owns the page: the VDP one returns false off a
      // VDP, the page-block one returns false on a VDP or where the block is
      // absent, so a click reports at most once.
      if (!reportVdpPayment(inputSignature)) reportPagePayment(inputSignature);
    }, 80);
  }

  // ---------------------------------------------------------------------------
  // Page-level "Car Loan Calculator" block (F-4, conformance test 2026-10-28).
  //
  // A custom-HTML Gutenberg block, not a theme component: found on
  // capitalcadillac.ca/payment-calculator/ (renfrewchrysler.com and
  // capitaldodge.ca have no such page). It is theme-hosted code the dealer
  // typed into the editor, so the third-party exemption of spec v2.3 does not
  // apply — it recomputed on every input (vehicle price, down payment,
  // trade-in, term, APR, Weekly/Bi-weekly/Monthly) and emitted nothing. Its
  // controls have ids only, which the change path already turns into
  // `label=value` signatures ("vehicle price=35000"); what was missing was the
  // container in the selectors above and a reader for the figure it prints:
  //
  //   <div class="input-payment-container"> … inputs … <button id="monthly">
  //   <div class="results-payment-container"> <p id="paymentDisplay">$1373.80</p>
  //
  // There is no vehicle on this page, so vehicle_* stay null; the figure is a
  // finance payment (the block has no lease mode), and the period comes from
  // the highlighted frequency button.
  // ---------------------------------------------------------------------------
  var PAGE_CALCULATOR = '.input-payment-container';
  var PAGE_CALCULATOR_RESULT = '.results-payment-container #paymentDisplay, .results-payment-container [id*="payment" i]';

  function readPagePayment() {
    var disp = document.querySelector(PAGE_CALCULATOR_RESULT);
    if (!disp || !elVisible(disp)) return null;
    var amount = parsePaymentAmount(textOf(disp));
    if (amount === null) return null;

    var calc = document.querySelector(PAGE_CALCULATOR);
    var buttons = calc ? Array.prototype.slice.call(calc.querySelectorAll('button')) : [];
    var active = buttons.filter(function (b) { return /(^|\s)bg-primary(\s|$)/.test(String(b.className || '')); })[0];
    var label = active ? (lc(textOf(active)) || lc(active.id) || '') : '';
    var frequency = /bi/.test(label) ? 'bi-weekly' : (/week/.test(label) ? 'weekly' : 'monthly');

    return { amount: amount, type: 'finance', frequency: frequency };
  }

  var lastPagePayment = null;

  function reportPagePayment(inputSignature) {
    if (ctx && ctx.page_type === 'vdp' && ctx.vehicle) return false;
    if (!document.querySelector(PAGE_CALCULATOR)) return false;

    var p = readPagePayment();
    // Same guard as the VDP: an unchanged figure is not a calculation. The
    // block prints "$0" until the first price is typed, which parses to null —
    // so the first real figure is the first event, never the page load.
    if (sameVdpPayment(lastPagePayment, p)) return false;
    lastPagePayment = p;
    if (!p) return false;

    LBX.track.toolInteraction({
      tool_name: 'payment_calculator',
      tool_action: 'calculate',
      tool_result: p.amount + '/' + (PAYMENT_FREQUENCY_SUFFIX[p.frequency] || 'mo'),
      tool_input: inputSignature || null,
      payment: vdpPaymentFields(p)
    });

    return true;
  }

  function initVdpPaymentSync() {
    if (!ctx || ctx.page_type !== 'vdp' || !ctx.vehicle) return;
    if (!document.querySelector('#payment-calculator, .payment-calculator__container')) return;

    // The calculator computes its default "starting from" figure shortly after
    // mount. That figure is RECORDED here as the baseline, not reported: spec
    // v2.3 puts the payment on 'calculate' "debounced, and not on page load"
    // (Testing checklist), so the first event is the first figure a shopper
    // actually produces. LBT-1603 used to report this default once, as the
    // figure most visitors see; v2.3 overrules that (LBT-1903, QA C+UI 6).
    //
    // The baseline still matters: reportVdpPayment() compares against it, so a
    // change that lands on the default figure stays silent, and an event fired
    // later in the visit carries the figure on screen.
    //
    // This deliberately does NOT feed lbx_vdp_ready. The ready event never waits
    // for a widget, ours included, so a payment that lands 600 ms to 7 s after
    // the page is not part of the page's payload.
    var tries = 0;
    var timer = setInterval(function () {
      tries++;
      if (recordVdpPaymentBaseline() || tries >= 12) {
        clearInterval(timer);
      }
    }, 600);
  }

  // Set by the calculator's change and click paths. Once the shopper has touched
  // the calculator, whatever is on screen is THEIR figure, not the default: the
  // poll recording it as the baseline inside the 500 ms debounce would make the
  // pending 'calculate' compare equal and stay silent, losing the change.
  var vdpPaymentTouched = false;

  // Store the calculator's current figure in ctx.vehicle without emitting.
  // Returns true once there is nothing left to wait for.
  function recordVdpPaymentBaseline() {
    if (!ctx || ctx.page_type !== 'vdp' || !ctx.vehicle) return true;
    if (vdpPaymentTouched) return true;

    // A shopper who changed a control before the poll caught the default has
    // already reported a real calculation, and that figure is the baseline now.
    // Overwriting it would let the next change compare against a stale number.
    if (lastVdpPayment) return true;

    var p = readVdpPayment();
    if (!p) return false;

    lastVdpPayment = p;
    var fields = vdpPaymentFields(p);
    ctx.vehicle.vehicle_payment_monthly = fields.vehicle_payment_monthly;
    ctx.vehicle.vehicle_payment_type = fields.vehicle_payment_type;

    return true;
  }

  // Gallery open/close detection — version-agnostic. Every vdp-gallery blade
  // renders the shared vue-gallery-slide-show-new component, whose root
  // <div class="vgs"> exists in the DOM only while the lightbox is open
  // (v-if="imgIndex !== null"). Watching that node appear/disappear is far more
  // reliable than matching per-version open-trigger selectors — which is why QA
  // saw 'open' never fire on several gallery versions (11.3).
  function observeGallery() {
    if (!window.MutationObserver || !document.body) return;
    var isOpen = !!document.querySelector('.vgs');
    if (isOpen) fireGalleryOpen();
    var obs = new MutationObserver(function () {
      var nowOpen = !!document.querySelector('.vgs');
      if (nowOpen === isOpen) return;
      isOpen = nowOpen;
      if (nowOpen) {
        fireGalleryOpen();
      } else {
        LBX.track.toolInteraction({ tool_name: 'gallery', tool_action: 'close' });
      }
    });
    obs.observe(document.body, { childList: true, subtree: true });
  }

  // Chat open/close for vendors that expose no events at all — same shape as
  // observeGallery(), and for the same reason: watching the panel beats
  // guessing at per-vendor triggers.
  //
  // The 'chat' VENDOR_ADAPTER above handles vendors that push chat_* to the
  // dataLayer and takes precedence over this. But on renfrewchrysler.com
  // (2026-08-15) opening Dealer Inspire Conversations produced no dataLayer
  // push of any kind, and nothing landed in _unmappedVendorEvents either —
  // DIConv is 11 commands with no subscription API. On that build there is no
  // channel, so H2's "lbx_chat_submit can never fire" extends to open/close.
  // This is what closes the open/close half without the vendor's cooperation.
  //
  // Presence is NOT the signal: the panel iframe stays in the DOM and is
  // resized to zero when closed. Measured live: 0px tall closed, 471px open.
  var CHAT_PANELS = '#messenger-frame, iframe[id*="messenger"], iframe[title*="messenger" i], iframe[title*="chat" i]';

  // Everything a chat vendor renders that is NOT the conversation being open.
  // The launcher is the point: Dealer Inspire titles its always-visible 60x60
  // bubble "Live Chat launcher", so a title match on 'chat' alone reports the
  // chat as permanently open — caught by testing the selector against the live
  // widget rather than by reading it. The proactive teaser is excluded for a
  // different reason: it appears on its own, so counting it would report an
  // 'open' the shopper never performed.
  var CHAT_CHROME = '#launcher-frame, iframe[id*="launcher"], iframe[title*="launcher" i], iframe[id*="proactive"], iframe[title*="proactive" i]';

  function chatPanelOpen() {
    var frames = document.querySelectorAll(CHAT_PANELS);
    for (var i = 0; i < frames.length; i++) {
      var f = frames[i];
      if (f.matches && f.matches(CHAT_CHROME)) continue;
      var r = f.getBoundingClientRect();
      if (r.height > 0 && r.width > 0) return true;
    }
    return false;
  }

  // When the shopper last touched the page. A tool that opens on its own is
  // not engagement (spec v2.3 §10: "When: User engages with any interactive
  // tool"), and the DOM observers below cannot tell a click from a vendor
  // script by looking at the panel — so they look at the clock instead: an
  // open that follows a pointer or key within this window is the shopper's,
  // anything else is the vendor's. Three seconds covers a widget that animates
  // in after the click; a proactive teaser fires tens of seconds after load
  // with nothing before it (D-29 on capitaldodge.ca: 37 s).
  var USER_GESTURE_WINDOW_MS = 3000;
  var lastUserGestureAt = 0;

  function noteUserGesture() { lastUserGestureAt = Date.now(); }

  function recentUserGesture() {
    return lastUserGestureAt > 0 && (Date.now() - lastUserGestureAt) <= USER_GESTURE_WINDOW_MS;
  }

  ['pointerdown', 'touchstart', 'keydown'].forEach(function (type) {
    window.addEventListener(type, noteUserGesture, true);
  });

  // The `blur` signal below has a blind spot: once focus is INSIDE a chat
  // iframe, the next click on another chat iframe moves focus frame→frame and
  // this window never blurs — it was already blurred. That is exactly the
  // state after a close: the vendor parks focus on the launcher bubble, the
  // shopper clicks that bubble to come back, and nothing here fires (retest
  // of 1.23.36, 2026-10-28: capitalcadillac.ca and renfrewchrysler.com, the
  // relaunch opened the window with zero events, the same click with focus on
  // the document emitted the open). No event crosses the frame boundary, so
  // focus RESTING on the launcher is polled instead and remembered like a
  // gesture. The panel never counts: the vendor focuses it on its own when it
  // restores the window (see the blur handler).
  var lastChatChromeFocusAt = 0;

  function focusOnChatChrome() {
    var active = document.activeElement;
    return !!(active && active.tagName === 'IFRAME' && active.matches && active.matches(CHAT_CHROME));
  }

  function recentChatChromeFocus() {
    return lastChatChromeFocusAt > 0 && (Date.now() - lastChatChromeFocusAt) <= USER_GESTURE_WINDOW_MS;
  }

  setInterval(function () {
    if (focusOnChatChrome()) lastChatChromeFocusAt = Date.now();
  }, 250);

  // A click INSIDE a vendor iframe never reaches this window — and that is
  // where the chat launcher lives (Cars Commerce renders its bubble in an
  // iframe). What the parent does see is focus leaving the document for that
  // frame: `blur` on window with the iframe as the active element. Measured
  // on renfrewchrysler.com: clicking the launcher produced no pointerdown here
  // and the gate suppressed a genuine open.
  //
  // But not every frame counts. The conversation panel takes focus on its own
  // when the vendor restores it on the next page (measured: blur to the
  // messenger frame 81 ms before the panel opened, with nobody touching the
  // page), so focus landing on the PANEL is the vendor's doing and says
  // nothing about the shopper. Focus landing on any other frame — the
  // launcher bubble, a form, a video — is a click. A proactive teaser moves
  // no focus at all.
  window.addEventListener('blur', function () {
    var active = document.activeElement;
    if (!active || active.tagName !== 'IFRAME' || !active.matches) return;
    var isChatPanel = active.matches(CHAT_PANELS) && !active.matches(CHAT_CHROME);
    if (!isChatPanel) noteUserGesture();
  });

  function observeChat() {
    if (!window.MutationObserver || !document.body) return;

    // Deferred one tick, and the reason is a hoisting trap worth knowing about:
    // this is called from ready(), which runs SYNCHRONOUSLY when the script
    // executes after parsing (the isolated bundle is deferred, so readyState is
    // already 'interactive'). At that moment the `var CHAT_PANELS` /
    // `CHAT_CHROME` declared further down this file exist but have not been
    // assigned yet, so chatPanelOpen() queried `undefined`, found nothing, and
    // the baseline was always "closed" — which is how a window the vendor
    // restored at load looked like a fresh closed→open transition (F-1). After
    // the tick the whole module has run and the selectors are real.
    setTimeout(startChatObserver, 0);
  }

  function startChatObserver() {
    if (!document.body) return;

    // The state at load is a BASELINE, not an event. The chat vendor restores
    // an open conversation on the next page, and reporting that as 'open'
    // counted one chat engagement per page view for as long as the window
    // stayed open (manual conformance test 2026-10-28, F-1: Cars Commerce on
    // renfrewchrysler.com and capitalcadillac.ca, five observations; closing
    // the window stopped it). The shopper opened the chat once, on the page
    // where it was reported; what they did on this page is nothing.
    var isOpen = chatPanelOpen();
    // Whether a conversation WINDOW is up that the shopper can close: one they
    // opened here, or one the vendor restored from the previous page (at the
    // baseline, or — on Cars Commerce — 2.5 to 4 s after it). Its 'close' is
    // the shopper's action and is reported; the restore itself never is. A
    // proactive teaser is not a panel (CHAT_CHROME) and never sets this.
    var closeReportable = isOpen;

    var obs = new MutationObserver(function () {
      // A vendor that speaks for itself wins; stop before double-reporting.
      if (chatVendorEventsSeen) { obs.disconnect(); return; }

      var nowOpen = chatPanelOpen();
      if (nowOpen === isOpen) return;
      isOpen = nowOpen;

      if (nowOpen) {
        // Opened with no shopper gesture behind it — no pointer or key here,
        // no focus leaving the document for a frame, no focus resting on the
        // launcher bubble: the vendor restoring or pushing the window, not a
        // tool launch. Tracked as the new state, not reported; but it is a
        // real window now, so closing it will be.
        if (!recentUserGesture() && !recentChatChromeFocus() && !focusOnChatChrome()) {
          closeReportable = true;
          return;
        }
        closeReportable = true;
        // A chat 'open' means the same thing whether it was detected from an
        // event or from the DOM: toolInteraction() reports a window state once
        // until it changes, so this and the vendor's own event for the same
        // open (Cars Commerce's ASC 'start' lands ~1.7 s later) are one push,
        // and a reopen after a close is a new one.
        LBX.track.toolInteraction({ tool_name: 'chat', tool_action: 'open', dedupeOpen: true });
        return;
      }

      if (!closeReportable) return;
      closeReportable = false;
      LBX.track.toolInteraction({ tool_name: 'chat', tool_action: 'close' });
    });

    // The widget injects late and then only resizes, so both the tree and the
    // attributes that carry the size have to be watched.
    obs.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'height', 'width']
    });
  }

  // Emits the gallery 'open' event, attaching any best-effort "opened-from"
  // metadata captured by the click that triggered it (galleryOpenHint).
  function fireGalleryOpen() {
    var hint = galleryOpenHint || {};
    galleryOpenHint = null;
    LBX.track.toolInteraction({
      tool_name: 'gallery',
      tool_action: 'open',
      tool_result: hint.openedFrom ? ('opened-from=' + hint.openedFrom) : null,
      tool_stage: hint.stage != null ? hint.stage : null
    });
  }

  // 1-based position of an element among its parent's element children. Used
  // for gallery thumb-position reporting; returns null if there is no parent.
  function indexOfSibling(el) {
    if (!el || !el.parentNode) return null;
    var siblings = el.parentNode.children;
    for (var i = 0; i < siblings.length; i++) {
      if (siblings[i] === el) return i + 1;
    }
    return null;
  }

  // Best-effort 1-based index of the photo currently shown in an open gallery
  // lightbox. Reads the counter text (e.g. "6 / 25") first, falls back to the
  // active bullet/dot. Returns null when no gallery is open.
  // Tester finding 12B (LBT-1395 round 3): this theme renders the counter inside
  // .vgs__gallery__title (vue-gallery-slide-show-new.vue.js line ~68 emits
  // `{{ imgIndex + 1 }} / {{ images.length }}` inside that div). The previous
  // selectors did not match anything, so tool_stage came back null for every
  // prev/next/keyboard event.
  function readGalleryIndex() {
    var counter = document.querySelector('.vgs__container__counter, .vgs__counter, .vgs__gallery__title');
    if (counter && counter.textContent) {
      var m = counter.textContent.match(/(\d+)/);
      if (m) return parseInt(m[1], 10);
    }
    var active = document.querySelector('.vgs__container__bullet--active, .vgs__bullet--active, .vgs__thumb--active, .vgs__gallery__container__img--active');
    if (active) return indexOfSibling(active.parentNode || active);
    return null;
  }

  function isDirectionsUrl(url) {
    var u = (url || '').toLowerCase();
    if (!u) return false;
    return (
      /maps\.google/.test(u) ||                // maps.google.com, maps.google.ca, etc.
      /google\.[a-z.]+\/maps/.test(u) ||        // google.com/maps, google.fr/maps
      /maps\.app\.goo\.gl/.test(u) ||           // modern Google Maps share links
      /goo\.gl\/maps/.test(u) ||                // legacy Google Maps shortener
      /g\.co\/(maps|kgs)/.test(u) ||            // g.co redirects to Maps / Knowledge Graph
      /maps\.apple\.com/.test(u) ||             // Apple Maps
      /waze\.com/.test(u) ||                    // Waze
      /bing\.com\/maps/.test(u) ||              // Bing Maps
      /here\.com/.test(u) ||                    // HERE WeGo
      /openstreetmap\.org/.test(u)              // OSM
    );
  }

  function isDirectionsAnchor(el) {
    if (!el) return false;
    var node = el;
    while (node && node.nodeType === 1) {
      var cls = (node.className || '') + '';
      // Theme conventions: .lbx-dealer-address-*, .LBX-dealer-address-*,
      // .get-directions, [data-lbx-directions]
      if (/lbx-dealer-address/i.test(cls)) return true;
      if (/\bget-?directions\b/i.test(cls)) return true;
      if (/\bdirections(-|_| )/i.test(cls)) return true;
      if (node.getAttribute && node.getAttribute('data-lbx-directions') === 'true') return true;
      node = node.parentNode;
    }
    return false;
  }

  function isWindowStickerLink(el) {
    if (!el) return false;
    var u = (el.href || '').toLowerCase();
    var cls = (el.className || '') + '';
    if (/windowsticker|window-sticker/.test(u)) return true;
    if (/cta-windowsticker|window-sticker-btn|lbx-window-sticker/i.test(cls)) return true;
    // Also accept ancestor carrying the class (in case the click lands inside a span).
    var node = el.parentNode;
    while (node && node.nodeType === 1) {
      var pcls = (node.className || '') + '';
      if (/cta-windowsticker|window-sticker-btn|lbx-window-sticker/i.test(pcls)) return true;
      node = node.parentNode;
    }
    return false;
  }

  function inferDepartmentFromClasses(el) {
    if (!el) return null;
    var node = el;
    while (node && node.nodeType === 1) {
      var cls = (node.className || '') + '';
      if (/lbx-service|service-(phone|dept)/i.test(cls)) return 'service';
      if (/lbx-parts|parts-(phone|dept)/i.test(cls)) return 'parts';
      if (/lbx-finance|finance-(phone|dept)/i.test(cls)) return 'finance';
      if (/lbx-sales|sale[s]?-(phone|dept)/i.test(cls)) return 'sales';
      node = node.parentNode;
    }
    return null;
  }
})();
